import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import QRCode from 'qrcode'
import type { AppCore } from '@core/bootstrap'
import type {
  OpenedContainer,
  Product,
  ReceiptStatus,
  SaleReceipt,
  WriteoffReasonCode
} from '@core/db/models'
import { returnableQtyByLine } from '@core/services/sale/returnValidation'
import { PosEvent } from '@core/bus/events'
import type { MarkRequest } from '@core/marking/types'
import type { MarkingIssue } from '@core/bus/types'
import {
  PAYMENT_METHODS,
  type PaymentOutcome,
  type PaymentQr,
  type PaymentQrRequest
} from '@core/payments/types'
import { formatCurrency, type CurrencyCode } from '@core/utils/currency'
import { kg as formatKg, qty as formatQty } from '@core/utils/format'
import { isNativeKkt } from '@core/native/kkt'
import type { NativeKktDocument } from '@core/native/kkt'
import { deviceLabel, shortId } from '@core/utils/deviceLabel'
import type { PosShift } from '@core/db/models'
import type { ShiftSummary } from '@core/services/shift/ShiftService'
import type { ReplacementTask } from '@core/services/sale/SaleService'
import { detectKktShiftMismatch, kktShiftMismatchText } from '@core/services/shift/kktReconcile'
import { holdCancelNotice, holdPrintNotice } from '@core/services/shift/holdAccounting'
import { syncService } from '@core/services/sync/SyncService'
import { PinPadModal, type ActiveCashier } from '@core/components/PinPadModal'
import { canOpenAdmin } from '@core/auth/adminAccess'
import { db } from '@core/db/db'
import {
  META_CARD_TERMINAL_MODE,
  META_COMPANY_INN,
  META_COMPANY_NAME,
  META_STORE_ADDRESS,
  fiscalPrefKey
} from '@core/db/schema'
import { CameraScannerModal } from '@core/components/CameraScannerModal'
import { CategoryGrid } from '@core/components/CategoryGrid'
import { CategoryImage } from '@core/components/CategoryImage'
import { PosShiftPanel } from '@core/components/PosShiftPanel'
import { ProductTileImage } from '@core/components/ProductTileImage'
import { PosProblemsPanel } from '@core/components/PosProblemsPanel'
import {
  escapeHtml,
  isOrganizationReady,
  organizationHtml,
  paymentMethodLabel,
  receiptItemsHtml
} from '@core/utils/receiptHtml'
import {
  buildPosProblems,
  criticalSignature,
  highestSeverity,
  type PosProblem
} from '@core/services/shift/posProblems'
import {
  orderCategoriesByFrequency,
  pickFrequentProducts
} from '@core/services/catalog/frequency'
import { usePosDensity } from '@core/utils/usePosDensity'
import { parseWeightBarcode } from '@core/utils/weightBarcode'
import {
  btn,
  btnBase,
  btnSize,
  cx,
  hint,
  input,
  overlay,
  modalSm,
  modalLg,
  fieldLabel,
  fieldInput,
  amountDisplay,
  qrBox,
  qrSvg,
  qrPayload
} from '../../ui/classes'
import {
  Alert,
  Box,
  Camera,
  Cart,
  Cash,
  Card,
  Check,
  Clock,
  Close,
  Marking,
  Pencil,
  Printer,
  Qr,
  Scales,
  Trash
} from '../../ui/icons'
import { IconButton } from '../../ui/IconButton'


/**
 * Срок годности вскрытой тары нарезки (сыр/колбаса) — 72 часа.
 * По истечении показываем кассиру красную плашку «изъять».
 */
const SHELF_LIFE_MS = 72 * 60 * 60 * 1000

/** Человекочитаемое «N назад» для времени вскрытия тары. */
function formatRelative(ts: number): string {
  const diff = Date.now() - ts
  if (diff < 60_000) return 'только что'
  const min = Math.floor(diff / 60_000)
  if (min < 60) return `${min} мин назад`
  const hours = Math.floor(min / 60)
  if (hours < 24) return `${hours} ч назад`
  const days = Math.floor(hours / 24)
  const plural = (n: number, one: string, few: string, many: string) =>
    n % 10 === 1 && n % 100 !== 11
      ? one
      : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)
        ? few
        : many
  return `${days} ${plural(days, 'день', 'дня', 'дней')} назад`
}

/**
 * Изолированная печать чека через скрытый <iframe>.
 *
 * В iframe пишется чистый чековый документ 58мм и вызывается его собственный
 * print(). Это гарантирует печать ровно одного чека без полей в любом браузере
 * и при сохранении в PDF — без влияния на DOM основного приложения.
 */
function printTicketViaIframe(htmlContent: string): void {
  let frame = document.getElementById('receipt-print-iframe') as HTMLIFrameElement | null
  if (!frame) {
    frame = document.createElement('iframe')
    frame.id = 'receipt-print-iframe'
    frame.style.position = 'fixed'
    frame.style.right = '0'
    frame.style.bottom = '0'
    frame.style.width = '0'
    frame.style.height = '0'
    frame.style.border = 'none'
    // Невидим и не перехватывает клики: на экране не должно быть белых прямоугольников.
    frame.style.opacity = '0'
    frame.style.pointerEvents = 'none'
    frame.style.zIndex = '-1'
    document.body.appendChild(frame)
  }
  const doc = frame.contentWindow?.document
  if (!doc) return
  doc.open()
  doc.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Печать чека</title>
        <style>
          @page { size: 58mm auto; margin: 0; }
          html, body {
            margin: 0;
            padding: 3mm;
            width: 58mm;
            box-sizing: border-box;
            font-family: monospace, -apple-system, sans-serif;
            font-size: 11px;
            line-height: 1.25;
            color: #000;
            background: #fff;
          }
          .line { display: flex; justify-content: space-between; margin: 2px 0; }
          .line span:last-child { white-space: nowrap; text-align: right; }
          .center { text-align: center; }
          .bold { font-weight: 700; }
          .sep { margin: 4px 0; border-top: 1px dashed #000; }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
    </html>
  `)
  doc.close()
  const win = frame.contentWindow
  if (!win) return
  setTimeout(() => {
    win.focus()
    win.print()
  }, 100)
  // Удаляем iframe по таймауту, а не по onafterprint: часть браузеров его
  // не вызывает, а в headless/PDF печать не блокирует поток. Иначе документ
  // исчезает раньше, чем его успевают прочитать (или остаётся навсегда).
  window.setTimeout(() => frame?.remove(), 15_000)
}

/** Короткий подтверждающий бип (успешное считывание марки). */
function beepMark(): void {
  try {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioCtx) return
    const ctx = new AudioCtx()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = 880
    gain.gain.setValueAtTime(0.0001, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.08)
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + 0.1)
    osc.onended = () => void ctx.close()
  } catch {
    /* звук недоступен */
  }
}

/** Сколько категорий показывать до раскрытия «Ещё». */
const CATEGORY_PREVIEW = 7
/** Сколько плиток каталога рендерить за раз. */
const GRID_PAGE = 120

/**
 * Человекочитаемая подпись состояния ККТ для бейджа статус-строки кассы.
 */
function kktStateText(state: 'ok' | 'paper_out' | 'shift_closed' | 'error' | 'no_device'): string {
  switch (state) {
    case 'ok':
      return 'ок'
    case 'paper_out':
      return 'нет бумаги'
    case 'shift_closed':
      return 'смена закрыта'
    case 'error':
      return 'ошибка'
    case 'no_device':
      return 'нет связи'
  }
}

/**
 * Причина, по которой чек не пробит на ККТ — человеческим языком.
 * Не путать с «нет связи»: речь про фискальный регистратор, а не интернет
 * (ARCH-TASK-65.6.2).
 */
function kktUnavailableReason(
  state: 'ok' | 'paper_out' | 'shift_closed' | 'error' | 'no_device' | undefined
): string {
  switch (state) {
    case 'paper_out':
      return 'нет бумаги'
    case 'shift_closed':
      return 'смена на ККТ закрыта'
    case 'error':
      return 'ошибка драйвера'
    case 'no_device':
      return 'устройство не найдено'
    default:
      return 'недоступен'
  }
}

interface PosPageProps {
  core: AppCore
  /** Мобильный режим (ширина < 768px) — плотность хрома и выбор устройства (камера/сканер). */
  isMobile?: boolean
  /**
   * Компактный лэйаут кассы (ширина < 1024px) — одна колонка + нижняя панель
   * + шторка чека. НЕ связан с устройством: на ноутбуке 900px лэйаут компактный,
   * а маркировка по-прежнему через аппаратный 2D-сканер.
   */
  compact?: boolean
  /** Имя активного кассира (для фискального чека и операций). */
  cashierName?: string
  /** Активный кассир (для проверки прав RBAC). */
  cashier?: ActiveCashier | null
}

/** Способ оплаты, выбранный кассиром. */
type PaymentMethod = 'cash' | 'card' | 'sbp'

/** Результат обработки штрихкода (для видоискателя камеры). */
export interface BarcodeScanOutcome {
  found: boolean
  name?: string
  barcode: string
  /** Цена за единицу найденного товара. */
  price?: number
  /** Сколько штук этого товара сейчас в чеке (после добавления). */
  cartQty?: number
  canCreate?: boolean
}

/** Позиция текущего чека (снимок цены/имени на момент добавления). */
/** Счётчик для генерации уникальных id строк чека. */
let lineSeq = 0

/**
 * Уникальный id строки чека. Пока строки различались только штрихкодом,
 * операции «взять цель марки / убрать / продать без марки» были неоднозначны
 * при дубликатах: два одинаковых маркируемых товара давали одну строку qty=2 и
 * одну марку на две единицы (нарушение). lineId делает строку адресуемой.
 */
const newLineId = (): string => `l${Date.now().toString(36)}-${(lineSeq += 1)}`

interface CartLine {
  /** Стабильный id строки чека (не меняется при правках количества). */
  lineId: string
  /** Строка исходного чека для line-level возврата (65.10). */
  originalLineId?: string
  /** id товара в каталоге (может отсутствовать у ещё не сохранённых). */
  productId?: number
  barcode: string
  name: string
  price: number
  qty: number
  /** Единица измерения ('шт' | 'кг'...). Весовые товары показываются в кг. */
  unit?: string
  requiresMarking?: boolean
  /** 65.10: кассир осознанно продаёт позицию без кода (право). */
  markingSkipped?: boolean
  /** 65.10: код не прошёл проверку — продажа без ККТ по праву. */
  markingInvalid?: boolean
  /** Причина пропуска/невалидного кода (код + комментарий). */
  markingReason?: string
  /** Код маркировки DataMatrix, если позиция уже «отмаркирована» кассиром. */
  datamatrix?: string
  /**
   * Максимум по исходному чеку (режим возврата, ARCH-TASK-65): больше
   * проданного вернуть нельзя, частичный возврат — уменьшением qty.
   */
  maxQty?: number
}

/**
 * Чек-кандидат для возврата (ARCH-TASK-65.5): остаток по строкам с учётом
 * ранее оформленных возвратов и статусы частичного/полного возврата.
 */
interface ReturnCandidate {
  receipt: SaleReceipt
  fullyReturned: boolean
  partiallyReturned: boolean
  /** Остаток по строкам чека (та же длина и порядок, что receipt.items). */
  byLine: number[]
  /** @deprecated С 65.10 нефискальный возврат — обычный документ. */
  requiresCorrection: boolean
  /** Исходная продажа без фискального чека: возврат наследует non_fiscal. */
  nonFiscalSource: boolean
}

/** Состояние модалки чека коррекции возврата прихода (ARCH-TASK-65.6). */
interface CorrectionModalState {
  receipt: SaleReceipt
  /** Доступно к возврату суммой (сумма чека минус оформленные коррекции). */
  remaining: number
  total: string
  basis: string
  baseDate: string
  baseNumber: string
  /** Состав чека коррекции: позиции исходной продажи (65.6.2). */
  items: CorrectionModalLine[]
  error?: string
}

/** Позиция в модалке коррекции: сколько продано и сколько ещё можно вернуть. */
interface CorrectionModalLine {
  lineId: string
  barcode: string
  name: string
  price: number
  unit?: string
  soldQty: number
  /** Остаток с учётом ранее оформленных коррекций. */
  availableQty: number
  /** Количество к возврату (строка ввода; допускаем дробные кг). */
  qty: string
  selected: boolean
}

/** Итог оформленной коррекции — подтверждение на экране (65.6.2). */
interface CorrectionSuccessState {
  total: number
  itemsCount: number
  status: ReceiptStatus
  fiscalData?: { fiscalDocumentNumber: number; fiscalDocumentSign: string }
  /** Состояние ККТ на момент оформления — для объяснения причины (65.6.2). */
  kktState?: 'ok' | 'paper_out' | 'shift_closed' | 'error' | 'no_device'
}

/** Короткое всплывающее уведомление. */
interface Toast {
  kind: 'error' | 'info'
  text: string
  /** Опциональное действие (например, «Добавить на лету»). */
  action?: { label: string; onClick: () => void }
}

/** Состояние модалки создания товара «на лету». */
interface CreateModalState {
  barcode: string
  name: string
  price: string
  /** Весовой товар: цена за кг, unit = 'кг'. */
  isWeight: boolean
  /** Требует кода маркировки при продаже. */
  requiresMarking: boolean
}

/** Состояние модалки ввода кодов маркировки (Честный Знак). */
interface MarkModalState {
  request: MarkRequest
  /** Введённые коды: lineId (или имя) позиции → DataMatrix. */
  codes: Record<string, string>
  /** Коллбэки resolve/reject для промиса резолвера плагина. */
  resolve: (codes: Record<string, string> | null) => void
}

/** Проблемы маркировки после PRE_SALE (65.10): убрать позицию или пропустить. */
interface MarkIssuesModalState {
  issues: MarkingIssue[]
  reasonCode: 'unreadable' | 'not_found' | 'supplier' | 'other'
  comment: string
}

/** Состояние модалки оплаты по СБП (QR + ожидание транзакции). */
interface SbpModalState {
  request: PaymentQrRequest
  qr: PaymentQr
  /** Готовый векторный SVG QR-кода по payload (строка разметки). */
  qrSvg: string
  /** 'waiting' — ждём статус (имитация WebSocket); 'succeeded' — оплачено. */
  phase: 'waiting' | 'succeeded'
  resolve: (outcome: PaymentOutcome) => void
}

/**
 * Кассовый экран (POS), Sprint 3.
 *
 * Две зоны по стандартам рынка (МойСклад/Эвотор):
 * - слева сетка ходовых товаров (добавление в один клик);
 * - справа текущий чек с управлением количеством, удалением строк, итогом
 *   и крупной кнопкой «ОПЛАТИТЬ».
 *
 * Источники добавления в чек: аппаратный сканер (через шину BARCODE_SCANNED),
 * ручной ввод штрихкода и клик по плитке. Поиск — по barcode активной версии.
 */
export function PosPage({ core, isMobile = false, compact = false, cashierName, cashier }: PosPageProps) {
  const { catalog, scales, fiscal } = core

  // Компактный ЛЭЙАУТ (одна колонка + нижняя панель). Мобильный экран тоже
  // компактный, поэтому isMobile включает его принудительно (защита от
  // вызова PosPage без пропа compact).
  const compactLayout = compact || isMobile
  // Плотность касания: POS-терминал 10.1", смартфон и грубый указатель →
  // крупнее цели. На мобильном лэйауте касание всегда основное.
  const touch = usePosDensity() === 'touch' || isMobile

  // RBAC: права активного кассира (отсутствие прав = «разрешено», обратная совместимость).
  const canCreateOnPos = cashier?.permissions?.can_create_on_pos !== false
  const canManageShifts = cashier?.permissions?.can_manage_shifts !== false
  // ARCH-TASK-65: возврат — отдельное право, НЕ наследуется от управления
  // сменами (только «Старший смены» и «Управляющий»). Роль admin — все права.
  const canRefund =
    cashier?.role === 'admin' || cashier?.permissions?.can_refund === true
  // Коррекции (возврат прихода по нефискальной продаже) — отдельное право.
  const canCorrect =
    cashier?.role === 'admin' ||
    (cashier?.permissions?.can_correction ??
      cashier?.permissions?.can_manage_shifts !== false) === true
  // 65.10: продажа маркированного товара без кода (non_fiscal + наличные).
  const canSkipMarking =
    cashier?.role === 'admin' || cashier?.permissions?.can_skip_marking === true
  // 65.10: списание возвращённого товара — отдельное право.
  const canWriteoff =
    cashier?.role === 'admin' || cashier?.permissions?.can_writeoff === true
  // 65.10: снять фискализацию — отдельное право (немаркированный товар).
  const canSkipFiscalization =
    cashier?.role === 'admin' || cashier?.permissions?.can_skip_fiscalization === true
  // Отключённая категория: продажа только с отдельным правом.
  const canSellInactiveCategory =
    cashier?.role === 'admin' ||
    cashier?.permissions?.can_sell_inactive_category === true

  const [cart, setCart] = useState<CartLine[]>([])
  // Актуальный снимок корзины для синхронного чтения из коллбэков камеры
  // (handleBarcode / onChangeItemQty), которые не должны ждать ре-рендер.
  const cartRef = useRef<CartLine[]>([])
  cartRef.current = cart
  // Навигация по категориям каталога + живой поиск по тексту.
  // Частота продаж за 7 дней: подъём категорий и блок «Часто покупают».
  const [frequency, setFrequency] = useState<{
    products: Map<string, number>
    categories: Map<string, number>
  }>(() => ({ products: new Map(), categories: new Map() }))
  const [frequentProducts, setFrequentProducts] = useState<Product[]>([])
  /** Показывать ли блок «Часто покупают» (настройка «Политики кассы»). */
  const [frequentEnabled, setFrequentEnabled] = useState(false)
  const [categories, setCategories] = useState<
    { name: string; count: number; hasImage?: boolean }[]
  >([])
  // Категории: показываем 7 самых крупных + «Ещё (N)» (иначе 27 чипов подряд).
  const [categoriesExpanded, setCategoriesExpanded] = useState(false)
  // Каталог: порционная отрисовка плиток (800+ кнопок в DOM — тяжело).
  const [gridLimit, setGridLimit] = useState(GRID_PAGE)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [subcategories, setSubcategories] = useState<
    { name: string; count: number; hasImage?: boolean }[]
  >([])
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('all')
  const [gridProducts, setGridProducts] = useState<Product[]>([])
  const [manualCode, setManualCode] = useState('')
  const [toast, setToast] = useState<Toast | null>(null)
  const [createModal, setCreateModal] = useState<CreateModalState | null>(null)
  // Галочка маркировки имеет смысл только при включённом модуле маркировки.
  // Состояние берём из generic-порта, без знания о конкретном плагине.
  const [czEnabled, setCzEnabled] = useState(() => core.marking?.isEnabled() ?? false)
  useEffect(() => {
    const sync = () => setCzEnabled(core.marking?.isEnabled() ?? false)
    sync()
    return core.bus.on(PosEvent.MODULE_ENABLED_CHANGED, sync)
  }, [core])
  const [saving, setSaving] = useState(false)
  const [paying, setPaying] = useState(false)
  const [markModal, setMarkModal] = useState<MarkModalState | null>(null)
  const [markIssuesModal, setMarkIssuesModal] = useState<MarkIssuesModalState | null>(null)
  // Синхронный снимок markModal для перехвата аппаратного 2D-сканера в
  // стабильном коллбэке шины (без пересоздания подписки).
  const markModalRef = useRef<MarkModalState | null>(null)
  markModalRef.current = markModal
  // Имя позиции, для которой открыта камера сканирования DataMatrix.
  const [cameraMarkTarget, setCameraMarkTarget] = useState<string | null>(null)
  // Ссылка на обработчик маршрутизации кода в марочную модалку (заполняется
  // ниже) — чтобы стабильная подписка на 2D-сканер звала свежую реализацию.
  const routeMarkRef = useRef<(code: string, target?: string) => boolean>(() => false)
  const [sbpModal, setSbpModal] = useState<SbpModalState | null>(null)
  const [online, setOnline] = useState(true)
  /** Статусы проверки кодов в ЧЗ (65.12.4) — приходят из модуля маркировки. */
  const [markStatus, setMarkStatus] = useState<
    Record<string, { status: 'valid' | 'invalid' | 'unknown'; reason?: string }>
  >({})
  const [currency, setCurrency] = useState<CurrencyCode>('RUB')
  const [cartSheetOpen, setCartSheetOpen] = useState(false)
  const [shift, setShift] = useState<PosShift | null>(null)
  const [shiftLoaded, setShiftLoaded] = useState(false)
  // Собственный device_id кассы: по нему определяется, «эта касса» ли открыла смену.
  const [myDeviceId, setMyDeviceId] = useState('')
  const [openShiftForm, setOpenShiftForm] = useState<{ initialCash: string; fiscal: boolean } | null>(null)
  const [zReport, setZReport] = useState<ShiftSummary | null>(null)
  const [fiscalizeThisSale, setFiscalizeThisSale] = useState(true)
  // Модалка выбора способа оплаты.
  const [payModal, setPayModal] = useState<null | { stage: 'choose' | 'cash' | 'card' }>(null)
  const [cashReceived, setCashReceived] = useState('')
  // ARCH-TASK-65 «Возвраты»: режим возврата + поиск исходного чека этой кассы.
  const [returnMode, setReturnMode] = useState(false)
  // «Возврат + списание» (65.10): причина обязательна.
  const [returnWriteoff, setReturnWriteoff] = useState(false)
  const [writeoffReason, setWriteoffReason] = useState<WriteoffReasonCode>('damage')
  const [writeoffComment, setWriteoffComment] = useState('')
  const [returnSearch, setReturnSearch] = useState('')
  const [returnCandidates, setReturnCandidates] = useState<ReturnCandidate[]>([])
  /** Чек-источник возврата (для ограничения qty по проданному). */
  const [returnSource, setReturnSource] = useState<SaleReceipt | null>(null)
  /** Модалка чека коррекции возврата прихода (65.6). */
  const [correctionModal, setCorrectionModal] = useState<CorrectionModalState | null>(null)
  /** Подтверждение успешной коррекции (65.6.2). */
  const [correctionSuccess, setCorrectionSuccess] = useState<CorrectionSuccessState | null>(null)
  // Снимок режима возврата для коллбэков без зависимости от состояния:
  // в возврате не запрашиваем коды маркировки (вне объёма 65).
  const returnModeRef = useRef(false)
  returnModeRef.current = returnMode
  const [cardMode, setCardMode] = useState<'manual' | 'integrated'>('manual')
  const [cameraOpen, setCameraOpen] = useState(false)
  // Единый блок «Проблемы»: всегда свёрнут, подсвечивается при появлении
  // критичной проблемы. Действие, требующее прав, подтверждается PIN.
  const [problemsOpen, setProblemsOpen] = useState(false)
  const [problemsHighlighted, setProblemsHighlighted] = useState(false)
  const lastCriticalSignatureRef = useRef('')
  const [adminConfirm, setAdminConfirm] = useState<{ label: string; run: () => void } | null>(
    null
  )
  const [moreActionsOpen, setMoreActionsOpen] = useState(false)
  const [weightModal, setWeightModal] = useState<{ product: Product } | null>(null)
  const [weightInput, setWeightInput] = useState('')
  // Весы (десктоп): живое показание из Hardware Agent + статус подключения.
  const [scaleReading, setScaleReading] = useState<{ kg: number; stable: boolean } | null>(null)
  const [scaleStatus, setScaleStatus] = useState(() => scales.getStatus())
  // Ручной ввод веса принудительно (битые весы) — переключатель в модалке.
  const [weightManual, setWeightManual] = useState(false)
  // Статус ККТ (фискальный регистратор): бейдж в шапке смены + предупреждение по ФН.
  const [kktStatus, setKktStatus] = useState(() => fiscal.getKktStatus())
  // Смена открыта другим ПО, а локальной смены POSPoint нет: это observer-режим,
  // а не adoption. Z и закрытие остаются у ПО-владельца смены.
  const externalKktShift = useMemo(
    () =>
      !shift &&
      kktStatus?.connected === true &&
      kktStatus.shiftOpen === true &&
      (kktStatus.shiftNumber ?? 0) > 0,
    [shift, kktStatus]
  )
  const kktShiftMismatch = useMemo(
    () => detectKktShiftMismatch(shift, kktStatus),
    [shift, kktStatus]
  )
  // Вес последнего авто-приёма: защита от дубля, пока груз не сняли.
  const lastAutoKgRef = useRef<number | null>(null)
  // Отложенный авто-приём (дебаунс). Таймер держим в ref, чтобы «дрожание»
  // стабильного веса не перезапускало отсчёт бесконечно.
  const autoConfirmRef = useRef<{ kg: number; timer: ReturnType<typeof setTimeout> } | null>(null)
  // Живая панель весов вместо numpad: десктоп + весы подключены + не ручной режим.
  const liveScaleMode = scaleStatus.connected && !weightManual
  // Вскрытие тары (пул массы): модалка открытия/просмотра голов сыра/колбасы.
  const [containerModal, setContainerModal] = useState<{
    tab: 'new' | 'list'
    product: Product | null
    search: string
    datamatrix: string
    weight: string
  } | null>(null)
  // Кандидаты весовых товаров для выбора в модалке вскрытия тары.
  const [containerCandidates, setContainerCandidates] = useState<Product[]>([])
  // Все тары (активные + исчерпанные) для вкладки «Открытые тары».
  const [openedContainers, setOpenedContainers] = useState<OpenedContainer[]>([])
  // Счётчик активных тар для бейджа в шапке смены.
  const [activeContainerCount, setActiveContainerCount] = useState(0)
  // Открыт ли экранный numpad для поля веса головки.
  const [containerNumpadOpen, setContainerNumpadOpen] = useState(false)
  // Инлайн-редактирование остатка тары («<Pencil size={16} /> Дописать вес»).
  const [containerEdit, setContainerEdit] = useState<{ id: number; value: string } | null>(null)
  // Экран успеха/сдачи после оплаты.
  const [successScreen, setSuccessScreen] = useState<{
    receiptId: string
    total: number
    method: PaymentMethod
    /** true — оформлен возврат (ARCH-TASK-65): итог показывается со знаком «−». */
    isReturn?: boolean
    cashGiven?: number
    change?: number
    /** Фискальный статус чека: fiscalized | offline_pending | non_fiscal. */
    status?: ReceiptStatus
    /** Предупреждение (напр. ККТ недоступна — дофискализация позже). */
    warning?: string
    fiscalData?: { fiscalDocumentNumber: number; fiscalDocumentSign: string }
    offlineReceiptNumber?: string
  } | null>(null)
  // Сдача последнего наличного чека: видна в области итога, пока корзина
  // пуста, и сбрасывается при добавлении первого товара в новый чек.
  const [lastChange, setLastChange] = useState<{
    change: number
    cashGiven: number
    total: number
  } | null>(null)
  // Количество чеков, отложенных к дофискализации (offlineQueue type='fiscalize').
  const [pendingFiscalCount, setPendingFiscalCount] = useState(0)
  // Прочитанные info-уведомления текущей смены. Ключ привязан к смене:
  // после открытия новой «Маркировка отключена» снова станет заметной.
  const [ackNotices, setAckNotices] = useState<PosProblem['id'][]>([])
  // B3/65.14: задачи замены non_fiscal → fiscal (уведомление кассиру).
  const [replacementTasks, setReplacementTasks] = useState<ReplacementTask[]>([])
  const [replacementModal, setReplacementModal] = useState<SaleReceipt | null>(null)
  const [replacementAmount, setReplacementAmount] = useState('')
  const [replacementReason, setReplacementReason] = useState('')
  const [replacementBusy, setReplacementBusy] = useState(false)
  // 65.14: hold-чеки (fiscalIntent=true): печать после valid или отмена.
  const [heldTasks, setHeldTasks] = useState<ReplacementTask[]>([])
  const [heldModal, setHeldModal] = useState<SaleReceipt | null>(null)
  const [heldAmount, setHeldAmount] = useState('')
  const [heldReason, setHeldReason] = useState('')
  const [heldBusy, setHeldBusy] = useState(false)
  // Очередь ожидания марок: barcode'ы строк чека, которым нужен DataMatrix.
  // Несколько маркируемых товаров подряд ждут сканы по порядку (F4).
  const scanMarkQueueRef = useRef<string[]>([])
  // Зеркало очереди в состоянии — чтобы React рендерил блокирующий диалог
  // «Требуется маркировка» (ref сам по себе рендер не триггерит).
  const [pendingMarks, setPendingMarks] = useState<string[]>([])
  // Синхронный снимок «открыта ли модалка заведения товара на лету».
  const createModalOpenRef = useRef(false)
  createModalOpenRef.current = createModal != null
  // Стабильный мост: скан ШК в поле «Штрихкод» модалки «на лету» (F6).
  const createModalBarcodeRef = useRef<(code: string) => void>(() => {})
  // Синхронный снимок «открыта ли вкладка вскрытия НОВОЙ тары» для 2D-сканера.
  // На вкладке списка сканер не должен писать код в скрытую форму.
  const containerModalOpenRef = useRef(false)
  containerModalOpenRef.current = containerModal?.tab === 'new'
  // Стабильный мост к свежему обработчику DataMatrix головки (для подписки на
  // шину сканера без её пересоздания).
  const containerDatamatrixRef = useRef<(code: string) => void>(() => {})
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  /**
   * Синхронизировать зеркало очереди марок с ref. Вызывается во ВСЕХ точках
   * мутации `scanMarkQueueRef`, иначе диалог «Требуется маркировка» разойдётся
   * с реальным состоянием ожидания.
   */
  const syncPendingMarks = useCallback(() => {
    setPendingMarks([...scanMarkQueueRef.current])
  }, [])

  const showToast = useCallback((t: Toast) => {
    setToast(t)
    if (toastTimer.current) {
      clearTimeout(toastTimer.current)
    }
    // Уведомления с действием держим дольше (у кассира должно быть время нажать).
    toastTimer.current = setTimeout(() => setToast(null), t.action ? 6000 : 3000)
  }, [])

  /**
   * Поиск чеков этой кассы для возврата (номер / дата / сумма).
   * Дебаунс 250 мс: список читается из локальной БД, но не на каждый символ.
   */
  useEffect(() => {
    if (!returnMode) return
    let cancelled = false
    const timer = setTimeout(() => {
      void core.sale.findReceiptsForReturn(returnSearch).then((list) => {
        if (!cancelled) setReturnCandidates(list)
      })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [returnMode, returnSearch, core.sale])

  /** Войти в режим возврата: пустая корзина и список чеков устройства. */
  const enterReturnMode = useCallback(() => {
    // В корзине может быть начатая продажа — не теряем её молча.
    if (
      cartRef.current.length > 0 &&
      !window.confirm('В чеке есть позиции продажи. Перейти в возврат и очистить чек?')
    ) {
      return
    }
    setReturnMode(true)
    setReturnSearch('')
    setReturnSource(null)
    setReturnWriteoff(false)
    setWriteoffComment('')
    setCart([])
    scanMarkQueueRef.current = []
    syncPendingMarks()
  }, [syncPendingMarks])

  /**
   * Выйти из режима возврата (корзина возврата сбрасывается).
   * @param force true — без подтверждения (вызывается после успешного возврата).
   */
  const exitReturnMode = useCallback(
    (force = false) => {
      if (
        !force &&
        cartRef.current.length > 0 &&
        !window.confirm('Выйти из режима возврата и очистить чек?')
      ) {
        return
      }
      setReturnMode(false)
      setReturnSearch('')
      setReturnSource(null)
      setReturnCandidates([])
      setReturnWriteoff(false)
      setWriteoffComment('')
      setCart([])
      scanMarkQueueRef.current = []
      syncPendingMarks()
    },
    [syncPendingMarks]
  )

  /**
   * Подставить позиции исходного чека в чек возврата.
   * Частичный возврат — кассир уменьшает количество строк; `maxQty` — остаток
   * с учётом уже оформленных возвратов (ARCH-TASK-65.5).
   */
  const loadReturnReceipt = useCallback(
    (candidate: ReturnCandidate) => {
      const { receipt, fullyReturned, byLine } = candidate
      if (fullyReturned) {
        showToast({ kind: 'info', text: 'По этому чеку уже возвращено всё проданное' })
        return
      }
      // Остаток берём ПОСТРОЧНО (byLine): дубли одного товара не должны
      // получать полный остаток каждый (ревью 65.5).
      const lines = receipt.items
        .map((it, idx) => {
          const left = byLine[idx] ?? 0
          return {
            lineId: newLineId(),
            // Возврат адресует конкретную строку исходного чека (line-level).
            originalLineId: it.lineId,
            barcode: it.barcode,
            name: it.name,
            price: it.price,
            unit: it.unit,
            // Возвращаем только остаток: проданное минус уже возвращённое.
            qty: left,
            maxQty: left,
            // Маркированный возврат несёт код исходной строки (возврат в оборот).
            requiresMarking: Boolean(it.requiresMarking || it.datamatrix),
            datamatrix: it.datamatrix
          }
        })
        .filter((line) => line.qty > 0)
      if (lines.length === 0) {
        showToast({ kind: 'info', text: 'По этому чеку больше нечего возвращать' })
        return
      }
      setReturnSource(receipt)
      setCart(lines)
      showToast({ kind: 'info', text: 'Позиции чека добавлены. Проверьте количество.' })
    },
    [showToast]
  )

  /** Открыть чек коррекции возврата прихода по нефискальной продаже (65.6). */
  const openCorrectionModal = useCallback(
    async (receipt: SaleReceipt) => {
      if (!canCorrect) {
        showToast({ kind: 'error', text: 'Нет права на чеки коррекции' })
        return
      }
      const already = await core.correction.correctionReturnsTotal(receipt.receiptId)
      const remaining = Number(Math.max(0, Math.abs(receipt.total) - already).toFixed(2))
      if (remaining <= 0.01) {
        showToast({ kind: 'info', text: 'По этому чеку уже возвращена вся сумма' })
        return
      }
      // Остаток по каждой позиции с учётом ранее оформленных коррекций.
      const returnedItems = await core.correction.correctionReturnedItems(receipt.receiptId)
      const available = returnableQtyByLine(receipt.items, returnedItems)
      const lines: CorrectionModalLine[] = receipt.items.map((it, idx) => {
        const availableQty = available[idx] ?? 0
        return {
          lineId: newLineId(),
          barcode: it.barcode,
          name: it.name,
          price: it.price,
          unit: it.unit,
          soldQty: it.qty,
          availableQty,
          qty: String(availableQty),
          selected: availableQty > 0
        }
      })
      // Сумма по составу по умолчанию; если позиций не осталось (например,
      // ранее вернули сумму без состава) — подставляем остаток по деньгам.
      const composition = lines
        .filter((l) => l.selected)
        .reduce((sum, l) => sum + l.price * Number(l.qty.replace(',', '.')), 0)
      const total = composition > 0 ? Math.min(composition, remaining) : remaining
      const d = new Date(receipt.createdAt)
      setCorrectionModal({
        receipt,
        remaining,
        total: String(Number(total.toFixed(2))),
        basis: 'Возврат по чеку без фискального чека',
        // ФФД: дата документа основания в формате YYYY-MM-DD.
        baseDate: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
          d.getDate()
        ).padStart(2, '0')}`,
        baseNumber: shortId(receipt.receiptId),
        items: lines
      })
    },
    [canCorrect, core.correction, showToast]
  )

  /** Правка позиции в модалке коррекции: выбор/количество, автопересчёт суммы. */
  const changeCorrectionLine = (
    lineId: string,
    patch: Partial<Pick<CorrectionModalLine, 'qty' | 'selected'>>
  ) => {
    setCorrectionModal((m) => {
      if (!m) return m
      const items = m.items.map((line) =>
        line.lineId === lineId ? { ...line, ...patch } : line
      )
      const composition = items
        .filter((line) => line.selected)
        .reduce((sum, line) => {
          const qty = Number(line.qty.replace(',', '.'))
          return Number.isFinite(qty) && qty > 0 ? sum + line.price * qty : sum
        }, 0)
      return {
        ...m,
        items,
        // Сумма пересчитывается автоматически; кассир может поправить вручную.
        total: composition > 0 ? String(Number(composition.toFixed(2))) : m.total,
        error: undefined
      }
    })
  }

  /** Пробить чек коррекции (возврат прихода) по нефискальной продаже. */
  const submitCorrection = async () => {
    if (!correctionModal) return
    const modal = correctionModal
    const lines = modal.items.filter((line) => {
      const qty = Number(line.qty.replace(',', '.'))
      return line.selected && Number.isFinite(qty) && qty > 0
    })
    for (const line of lines) {
      const qty = Number(line.qty.replace(',', '.'))
      if (qty > line.availableQty + 1e-3) {
        setCorrectionModal((m) =>
          m
            ? { ...m, error: `«${line.name}»: доступно к возврату ${line.availableQty}` }
            : m
        )
        return
      }
    }
    const total = Number(modal.total.replace(',', '.'))
    if (!Number.isFinite(total) || total <= 0) {
      setCorrectionModal((m) => (m ? { ...m, error: 'Укажите сумму больше нуля' } : m))
      return
    }
    if (total > modal.remaining + 0.01) {
      setCorrectionModal((m) =>
        m ? { ...m, error: `Доступно к возврату: ${modal.remaining}` } : m
      )
      return
    }
    setPaying(true)
    try {
      const result = await core.correction.createCorrection({
        settlementType: 'income_return',
        correction: {
          kind: 'self',
          basis: modal.basis.trim(),
          baseDate: modal.baseDate,
          baseNumber: modal.baseNumber.trim()
        },
        total,
        items: lines.map((line) => ({
          barcode: line.barcode,
          name: line.name,
          qty: Number(line.qty.replace(',', '.')),
          price: line.price,
          unit: line.unit
        })),
        // Безнал исходной продажи → electronic, наличные → cash.
        paymentMethod:
          modal.receipt.paymentMethod && modal.receipt.paymentMethod !== 'cash'
            ? 'electronic'
            : 'cash',
        cashierName,
        originalReceiptId: modal.receipt.receiptId
      })
      if (!result.ok) {
        setCorrectionModal((m) =>
          m ? { ...m, error: result.error ?? 'Не удалось пробить коррекцию' } : m
        )
        return
      }
      setCorrectionModal(null)
      // Полноценное подтверждение на экране (раньше был только тост).
      setCorrectionSuccess({
        total,
        itemsCount: lines.length,
        status: result.status,
        kktState: result.kktState,
        fiscalData: result.fiscalData
          ? {
              fiscalDocumentNumber: result.fiscalData.fiscalDocumentNumber,
              fiscalDocumentSign: result.fiscalData.fiscalDocumentSign
            }
          : undefined
      })
      // Обновляем остаток в списке чеков возврата.
      void core.sale.findReceiptsForReturn(returnSearch).then(setReturnCandidates)
    } catch (err: unknown) {
      setCorrectionModal((m) =>
        m ? { ...m, error: err instanceof Error ? err.message : String(err) } : m
      )
    } finally {
      setPaying(false)
    }
  }

  /**
   * Пересчёт частоты продаж. Запускается при старте и после чека, а не на
   * каждом добавлении товара: порядок плиток не «прыгает» под пальцем.
   */
  const reloadFrequency = useCallback(() => {
    void catalog
      .getFrequencyStats()
      .then(async (stats) => {
        setFrequency(stats)
        const products = await catalog.listActiveProducts()
        setFrequentProducts(
          pickFrequentProducts(
            products.filter((p) => !p.isDeleted && p.isActive !== false),
            stats.products
          ).map((entry) => entry.product)
        )
      })
      .catch(() => {
        setFrequency({ products: new Map(), categories: new Map() })
        setFrequentProducts([])
      })
  }, [catalog])

  /** Перезагрузить верхний уровень дерева категорий (с количеством товаров). */
  const reloadCategories = useCallback(() => {
    void catalog
      .listTopCategories()
      .then(setCategories)
      .catch(() => setCategories([]))
  }, [catalog])

  /** Перезагрузить листья выбранного верхнего уровня. */
  const reloadSubcategories = useCallback(
    (category: string) => {
      if (category === 'all') {
        setSubcategories([])
        return
      }
      void catalog
        .listChildCategories(category)
        .then(setSubcategories)
        .catch(() => setSubcategories([]))
    },
    [catalog]
  )

  /**
   * Пересчитать товары в сетке каталога. Если в строке поиска есть текст —
   * показываем результаты живого поиска (в т.ч. товары без штрихкода по
   * названию), иначе — товары выбранного поддерева.
   */
  const reloadGrid = useCallback(
    (query: string, category: string, subcategory: string) => {
      const q = query.trim()
      // Главный экран показывает категории, а не товары: полный список
      // не читаем, пока кассир не выбрал категорию или не начал поиск.
      if (!q && category === 'all') {
        setGridProducts([])
        return
      }
      const loader = q
        ? catalog.searchProducts(q)
        : catalog.listProductsByCategoryPath(
            category,
            subcategory === 'all' ? undefined : subcategory
          )
      void loader.then(setGridProducts).catch(() => setGridProducts([]))
    },
    [catalog]
  )

  useEffect(() => {
    return () => {
      if (toastTimer.current) {
        clearTimeout(toastTimer.current)
      }
    }
  }, [])

  // Подписка на весы (Hardware Agent): живые показания + статус подключения.
  // Сняли груз (вес < 5 г) — разрешаем следующий авто-приём.
  useEffect(() => {
    const offWeight = scales.onWeight((reading) => {
      setScaleReading({ kg: reading.kg, stable: reading.stable })
      if (reading.kg < 0.005) lastAutoKgRef.current = null
    })
    const offStatus = scales.onStatus((status) => setScaleStatus(status))
    scales.requestStatus()
    return () => {
      offWeight()
      offStatus()
    }
  }, [scales])

  // Подписка на статус ККТ: бейдж в шапке смены обновляется push-событиями.
  // Раз в 30 с запрашиваем статус принудительно: очередь ОФД внутри ФН
  // уменьшается асинхронно, без push-события от драйвера.
  useEffect(() => {
    const off = fiscal.onKktStatus((status) => setKktStatus(status))
    fiscal.requestStatus()
    const timer = window.setInterval(() => fiscal.requestStatus(), 30_000)
    return () => {
      off()
      window.clearInterval(timer)
    }
  }, [fiscal])

  /**
   * Добавить товар в чек.
   * - штучный: при повторе увеличиваем qty на 1 (или на qtyOverride).
   * - весовой (unit==='кг') без qtyOverride: открываем модалку ввода веса.
   * - весовой с qtyOverride (из весового ШК): добавляем сразу этим весом.
   */
  const addProductToCart = useCallback(
    (product: Product, qtyOverride?: number, datamatrix?: string, lineId?: string) => {
      // Начался новый чек — сдача прошлого больше не актуальна.
      setLastChange(null)
      const isWeight = product.unit === 'кг'
      if (isWeight && qtyOverride == null) {
        setWeightInput('')
        // Новый цикл ввода: ручной режим сбрасываем. Анти-дубль (lastAutoKgRef)
        // НЕ сбрасываем: он живёт между позициями — пока груз не снят с весов,
        // тот же вес повторно не принимается (иначе — дубль строки).
        setWeightManual(false)
        setWeightModal({ product })
        return
      }
      const addQty = qtyOverride ?? 1
      setCart((prev) => {
        const idx = prev.findIndex((l) => {
          const sameProduct =
            product.id != null ? l.productId === product.id : l.barcode === product.barcode
          if (!sameProduct) return false
          // Маркируемый товар — отдельная строка на каждую единицу/марку.
          // Слияние допустимо ТОЛЬКО при том же коде марки (напр. пул одной
          // вскрытой тары: вся нарезка списывается из одного кода).
          if (product.requiresMarking) {
            return datamatrix != null && l.datamatrix === datamatrix
          }
          return true
        })
        if (idx !== -1) {
          const next = [...prev]
          next[idx] = {
            ...next[idx],
            qty: Number((next[idx].qty + addQty).toFixed(3)),
            // Если новый код марки пришёл — не затираем уже имеющийся.
            datamatrix: next[idx].datamatrix ?? datamatrix
          }
          return next
        }
        return [
          ...prev,
          {
            // lineId передан вызывающим (напр. взвод очереди марок) или новый.
            lineId: lineId ?? newLineId(),
            productId: product.id,
            barcode: product.barcode,
            name: product.name,
            price: product.price,
            qty: addQty,
            unit: product.unit,
            // 65.10: в возврате НЕ отключаем признак маркировки — иначе ручной
            // возврат маркированного товара уходил бы без кода.
            requiresMarking: product.requiresMarking,
            datamatrix
          }
        ]
      })
    },
    []
  )

  /**
   * Привязать код марки к строке чека по её lineId (scan-time marking).
   * Адресация по lineId (а не по штрихкоду) — единственный корректный способ
   * при дубликатах одного и того же маркируемого товара в чеке.
   */
  const attachMarkToLine = useCallback(
    (lineId: string, code: string) => {
      // Нормализуем один раз: и в чек, и в очередь проверки должен уйти
      // одинаковый код, иначе бейдж не найдёт статус (HID-сканеры шлют \r\n).
      const normalized = core.marking?.inspect(code).normalized ?? code
      setCart((prev) =>
        prev.map((l) =>
          l.lineId === lineId && !l.datamatrix ? { ...l, datamatrix: normalized } : l
        )
      )
      // 65.12.4: статус в обороте выясняем фоном — продажа не ждёт контур.
      core.marking?.verify([normalized])
    },
    [core.marking]
  )

  /**
   * Взять цель для марки из очереди (F4): первый lineId, чья строка чека ещё
   * БЕЗ DataMatrix. Из очереди он удаляется. Строки, которых уже нет в чеке
   * (или уже промаркированные), из очереди вычищаются.
   * @returns lineId строки для привязки марки или null.
   */
  const takeMarkTarget = useCallback((): string | null => {
    const queue = scanMarkQueueRef.current
    for (let i = 0; i < queue.length; i += 1) {
      const lineId = queue[i]
      const line = cartRef.current.find((l) => l.lineId === lineId)
      if (line && !line.datamatrix) {
        queue.splice(i, 1)
        syncPendingMarks()
        return lineId
      }
      // Строки нет / уже промаркирована — в очереди держать нечего.
      queue.splice(i, 1)
      i -= 1
    }
    syncPendingMarks()
    return null
  }, [syncPendingMarks])

  /** Убрать строку из чека и снять её из очереди ожидания марок (A2). */
  const dropPendingLine = useCallback(
    (lineId: string) => {
      setCart((prev) => prev.filter((l) => l.lineId !== lineId))
      scanMarkQueueRef.current = scanMarkQueueRef.current.filter((id) => id !== lineId)
      syncPendingMarks()
    },
    [syncPendingMarks]
  )

  /**
   * «Без марки» из scan-time диалога (65.10).
   *
   * НЕ отключает `requiresMarking` (это был обход правил): открывает модалку
   * причины, а решение применяется через `skipMarkingIssueLines` — с правом
   * `can_skip_marking`, причиной и принудительным non_fiscal + наличными.
   */
  const sellWithoutMark = useCallback(
    (lineId: string) => {
      const line = cartRef.current.find((l) => l.lineId === lineId)
      if (!line) return
      if (!canSkipMarking) {
        showToast({
          kind: 'error',
          text: 'Нет права продавать маркированный товар без кода — уберите позицию'
        })
        return
      }
      setMarkIssuesModal({
        issues: [
          {
            lineId,
            name: line.name,
            kind: 'missing',
            reason: 'Код маркировки не введён'
          }
        ],
        reasonCode: 'unreadable',
        comment: ''
      })
    },
    [canSkipMarking, showToast]
  )

  /**
   * Направить скан ШК в поле «Штрихкод» модалки заведения товара «на лету»
   * (F6). Без этого скан уходил в handleBarcode и в форме «терялся».
   */
  const applyCreateModalBarcode = useCallback((code: string) => {
    setCreateModal((m) => (m ? { ...m, barcode: code.trim() } : m))
  }, [])
  createModalBarcodeRef.current = (code: string) => applyCreateModalBarcode(code)

  /**
   * Пересчитать тары: список для вкладки «Открытые тары» и бейдж активных
   * тар в шапке смены.
   */
  const reloadOpenedContainers = useCallback(async (): Promise<void> => {
    const all = await db.opened_containers.orderBy('openedAt').reverse().toArray()
    setOpenedContainers(all)
    setActiveContainerCount(all.filter((c) => c.status === 'active').length)
  }, [])

  // Первичная загрузка счётчика активных тар при старте кассы.
  useEffect(() => {
    void reloadOpenedContainers()
  }, [reloadOpenedContainers])

  /**
   * Найти активную вскрытую тару для штрихкода с остатком массы.
   * @returns запись пула или null.
   */
  const findActiveContainer = useCallback(async (barcode: string) => {
    const list = await db.opened_containers
      .where('barcode')
      .equals(barcode)
      .toArray()
    return (
      list.find((c) => c.status === 'active' && c.remainingWeight > 0) ?? null
    )
  }, [])

  /**
   * Списать массу qty из вскрытой тары. При исчерпании остатка — статус
   * 'exhausted'. Обновляет бейдж активных тар.
   */
  const consumeFromContainer = useCallback(
    async (containerId: number, qty: number): Promise<void> => {
      const c = await db.opened_containers.get(containerId)
      if (!c) return
      const remaining = Number((c.remainingWeight - qty).toFixed(3))
      await db.opened_containers.update(containerId, {
        remainingWeight: remaining > 0 ? remaining : 0,
        status: remaining > 0 ? 'active' : 'exhausted'
      })
      await reloadOpenedContainers()
    },
    [reloadOpenedContainers]
  )

  /**
   * Добавить маркируемый товар с учётом пула вскрытой тары и scan-time марки.
   *
   * - requiresMarking + есть активная тара → привязываем её DataMatrix,
   *   списываем массу qty, DataMatrix у кассира НЕ запрашиваем.
   * - requiresMarking + тары нет → добавляем ОТДЕЛЬНУЮ строку (одна единица =
   *   одна марка) и ждём DataMatrix: на мобиле открываем камеру, на десктопе —
   *   блокирующий диалог, а код принимает аппаратный 2D-сканер.
   * - без маркировки → обычное добавление.
   */
  const addProductWithMarking = useCallback(
    async (product: Product, qtyOverride?: number): Promise<void> => {
      const qty = qtyOverride ?? 1
      // Модуль отключён — не требуем DataMatrix даже у маркированного товара.
      if (core.marking && !core.marking.isEnabled()) {
        addProductToCart({ ...product, requiresMarking: false }, qtyOverride)
        return
      }
      // Режим возврата: для маркированного товара код всё равно нужен —
      // иначе возврат уйдёт фискальным без кода (65.10). Для немаркированного
      // просто добавляем строку.
      if (returnModeRef.current) {
        if (!product.requiresMarking) {
          addProductToCart(product, qtyOverride)
          return
        }
        const lineId = newLineId()
        addProductToCart(product, qtyOverride, undefined, lineId)
        scanMarkQueueRef.current.push(lineId)
        syncPendingMarks()
        if (isMobile) setCameraOpen(true)
        showToast({ kind: 'info', text: 'Отсканируйте код маркировки для возврата' })
        return
      }
      if (!product.requiresMarking) {
        addProductToCart(product, qtyOverride)
        return
      }
      const container = await findActiveContainer(product.barcode)
      if (container?.id != null) {
        // Пул массы: привязываем код головы, списываем вес, без запроса марки.
        addProductToCart(product, qtyOverride, container.datamatrix)
        await consumeFromContainer(container.id, qty)
        showToast({
          kind: 'info',
          text: `Из вскрытой тары «${container.name}»: списано ${formatKg(qty)} кг (код марки привязан)`
        })
        return
      }
      // Тары нет — добавляем ОТДЕЛЬНОЙ строкой и ждём её код маркировки.
      // lineId генерируем заранее: очередь марок адресуется именно им, поэтому
      // две единицы одного товара дают две строки и две марки.
      const lineId = newLineId()
      addProductToCart(product, qtyOverride, undefined, lineId)
      scanMarkQueueRef.current.push(lineId)
      syncPendingMarks()
      // Камера для DataMatrix — ТОЛЬКО на мобиле; на стационаре аппаратный сканер.
      // На десктопе вместо таймаутного тоста показывается блокирующий диалог
      // «Требуется маркировка» (рендерится по pendingMarks).
      if (isMobile) setCameraOpen(true)
      showToast({ kind: 'info', text: 'Отсканируйте код маркировки DataMatrix' })
    },
    [
      addProductToCart,
      core.marking,
      findActiveContainer,
      consumeFromContainer,
      showToast,
      isMobile,
      syncPendingMarks
    ]
  )

  /** Текущее количество товара в чеке (по снимку корзины до добавления). */
  const cartQtyFor = useCallback((barcode: string, productId?: number): number => {
    const line = cartRef.current.find((l) =>
      productId != null ? l.productId === productId : l.barcode === barcode
    )
    return line?.qty ?? 0
  }, [])

  /**
   * Изменить количество товара в чеке на delta (+1 / −1) по штрихкоду.
   * Используется виджетом камеры. Возвращает новое количество (0 — строки нет).
   */
  const onChangeItemQty = useCallback((barcode: string, delta: number): number => {
    const idx = cartRef.current.findIndex((l) => l.barcode === barcode)
    if (idx === -1) return 0
    const current = cartRef.current[idx]
    const nextQty = Number((current.qty + delta).toFixed(3))
    setCart((prev) => {
      const i = prev.findIndex((l) => l.barcode === barcode)
      if (i === -1) return prev
      const next = [...prev]
      if (nextQty <= 0) {
        next.splice(i, 1)
      } else {
        next[i] = { ...next[i], qty: nextQty }
      }
      return next
    })
    return nextQty > 0 ? nextQty : 0
  }, [])

  /**
   * Обработать штрихкод (сканер/ручной ввод/камера).
   * @returns результат для видоискателя камеры (found/canCreate).
   */
  const handleBarcode = useCallback(
    async (rawCode: string): Promise<BarcodeScanOutcome> => {
      const code = rawCode.trim()
      if (!code) {
        return { found: false, barcode: code, canCreate: false }
      }

      // Весовой штрихкод (префиксы 20-29): извлекаем артикул и вес, ищем товар
      // по прямому ШК, затем по артикулу, добавляем сразу с точным весом.
      const weight = parseWeightBarcode(code)
      if (weight) {
        let product = await catalog.findByBarcode(code)
        if (!product) {
          product = await catalog.findByItemCode(weight.itemCode)
        }
        if (product) {
          await addProductWithMarking(product, weight.weightKg)
          showToast({ kind: 'info', text: `Добавлено: ${product.name} (${weight.weightKg} кг)` })
          const qty = cartQtyFor(product.barcode, product.id) + weight.weightKg
          return {
            found: true,
            name: product.name,
            barcode: code,
            price: product.price,
            cartQty: Number(qty.toFixed(3))
          }
        }
        showToast({ kind: 'error', text: `Весовой товар не найден (артикул ${weight.itemCode})` })
        return {
          found: false,
          barcode: code,
          canCreate: (await catalog.isOnTheFlyAllowed()) && canCreateOnPos
        }
      }

      const product = await catalog.findByBarcode(code)
      if (product) {
        // Отключённая категория: не продаём без отдельного права.
        const categoryActive = await catalog.isCategoryActive(
          product.categoryParent,
          product.category
        )
        if (!categoryActive && !canSellInactiveCategory) {
          showToast({
            kind: 'error',
            text: `Категория «${product.categoryParent || product.category || 'Общее'}» отключена — продажа запрещена`
          })
          return { found: false, barcode: code, canCreate: false }
        }
        // Весовой маркируемый товар через addProductWithMarking (пул тары),
        // штучный — через него же (откроет scan-time марку при необходимости).
        if (product.unit === 'кг') {
          // Без qtyOverride весовой уходит в модалку веса — там и применится
          // маркировка (confirmWeight вызывает addProductWithMarking).
          addProductToCart(product)
        } else {
          await addProductWithMarking(product, 1)
        }
        showToast({ kind: 'info', text: `Добавлено: ${product.name}` })
        const qty = cartQtyFor(product.barcode, product.id) + 1
        return {
          found: true,
          name: product.name,
          barcode: code,
          price: product.price,
          cartQty: qty
        }
      }

      // Товар не найден. Для ручного ввода/HID — тост с действием «на лету».
      const allowed = (await catalog.isOnTheFlyAllowed()) && canCreateOnPos
      showToast({
        kind: 'error',
        text: `Товар не найден в каталоге: ${code}`,
        action: allowed
          ? {
              label: 'Добавить на лету',
              onClick: () =>
                setCreateModal({
                  barcode: code,
                  name: '',
                  price: '',
                  isWeight: false,
                  requiresMarking: false
                })
            }
          : undefined
      })
      return { found: false, barcode: code, canCreate: allowed }
    },
    [
      catalog,
      addProductToCart,
      addProductWithMarking,
      cartQtyFor,
      showToast,
      canCreateOnPos,
      canSellInactiveCategory
    ]
  )

  // Подписка на аппаратный сканер через шину.
  useEffect(() => {
    const off = core.bus.on(PosEvent.BARCODE_SCANNED, ({ raw }) => {
      // 1. Открыта форма вскрытия тары — код DataMatrix головы идёт туда.
      if (containerModalOpenRef.current) {
        containerDatamatrixRef.current(raw)
        return
      }
      // 2. Модалка заведения товара «на лету» — скан в поле «Штрихкод» (F6).
      if (createModalOpenRef.current) {
        createModalBarcodeRef.current(raw)
        return
      }
      // 3. Модалка марок (Честный Знак) — блокирующий UI: код идёт в неё.
      //    Код-марка НЕ должен утекать в handleBarcode («товар не найден»).
      if (markModalRef.current && (core.marking?.looksLike(raw) ?? false)) {
        routeMarkRef.current(raw)
        return
      }
      // 4. Scan-time marking: очередь ожидания марок (F4/F5). Очередь хранит
      // lineId строк чека — по нему и адресуем марку (дубликаты ШК не мешают).
      if (scanMarkQueueRef.current.length > 0) {
        if (core.marking?.looksLike(raw)) {
          const lineId = takeMarkTarget()
          if (lineId) {
            attachMarkToLine(lineId, raw)
            beepMark()
          }
          // Цель не нашлась (всё уже промаркировано) — код марки молча
          // игнорируем, а не отправляем в поиск товара.
          return
        }
        // Не похоже на DataMatrix — кассир отсканировал товар. Очередь
        // марок СОХРАНЯЕМ, код обрабатываем как обычный штрихкод.
        void handleBarcode(raw)
        return
      }
      void handleBarcode(raw)
    })
    return off
  }, [core.bus, handleBarcode, attachMarkToLine, takeMarkTarget])

  // Загрузка категорий активной версии.
  useEffect(() => {
    reloadCategories()
  }, [reloadCategories])

  // Частота: при старте и после каждой продажи (счётчик смены), но не на
  // каждом добавлении в корзину — порядок плиток не прыгает под пальцем.
  useEffect(() => {
    reloadFrequency()
  }, [reloadFrequency, shift?.salesCount])

  // Подкатегории зависят от выбранной категории.
  useEffect(() => {
    setSelectedSubcategory('all')
    reloadSubcategories(selectedCategory)
  }, [selectedCategory, reloadSubcategories])

  // Пересчёт сетки каталога при изменении поиска/категории/подкатегории.
  useEffect(() => {
    reloadGrid(manualCode, selectedCategory, selectedSubcategory)
  }, [manualCode, selectedCategory, selectedSubcategory, reloadGrid])

  // Загрузка валюты магазина.
  useEffect(() => {
    void core.catalog
      .getStoreCurrency()
      .then(setCurrency)
      .catch(() => setCurrency('RUB'))
  }, [core.catalog])

  // Блок «Часто покупают» — по настройке «Политики кассы» (по умолчанию скрыт).
  useEffect(() => {
    void core.catalog
      .isFrequentProductsEnabled()
      .then(setFrequentEnabled)
      .catch(() => setFrequentEnabled(false))
  }, [core.catalog])

  // Собственный device_id (для определения кассы-открывателя смены).
  useEffect(() => {
    void syncService
      .ensureDeviceId()
      .then(setMyDeviceId)
      .catch(() => setMyDeviceId(''))
  }, [])

  /**
   * Применить выбор «фискализировать наличные» для текущей смены.
   * Предпочтение хранится локально по ключу (зона + номер смены): перезагрузка
   * кассы его сохраняет, новая смена — сброс к настройке смены
   * (fiscalizationEnabled).
   */
  const applyFiscalPref = useCallback(async (s: PosShift): Promise<void> => {
    const key = fiscalPrefKey(s.zoneId ?? 'legacy', s.shiftNumber)
    const saved = await db.meta.get(key)
    setFiscalizeThisSale(
      typeof saved?.value === 'boolean' ? saved.value : s.fiscalizationEnabled
    )
  }, [])

  // Загрузка состояния текущей смены (с проверкой таймаута 24 ч).
  useEffect(() => {
    void (async () => {
      try {
        // «Забытая» смена старше 24 ч закрывается автоматически.
        await core.shift.ensureShiftFreshness()
      } catch {
        /* проверка свежести не должна блокировать кассу */
      }
      try {
        const s = await core.shift.getActiveShift()
        setShift(s)
        if (s) await applyFiscalPref(s)
        // После логина/старта обязательно сверяем локальную смену с ККТ.
        void core.fiscal.refreshKktStatus().catch(() => undefined)
      } catch {
        setShift(null)
      } finally {
        setShiftLoaded(true)
      }
    })()
  }, [core.shift, core.fiscal, applyFiscalPref])

  // Режим банковского терминала (manual/integrated) из настроек.
  useEffect(() => {
    void db.meta
      .get(META_CARD_TERMINAL_MODE)
      .then((e) => setCardMode(e?.value === 'integrated' ? 'integrated' : 'manual'))
      .catch(() => setCardMode('manual'))
  }, [])

  // Реактивное обновление категорий/подкатегорий/сетки при обновлении каталога.
  useEffect(() => {
    const off = core.bus.on(PosEvent.CATALOG_UPDATED, () => {
      reloadCategories()
      reloadSubcategories(selectedCategory)
      reloadGrid(manualCode, selectedCategory, selectedSubcategory)
    })
    return off
  }, [
    core.bus,
    reloadCategories,
    reloadSubcategories,
    reloadGrid,
    manualCode,
    selectedCategory,
    selectedSubcategory
  ])

  // Пул вскрытых тар обновился из облака (другое устройство той же зоны):
  // перечитываем список и бейдж активных тар без перезагрузки.
  useEffect(() => {
    const off = core.bus.on(PosEvent.CONTAINERS_UPDATED, () => {
      void reloadOpenedContainers()
    })
    return off
  }, [core.bus, reloadOpenedContainers])

  // Смена зоны изменилась в облаке (присоединение к смене другого устройства
  // или её закрытие) — перечитываем активную смену, чтобы UI был актуален.
  useEffect(() => {
    const off = core.bus.on(PosEvent.SHIFTS_UPDATED, () => {
      void core.shift
        .getActiveShift()
        .then((s) => {
          setShift(s)
          if (s) void applyFiscalPref(s)
        })
        .catch(() => undefined)
    })
    return off
  }, [core.bus, core.shift, applyFiscalPref])

  /** Пересчитать счётчик чеков, отложенных к дофискализации (offline_pending). */
  const reloadPendingFiscal = useCallback(async (): Promise<void> => {
    const items = await db.offlineQueue.where('type').equals('fiscalize').toArray()
    setPendingFiscalCount(items.filter((i) => i.status === 'pending').length)
  }, [])

  useEffect(() => {
    void reloadPendingFiscal()
    // Периодически обновляем счётчик — воркер разгребает очередь в фоне.
    const timer = window.setInterval(() => void reloadPendingFiscal(), 30000)
    return () => clearInterval(timer)
  }, [reloadPendingFiscal])

  /** B3/65.14: перечитать задачи замены non_fiscal → fiscal. */
  const reloadReplacementTasks = useCallback(async (): Promise<void> => {
    try {
      const [replacement, held] = await Promise.all([
        core.sale.listReplacementTasks(),
        core.sale.listHeldReceipts()
      ])
      setReplacementTasks(replacement)
      setHeldTasks(held)
    } catch {
      /* локальная БД может быть занята — не блокируем кассу */
    }
  }, [core.sale])

  useEffect(() => {
    void reloadReplacementTasks()
    const timer = window.setInterval(() => void reloadReplacementTasks(), 15000)
    const off = core.bus.on(PosEvent.MARKING_STATUS_UPDATED, () => {
      void reloadReplacementTasks()
    })
    return () => {
      clearInterval(timer)
      off()
    }
  }, [reloadReplacementTasks, core.bus])

  // Режим сети модуля маркировки — через generic-порт, без знания meta-ключа.
  useEffect(() => {
    setOnline(core.marking?.isOnline?.() ?? true)
  }, [core.marking])

  // 65.12.4: статусы кодов из фоновой проверки (скан → модуль → событие шины).
  // Ref нужен оплате: ждём терминальный статус, не полагаясь на stale state.
  const markStatusRef = useRef(markStatus)
  useEffect(() => {
    markStatusRef.current = markStatus
  }, [markStatus])
  useEffect(() => {
    return core.bus.on(PosEvent.MARKING_STATUS_UPDATED, ({ code, status, reason }) => {
      setMarkStatus((prev) => ({ ...prev, [code]: { status, reason } }))
    })
  }, [core.bus])

  // Регистрируем UI-резолвер ввода марок для плагина Честного Знака.
  useEffect(() => {
    core.marking?.setMarkResolver(
      (request) =>
        new Promise<Record<string, string> | null>((resolve) => {
          // Модалка марок сама соберёт ВСЕ недостающие марки (плагин проставит
          // item.datamatrix) — очередь ожидания сканера сбрасываем (F4).
          scanMarkQueueRef.current = []
          syncPendingMarks()
          setMarkModal({ request, codes: {}, resolve })
        })
    )
    return () => core.marking?.setMarkResolver(null)
  }, [core.marking, syncPendingMarks])

  // Транслируем режим сети в плагин ЧЗ (демо онлайн/оффлайн).
  useEffect(() => {
    core.marking?.setOnline(online)
  }, [core.marking, online])

  // UI-резолвер оплаты: его вызывает провайдер из реестра ядра (65.12.6).
  useEffect(() => {
    core.payments.setResolver(({ request, qr }) =>
      new Promise<PaymentOutcome>((resolve) => {
        // Векторный SVG: минимум нагрузки на старые планшеты (Android 7)
        // и чёткость при любом разрешении. Ошибка генерации — фолбэк на ссылку.
        void QRCode.toString(qr.payload, {
          type: 'svg',
          errorCorrectionLevel: 'M',
          margin: 1
        })
          .then((qrSvg) => {
            setSbpModal({ request, qr, qrSvg, phase: 'waiting', resolve })
          })
          .catch((err: unknown) => {
            console.error('[payments] не удалось сгенерировать QR:', err)
            setSbpModal({ request, qr, qrSvg: '', phase: 'waiting', resolve })
          })
      })
    )
    return () => core.payments.setResolver(null)
  }, [core.payments])

  // Имитация WebSocket NewPay: пока модалка «ждёт», через 3с приходит статус
  // «оплачено» — показываем успех и завершаем промис резолвера.
  useEffect(() => {
    if (!sbpModal || sbpModal.phase !== 'waiting') {
      return
    }
    const t = setTimeout(() => {
      sbpModal.resolve({ status: 'succeeded' })
      setSbpModal((m) => (m ? { ...m, phase: 'succeeded' } : m))
      // Небольшая пауза, чтобы кассир увидел «Оплачено», затем закрываем.
      setTimeout(() => setSbpModal(null), 1200)
    }, 3000)
    return () => clearTimeout(t)
  }, [sbpModal])

  /** Отмена оплаты по СБП кассиром. */
  const cancelSbp = () => {
    if (!sbpModal) {
      return
    }
    sbpModal.resolve({ status: 'cancelled' })
    setSbpModal(null)
  }

  const submitManual = (e: React.FormEvent) => {
    e.preventDefault()
    const code = manualCode
    setManualCode('')
    void handleBarcode(code)
  }

  const changeQty = (index: number, delta: number) => {
    setCart((prev) => {
      const next = [...prev]
      const line = next[index]
      if (!line) return prev
      // Маркированная строка — одна единица = одна марка: увеличение запрещено
      // (UI скрывает «+», это защита от вызовов в обход).
      if (line.datamatrix && delta > 0) return prev
      const qty = Number((line.qty + delta).toFixed(3))
      // Возврат: вернуть больше проданного нельзя (maxQty из чека-источника).
      if (line.maxQty != null && qty > line.maxQty) return prev
      if (qty <= 0) {
        next.splice(index, 1)
      } else {
        next[index] = { ...line, qty }
      }
      return next
    })
  }

  const removeLine = (index: number) => {
    setCart((prev) => prev.filter((_, i) => i !== index))
  }

  /** Отменить текущий чек: корзина, очередь марок и модалка веса очищаются. */
  const cancelCart = () => {
    if (cart.length === 0 || paying) return
    if (!window.confirm('Отменить текущий чек? Все позиции будут удалены.')) return
    setCart([])
    scanMarkQueueRef.current = []
    syncPendingMarks()
    setWeightModal(null)
    setWeightInput('')
    setCartSheetOpen(false)
    // В режиме возврата забываем и чек-источник: иначе следующая корзина
    // проверялась бы по чужому чеку (ревью 65.5).
    if (returnModeRef.current) setReturnSource(null)
  }

  const total = cart.reduce((sum, l) => sum + l.price * l.qty, 0)
  const qtyTotal = cart.reduce((sum, l) => sum + l.qty, 0)
  // 65.10: в чеке есть позиция без кода — только наличные и без ККТ.
  const hasMarkingSkip = cart.some((l) => l.markingSkipped || l.markingInvalid)
  // 65.10: возврат по нефискальной продаже — только наличные.
  const returnNonFiscal = returnMode && returnSource?.status === 'non_fiscal'
  const cashOnly = hasMarkingSkip || returnNonFiscal

  /** Сумма по выбранному составу коррекции (для сверки с ручной суммой). */
  const correctionComposition = correctionModal
    ? correctionModal.items
        .filter((line) => line.selected)
        .reduce((sum, line) => {
          const qty = Number(line.qty.replace(',', '.'))
          return Number.isFinite(qty) && qty > 0 ? sum + line.price * qty : sum
        }, 0)
    : 0

  const pay = async (method: PaymentMethod = 'cash') => {
    if (cart.length === 0) {
      showToast({ kind: 'error', text: 'Чек пуст' })
      return
    }
    // A4: страховка — на десктопе не уходим в оплату, пока ждём коды марок
    // (блокирующий диалог «Требуется маркировка» открыт).
    if (!isMobile && scanMarkQueueRef.current.length > 0) {
      showToast({ kind: 'error', text: 'Считайте код маркировки для позиций чека' })
      return
    }
    // Native KKT на Android: не доводим маркировку до оплаты без preflight.
    const markingReadiness = core.fiscal.getMarkingReadiness()
    if (isNativeKkt() && cart.some((l) => l.requiresMarking) && !markingReadiness.ok) {
      showToast({
        kind: 'error',
        text: `Маркированный чек заблокирован: ${markingReadiness.reason ?? 'ККТ не готова'}`
      })
      return
    }
    setPaying(true)
    try {
      // Направление чека фиксируем ОДИН раз до await (65.14: нужно и для гейта).
      const isReturn = returnModeRef.current
      // 65.14: перед оплатой ждём терминальный статус кодов (до 5 с).
      // Если не успели — чек уйдёт в hold (`unknown`), а не будет продан «молча».
      const markedCodes = cart
        .filter((l) => l.requiresMarking && l.datamatrix)
        .map((l) => (l.datamatrix ?? '').trim())
      if (!isReturn && markedCodes.length > 0) {
        const deadline = Date.now() + 5000
        while (
          Date.now() < deadline &&
          markedCodes.some((code) => !markStatusRef.current[code]?.status)
        ) {
          await new Promise((resolve) => setTimeout(resolve, 150))
        }
      }
      // Фискальное НАМЕРЕНИЕ кассира (галочка) — НЕ зависит от доступности ККТ:
      // при недоступной ККТ чек уйдёт в очередь дофискализации.
      // Карта и СБП фискализируются ВСЕГДА; галочка управляет только наличными.
      const paymentMethod: PaymentMethod = method
      // 65.10: позиция без кода маркировки — только non_fiscal + наличные.
      const effectiveFiscalIntent =
        hasMarkingSkip || returnNonFiscal
          ? false
          : method === 'cash' && canSkipFiscalization
            ? fiscalizeThisSale
            : true
      const result = await core.sale.checkout(
        {
          items: cart.map((l) => ({
            lineId: l.lineId,
            originalLineId: isReturn ? l.originalLineId : undefined,
            barcode: l.barcode,
            name: l.name,
            qty: l.qty,
            price: l.price,
            unit: l.unit,
            datamatrix: l.datamatrix,
            requiresMarking: l.requiresMarking,
            markingSkipped: l.markingSkipped,
            markingInvalid: l.markingInvalid,
            markingReason: l.markingReason,
            // 65.14: гейт оплаты использует статус проверки ЧЗ.
            czStatus: l.datamatrix
              ? markStatusRef.current[l.datamatrix.trim()]?.status
              : undefined
          })),
          paymentMethod,
          // ARCH-TASK-65: возврат проходит тем же документом, но с отрицательной
          // суммой и признаком расчёта 'income_return'.
          settlementType: isReturn ? 'income_return' : 'income',
          // ARCH-TASK-65.5: связь с исходным чеком — лимит возврата и аудит.
          originalReceiptId: isReturn ? returnSource?.receiptId : undefined,
          // «Возврат + списание»: документ Writeoff + два движения ledger.
          writeoff:
            isReturn && returnWriteoff
              ? { reasonCode: writeoffReason, comment: writeoffComment.trim() || undefined }
              : undefined
        },
        {
          fiscalIntent: effectiveFiscalIntent,
          cashierName,
          canSkipMarking,
          canWriteoff,
          canSkipFiscalization
        }
      )
      if (result.ok) {
        // Учёт в текущей смене: продажа/возврат считаются раздельно.
        if (isReturn) {
          await core.shift.recordReturn(result.receipt.total, paymentMethod)
        } else if (result.receipt.status === 'marking_review') {
          // 65.14: деньги в ящике, но выручкой станет после решения.
          await core.shift.recordHold(result.receipt.total, paymentMethod)
        } else {
          await core.shift.recordSale(result.receipt.total, paymentMethod)
        }
        setShift(await core.shift.getActiveShift())
        const fd = result.receipt.fiscalData
        // Сумма возврата хранится отрицательной — на экране успеха показываем
        // модуль (направление передаёт флаг isReturn).
        const receiptTotal = Math.abs(result.receipt.total)
        const wasReturn = isReturn
        const given = Number(cashReceived.replace(',', '.')) || 0
        // Сдача остаётся в области итога после автозакрытия модалки.
        if (paymentMethod === 'cash' && !wasReturn) {
          setLastChange({
            change: Number(Math.max(0, given - receiptTotal).toFixed(2)),
            cashGiven: given,
            total: receiptTotal
          })
        } else {
          setLastChange(null)
        }
        // Экран успеха/сдачи (наличные показывают сдачу крупно).
        setSuccessScreen({
          receiptId: result.receipt.receiptId,
          total: receiptTotal,
          method: paymentMethod,
          isReturn: wasReturn,
          cashGiven: paymentMethod === 'cash' && !wasReturn ? given : undefined,
          change:
            paymentMethod === 'cash' && !wasReturn
              ? Number(Math.max(0, given - receiptTotal).toFixed(2))
              : undefined,
          // Фискальный статус чека (fiscalized / offline_pending / non_fiscal).
          status: result.receipt.status,
          warning: result.warning,
          offlineReceiptNumber: result.receipt.offlineReceiptNumber,
          fiscalData: fd
            ? {
                fiscalDocumentNumber: fd.fiscalDocumentNumber,
                fiscalDocumentSign: fd.fiscalDocumentSign
              }
            : undefined
        })
        setCart([])
        setCartSheetOpen(false)
        setPayModal(null)
        setCashReceived('')
        // Завершённый возврат закрывает режим возврата (без подтверждения:
        // корзина уже очищена, спрашивать не о чем).
        if (wasReturn) exitReturnMode(true)
        // Чек мог уйти в очередь дофискализации — обновляем счётчик.
        void reloadPendingFiscal()
        if (result.warning) {
          showToast({ kind: 'info', text: result.warning })
        }
      } else {
        // 65.10: структурированные проблемы маркировки — модалка с выбором
        // «Убрать позицию» / «Пропустить без кода» (по праву).
        if (result.markingIssues && result.markingIssues.length > 0) {
          setMarkIssuesModal({
            issues: result.markingIssues,
            reasonCode: 'unreadable',
            comment: ''
          })
          return
        }
        showToast({
          kind: 'error',
          text:
            result.stage === 'validation'
              ? `Возврат не оформлен: ${result.reason}`
              : result.stage === 'pre_sale'
                ? `Продажа заблокирована: ${result.reason}`
                : `Оплата не прошла: ${result.reason}`
        })
      }
    } catch (err: unknown) {
      showToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) })
    } finally {
      setPaying(false)
    }
  }

  // Гибридный ввод: физическая клавиатура при открытом экране наличных.
  useEffect(() => {
    if (payModal?.stage !== 'cash') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        setCashReceived((prev) => (prev === '0' || prev === '' ? e.key : prev + e.key))
      } else if (e.key === 'Backspace') {
        setCashReceived((prev) => prev.slice(0, -1) || '0')
      } else if (e.key === 'Escape') {
        setPayModal({ stage: 'choose' })
      } else if (e.key === 'Enter') {
        const received = Number(cashReceived.replace(',', '.')) || 0
        if (received >= total && !paying) {
          void pay('cash')
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payModal?.stage, cashReceived, total, paying])

  // Экран сдачи: авто-закрытие через 4с и по Enter («Следующий покупатель»).
  useEffect(() => {
    if (!successScreen) return
    const timer = setTimeout(() => setSuccessScreen(null), 4000)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') setSuccessScreen(null)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('keydown', onKey)
    }
  }, [successScreen])

  // Гибридный ввод веса: физическая клавиатура при открытой модалке веса.
  useEffect(() => {
    if (!weightModal) return
    const onKey = (e: KeyboardEvent) => {
      // Живая панель весов: numpad скрыт, стабильный вес применяется сам.
      // Оставляем только Escape (закрыть) и выход в ручной ввод.
      if (liveScaleMode) {
        if (e.key === 'Escape') {
          setWeightModal(null)
          setWeightInput('')
          setWeightManual(false)
        }
        return
      }
      if ((e.key >= '0' && e.key <= '9') || e.key === '.' || e.key === ',') {
        weightPress(e.key === ',' ? '.' : e.key)
      } else if (e.key === 'Backspace') {
        weightBackspace()
      } else if (e.key === 'Escape') {
        setWeightModal(null)
        setWeightInput('')
      } else if (e.key === 'Enter') {
        confirmWeight()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weightModal, weightInput, liveScaleMode])

  /** Сохранить товар «на лету» и сразу добавить его в чек. */
  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!createModal) {
      return
    }
    const price = Number(createModal.price.replace(',', '.'))
    if (!createModal.name.trim()) {
      showToast({ kind: 'error', text: 'Укажите наименование' })
      return
    }
    if (!Number.isFinite(price) || price < 0) {
      showToast({ kind: 'error', text: 'Укажите корректную цену' })
      return
    }
    setSaving(true)
    try {
      const product = await catalog.createOnPos({
        name: createModal.name,
        price,
        barcode: createModal.barcode || undefined,
        isWeight: createModal.isWeight,
        requiresMarking: createModal.requiresMarking,
        createdByName: cashier?.name ?? cashierName
      })
      addProductToCart(product)
      reloadCategories()
      reloadSubcategories(selectedCategory)
      reloadGrid(manualCode, selectedCategory, selectedSubcategory)
      setCreateModal(null)
      showToast({ kind: 'info', text: `Добавлен на лету: ${product.name}` })
    } catch (err: unknown) {
      showToast({
        kind: 'error',
        text: err instanceof Error ? err.message : String(err)
      })
    } finally {
      setSaving(false)
    }
  }

  /** Ключ позиции в модалке марок: lineId (дубли товара) или имя. */
  const markKey = (it: MarkRequest['items'][number]): string => it.lineId ?? it.name

  /** Записать код марки в поле позиции. */
  const updateMarkCode = (key: string, code: string) => {
    setMarkModal((m) => (m ? { ...m, codes: { ...m.codes, [key]: code } } : m))
    // 65.12.4: проверяем код сразу при вводе/скане — статус придёт событием.
    const trimmed = code.trim()
    if (trimmed && core.marking?.looksLike(trimmed)) core.marking.verify([trimmed])
  }

  /** Открыть камеру для сканирования DataMatrix конкретной позиции. */
  const openCameraForMark = (key: string) => {
    setCameraMarkTarget(key)
    setCameraOpen(true)
  }

  /** Подтвердить ввод марок — вернуть коды резолверу плагина. */
  const confirmMarks = (e?: React.FormEvent) => {
    e?.preventDefault()
    const current = markModalRef.current
    if (!current) {
      return
    }
    const missing = current.request.items.find((it) => !current.codes[markKey(it)]?.trim())
    if (missing) {
      showToast({ kind: 'error', text: `Отсканируйте марку для «${missing.name}»` })
      return
    }
    // 65.12.1: структура кода проверяется локально, до обращения к ККТ/сети.
    for (const it of current.request.items) {
      const code = current.codes[markKey(it)]?.trim()
      if (!code) continue
      const inspection = core.marking?.inspect(code)
      if (inspection && !inspection.acceptable) {
        showToast({
          kind: 'error',
          text: `«${it.name}»: ${inspection.reason ?? 'код недействителен'}`
        })
        return
      }
    }
    current.resolve(current.codes)
    setMarkModal(null)
  }

  /**
   * Записать отсканированный код DataMatrix в первую незаполненную позицию
   * марочной модалки. Если после записи все позиции заполнены — подтверждаем
   * автоматически. Используется и аппаратным 2D-сканером, и камерой.
   * @returns true, если код удалось направить в марочную модалку.
   */
  const routeCodeToMarkModal = (code: string, targetName?: string): boolean => {
    const current = markModalRef.current
    if (!current) return false
    const items = current.request.items
    // Явная цель (камера для конкретного поля) или первая незаполненная.
    const target =
      (targetName && items.find((it) => markKey(it) === targetName)) ||
      items.find((it) => !current.codes[markKey(it)]?.trim())
    if (!target) return false

    const nextCodes = { ...current.codes, [markKey(target)]: code }
    setMarkModal({ ...current, codes: nextCodes })
    markModalRef.current = { ...current, codes: nextCodes }
    beepMark()

    // Все позиции заполнены — подтверждаем автоматически.
    const allFilled = items.every((it) => nextCodes[markKey(it)]?.trim())
    if (allFilled) {
      confirmMarks()
    }
    return true
  }
  // Держим актуальную реализацию для стабильной подписки на 2D-сканер.
  routeMarkRef.current = routeCodeToMarkModal

  /** Отмена ввода марок — продажа будет заблокирована плагином. */
  const cancelMarks = () => {
    if (!markModal) {
      return
    }
    markModal.resolve(null)
    setMarkModal(null)
  }

  /** 65.10: убрать из корзины строки с проблемной маркировкой. */
  const removeMarkingIssueLines = () => {
    const modal = markIssuesModal
    if (!modal) return
    const issueLineIds = new Set(modal.issues.map((i) => i.lineId).filter(Boolean))
    setCart((prev) =>
      prev.filter(
        (l) =>
          !modal.issues.some(
            (i) => (i.lineId && i.lineId === l.lineId) || (!i.lineId && i.name === l.name)
          )
      )
    )
    scanMarkQueueRef.current = scanMarkQueueRef.current.filter(
      (id) => !issueLineIds.has(id)
    )
    syncPendingMarks()
    setMarkIssuesModal(null)
  }

  /** 65.10: осознанно продать проблемные строки без кода (non_fiscal + наличные). */
  const skipMarkingIssueLines = () => {
    const modal = markIssuesModal
    if (!modal) return
    if (!canSkipMarking) {
      showToast({ kind: 'error', text: 'Нет права продавать маркированный товар без кода' })
      return
    }
    const reasonLabel =
      modal.reasonCode === 'unreadable'
        ? 'код не читается'
        : modal.reasonCode === 'not_found'
          ? 'код не найден в ЧЗ'
          : modal.reasonCode === 'supplier'
            ? 'ошибка поставщика'
            : 'другое'
    const reason = `${reasonLabel}${modal.comment.trim() ? `: ${modal.comment.trim()}` : ''}`
    const issueLineIds = new Set(modal.issues.map((i) => i.lineId).filter(Boolean))
    setCart((prev) =>
      prev.map((l) => {
        const issue = modal.issues.find(
          (i) => (i.lineId && i.lineId === l.lineId) || (!i.lineId && i.name === l.name)
        )
        if (!issue) return l
        return {
          ...l,
          // Код не уходит в ККТ/ЧЗ: чек будет non_fiscal.
          datamatrix: undefined,
          markingSkipped: true,
          markingInvalid: issue.kind === 'invalid',
          markingReason: reason,
          requiresMarking: true
        }
      })
    )
    // Строка больше не ждёт сканирования: убираем из scan-time очереди.
    scanMarkQueueRef.current = scanMarkQueueRef.current.filter(
      (id) => !issueLineIds.has(id)
    )
    syncPendingMarks()
    setMarkIssuesModal(null)
    showToast({
      kind: 'info',
      text: 'Чек будет без ККТ. Оплата — только наличными.'
    })
  }

  /** Нажатие цифры на экранном numpad. */
  const numpadPress = (val: string) => {
    setCashReceived((prev) => {
      if (prev === '0' || prev === '') return val === '00' ? '0' : val
      return prev + val
    })
  }
  const numpadBackspace = () => setCashReceived((prev) => prev.slice(0, -1) || '0')
  const numpadClear = () => setCashReceived('0')
  const addDenomination = (n: number) =>
    setCashReceived((prev) => String((Number(prev.replace(',', '.')) || 0) + n))

  /** Нажатия для ввода веса (кг, до 3 знаков). */
  const weightPress = (val: string) => {
    setWeightInput((prev) => {
      if (val === '.') {
        return prev.includes('.') ? prev : prev === '' ? '0.' : prev + '.'
      }
      // не больше 3 знаков после точки
      if (prev.includes('.') && prev.split('.')[1].length >= 3) return prev
      return prev + val
    })
  }
  const weightBackspace = () => setWeightInput((prev) => prev.slice(0, -1))
  const weightAdd = (kg: number) =>
    setWeightInput((prev) => {
      const next = (Number(prev.replace(',', '.')) || 0) + kg
      return next.toFixed(3)
    })

  /**
   * Подтвердить вес и добавить весовой товар в чек.
   * kgOverride — вес с весов (живая панель): weightInput в этом режиме пуст.
   */
  const confirmWeight = (kgOverride?: number) => {
    if (!weightModal) return
    const kg = kgOverride ?? Number(weightInput.replace(',', '.'))
    if (!Number.isFinite(kg) || kg <= 0) {
      showToast({ kind: 'error', text: 'Укажите вес больше нуля' })
      return
    }
    const p = weightModal.product
    void addProductWithMarking(p, Number(kg.toFixed(3)))
    setWeightModal(null)
    setWeightInput('')
    setWeightManual(false)
    showToast({ kind: 'info', text: `Добавлено: ${p.name} (${formatKg(kg)} кг)` })
  }

  /**
   * Авто-применение веса с весов (десктоп): стабильный вес > 0 принимается
   * через 250 мс дебаунса. Защита от дубля: пока груз не снят (вес не упал
   * ниже 5 г) и не изменился более чем на 20 г, повторно не принимаем —
   * кассиру показывается подсказка «Снимите товар с весов».
   */
  useEffect(() => {
    const cancelPending = () => {
      if (autoConfirmRef.current) {
        clearTimeout(autoConfirmRef.current.timer)
        autoConfirmRef.current = null
      }
    }

    // Модалка закрыта / ручной режим — отложенный авто-приём отменяем.
    if (!weightModal || !liveScaleMode) {
      cancelPending()
      return
    }
    const reading = scaleReading
    if (!reading || !reading.stable || reading.kg <= 0) return

    const last = lastAutoKgRef.current
    const stillLoaded =
      last != null && !(reading.kg < 0.005 || Math.abs(reading.kg - last) > 0.02)
    if (stillLoaded) return

    // Тот же вес уже ждёт дебаунс — НЕ перезапускаем отсчёт. Иначе троттлинг
    // агента (200 мс) короче дебаунса (250 мс) и при дрожании веса авто-приём
    // не сработал бы никогда.
    const pending = autoConfirmRef.current
    if (pending && Math.abs(pending.kg - reading.kg) < 0.005) return
    cancelPending()

    const timer = setTimeout(() => {
      autoConfirmRef.current = null
      lastAutoKgRef.current = reading.kg
      confirmWeight(reading.kg)
    }, 250)
    autoConfirmRef.current = { kg: reading.kg, timer }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weightModal, liveScaleMode, scaleReading])

  // Снять отложенный авто-приём при размонтировании.
  useEffect(
    () => () => {
      if (autoConfirmRef.current) clearTimeout(autoConfirmRef.current.timer)
    },
    []
  )

  /**
   * Применить код DataMatrix головки и распознать товар по GTIN (smart-lookup).
   * Код марки «Честный Знак» несёт GTIN — сопоставляем его с каталогом активной
   * версии и автоподставляем товар, чтобы кассиру не искать его вручную.
   */
  const applyContainerDatamatrix = useCallback(
    async (raw: string): Promise<void> => {
      const code = raw.trim()
      setContainerModal((m) => (m ? { ...m, datamatrix: code } : m))
      const gtin = core.marking?.gtinOf(code) ?? null
      if (!gtin) return
      const found = await catalog.findByBarcode(gtin)
      if (found) {
        setContainerModal((m) => (m ? { ...m, product: found, search: found.name } : m))
        showToast({ kind: 'info', text: `Распознан товар: ${found.name}` })
      }
    },
    [catalog, showToast]
  )
  containerDatamatrixRef.current = (code: string) => void applyContainerDatamatrix(code)

  /** Открыть модалку тар (стартуем со вкладки «Вскрыть новую тару»). */
  const openContainerModal = () => {
    setContainerModal({ tab: 'new', product: null, search: '', datamatrix: '', weight: '' })
    setContainerCandidates([])
    setContainerNumpadOpen(false)
    setContainerEdit(null)
    void reloadOpenedContainers()
  }

  /** Нажатие на экранном numpad веса головки (цифры и точка). */
  const containerWeightPress = (val: string) => {
    setContainerModal((m) => {
      if (!m) return m
      if (val === '.') {
        if (m.weight.includes('.')) return m
        return { ...m, weight: m.weight === '' ? '0.' : `${m.weight}.` }
      }
      // Не больше 3 знаков после точки.
      if (m.weight.includes('.') && m.weight.split('.')[1].length >= 3) return m
      return { ...m, weight: m.weight === '0' ? val : m.weight + val }
    })
  }
  const containerWeightBackspace = () =>
    setContainerModal((m) => (m ? { ...m, weight: m.weight.slice(0, -1) } : m))
  const containerWeightClear = () => setContainerModal((m) => (m ? { ...m, weight: '' } : m))

  /** Подтвердить вскрытие тары — сохранить активную запись в пул массы. */
  const confirmContainerOpen = async () => {
    const m = containerModal
    if (!m) return
    if (!m.product) {
      showToast({ kind: 'error', text: 'Выберите весовой товар' })
      return
    }
    if (!m.datamatrix.trim()) {
      showToast({ kind: 'error', text: 'Отсканируйте код DataMatrix головы' })
      return
    }
    const kg = Number(m.weight.replace(',', '.'))
    if (!Number.isFinite(kg) || kg <= 0) {
      showToast({ kind: 'error', text: 'Укажите начальный вес больше нуля' })
      return
    }
    await db.opened_containers.add({
      barcode: m.product.barcode,
      name: m.product.name,
      datamatrix: m.datamatrix.trim(),
      initialWeight: Number(kg.toFixed(3)),
      remainingWeight: Number(kg.toFixed(3)),
      status: 'active',
      openedAt: Date.now()
    })
    await reloadOpenedContainers()
    showToast({
      kind: 'info',
      text: `Вскрыта тара «${m.product.name}»: ${formatKg(kg)} кг. Нарезка не потребует повторного сканирования марки.`
    })
    setContainerModal(null)
    setContainerNumpadOpen(false)
  }

  /** Принудительно закрыть тару (изъять из оборота). */
  const closeContainer = async (id: number) => {
    await db.opened_containers.update(id, { remainingWeight: 0, status: 'exhausted' })
    await reloadOpenedContainers()
    showToast({ kind: 'info', text: 'Тара закрыта (изъята из оборота)' })
  }

  /** Сохранить ручную корректировку остатка тары («<Pencil size={16} /> Дописать вес»). */
  const saveContainerEdit = async () => {
    if (!containerEdit) return
    const kg = Number(containerEdit.value.replace(',', '.'))
    if (!Number.isFinite(kg) || kg < 0) {
      showToast({ kind: 'error', text: 'Укажите остаток ≥ 0' })
      return
    }
    await db.opened_containers.update(containerEdit.id, {
      remainingWeight: Number(kg.toFixed(3)),
      status: kg > 0 ? 'active' : 'exhausted'
    })
    setContainerEdit(null)
    await reloadOpenedContainers()
  }

  // Гибридный ввод: физическая клавиатура/сканер при открытой модалке тары.
  useEffect(() => {
    if (!containerModal) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setContainerModal(null)
        setContainerNumpadOpen(false)
        return
      }
      // Не перехватываем ввод, когда фокус в текстовом поле (поиск/DataMatrix),
      // кроме read-only поля веса — оно предназначено для ввода с numpad.
      const el = e.target as HTMLElement | null
      if (
        el &&
        (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') &&
        el.dataset.containerField !== 'weight'
      ) {
        return
      }
      if ((e.key >= '0' && e.key <= '9') || e.key === '.') {
        containerWeightPress(e.key)
      } else if (e.key === ',') {
        containerWeightPress('.')
      } else if (e.key === 'Backspace') {
        containerWeightBackspace()
      } else if (e.key === 'Enter') {
        void confirmContainerOpen()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerModal])

  /** Открыть способ оплаты картой по настроенному режиму терминала. */
  const chooseCard = () => {
    if (cardMode === 'integrated') {
      // Интегрированный: команда уйдёт на пин-пад (демо — сразу подтверждаем).
      setPayModal(null)
      void pay('card')
    } else {
      // Ручной: окно ожидания подтверждения с банковского терминала.
      setPayModal({ stage: 'card' })
    }
  }

  /** 65.14: ручной повтор проверки кодов после сетевого `unknown`. */
  const retryMarkingCheck = async (receipt: SaleReceipt) => {
    const codes = receipt.items
      .map((item) => item.datamatrix?.trim())
      .filter((code): code is string => Boolean(code))
    if (codes.length === 0 || !core.marking?.retryVerification) return
    core.marking.retryVerification(codes)
    showToast({ kind: 'info', text: 'Повторная проверка кодов запущена' })
    await reloadReplacementTasks()
  }

  /** B3/65.14: открыть модалку замены non_fiscal на фискальный. */
  const openReplacementModal = (receipt: SaleReceipt) => {
    setReplacementModal(receipt)
    setReplacementAmount(receipt.total.toFixed(2))
    setReplacementReason('')
  }

  /** B3/65.14: подтвердить возврат наличных и пробить фискальный чек. */
  const confirmReplacement = async () => {
    if (!replacementModal) return
    setReplacementBusy(true)
    try {
      const result = await core.sale.printReplacement(replacementModal.receiptId, {
        confirmedAmount: Number(replacementAmount.replace(',', '.')),
        cashierName
      })
      if (!result.ok) {
        showToast({ kind: 'error', text: result.reason })
        await reloadReplacementTasks()
        return
      }
      setReplacementModal(null)
      setSuccessScreen({
        receiptId: result.receipt.receiptId,
        total: result.receipt.total,
        method: 'cash',
        status: result.receipt.status,
        fiscalData: result.receipt.fiscalData
          ? {
              fiscalDocumentNumber: result.receipt.fiscalData.fiscalDocumentNumber,
              fiscalDocumentSign: result.receipt.fiscalData.fiscalDocumentSign
            }
          : undefined
      })
      showToast({ kind: 'info', text: 'Фискальный чек пробит, код выведен из оборота' })
      await reloadReplacementTasks()
    } catch (err: unknown) {
      showToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) })
    } finally {
      setReplacementBusy(false)
    }
  }

  /** B3/65.14: отменить замену — non_fiscal остаётся, код остаётся в обороте. */
  const cancelReplacement = async () => {
    if (!replacementModal) return
    setReplacementBusy(true)
    try {
      await core.sale.cancelReplacement(
        replacementModal.receiptId,
        replacementReason,
        cashierName
      )
      setReplacementModal(null)
      showToast({ kind: 'info', text: 'Замена отменена; чек без ККТ сохранён' })
      await reloadReplacementTasks()
    } catch (err: unknown) {
      showToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) })
    } finally {
      setReplacementBusy(false)
    }
  }

  /** 65.14: открыть модалку hold-чека. */
  const openHeldModal = (receipt: SaleReceipt) => {
    setHeldModal(receipt)
    setHeldAmount(receipt.total.toFixed(2))
    setHeldReason('')
  }

  /** 65.14: пробить hold-чек после valid (деньги уже в ящике). */
  const confirmHeldPrint = async () => {
    if (!heldModal) return
    setHeldBusy(true)
    try {
      const result = await core.sale.printHeldReceipt(heldModal.receiptId, {
        confirmedAmount: Number(heldAmount.replace(',', '.')),
        cashierName
      })
      if (!result.ok) {
        showToast({ kind: 'error', text: result.reason })
        await reloadReplacementTasks()
        return
      }
      const accounted = await core.shift.releaseHold(
        result.receipt.total,
        result.receipt.paymentMethod ?? 'cash',
        result.receipt.receiptId
      )
      setShift(await core.shift.getActiveShift())
      setHeldModal(null)
      if (accounted) {
        setSuccessScreen({
          receiptId: result.receipt.receiptId,
          total: result.receipt.total,
          method: 'cash',
          status: result.receipt.status,
          fiscalData: result.receipt.fiscalData
            ? {
                fiscalDocumentNumber: result.receipt.fiscalData.fiscalDocumentNumber,
                fiscalDocumentSign: result.receipt.fiscalData.fiscalDocumentSign
              }
            : undefined
        })
        const notice = holdPrintNotice(true)
        showToast(notice)
      } else {
        // Фискальный чек уже существует, но обычный success screen запрещён:
        // смена останется заблокированной до recovery.
        showToast(holdPrintNotice(false))
      }
      await reloadReplacementTasks()
    } catch (err: unknown) {
      showToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) })
    } finally {
      setHeldBusy(false)
    }
  }

  /** 65.14: отменить hold-чек с возвратом наличных и товара на склад. */
  const confirmHeldCancel = async () => {
    if (!heldModal) return
    setHeldBusy(true)
    try {
      const cancelled = await core.sale.cancelHeldReceipt(
        heldModal.receiptId,
        heldReason,
        cashierName
      )
      const accounted = await core.shift.cancelHold(
        cancelled.total,
        cancelled.paymentMethod ?? 'cash',
        cancelled.receiptId
      )
      setShift(await core.shift.getActiveShift())
      setHeldModal(null)
      if (accounted) {
        showToast(holdCancelNotice(true))
      } else {
        showToast(holdCancelNotice(false))
      }
      await reloadReplacementTasks()
    } catch (err: unknown) {
      showToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) })
    } finally {
      setHeldBusy(false)
    }
  }

  /**
   * Прочитать документы открытой смены из ККТ и обновить локальную сводку.
   *
   * Инкрементально: после первого чтения берём только номера больше
   * `shift.kktLastDocNumber`. Это read-only; фискальные документы не меняются.
   */
  const syncKktShiftDocuments = useCallback(
    async (
      currentShift: PosShift,
      status: {
        shiftOpen?: boolean
        shiftNumber?: number
        receiptNumber?: number
        lastDocNumber?: number
      }
    ): Promise<boolean> => {
      if (!status.shiftOpen || status.shiftNumber !== currentShift.shiftNumber) return true
      const last = status.lastDocNumber ?? 0
      if (last <= 0) return true
      const from = currentShift.kktLastDocNumber
        ? currentShift.kktLastDocNumber + 1
        : Math.max(1, last - Math.max(status.receiptNumber ?? 0, 0) - 1)
      if (from > last) return true

      const batchSize = 50
      const documents: NativeKktDocument[] = []
      let complete = true
      for (let start = from; start <= last; start += batchSize) {
        const end = Math.min(start + batchSize - 1, last)
        const result = await core.fiscal.readKktDocuments(start, end)
        if (!result.success || !result.documents) {
          const failed = await core.shift.markKktSyncFailed(
            currentShift.shiftNumber,
            result.error ?? 'kkt_documents_read_failed'
          )
          if (failed) setShift(failed)
          return false
        }
        documents.push(...result.documents)
        if (result.errors && result.errors.length > 0) complete = false
      }

      const updated = await core.shift.syncKktDocuments(
        currentShift.shiftNumber,
        documents,
        {
          incremental: Boolean(currentShift.kktLastDocNumber),
          complete
        }
      )
      if (updated) setShift(updated)
      return complete
    },
    [core]
  )

  const kktDocsSyncRef = useRef('')
  useEffect(() => {
    if (!shift || !kktStatus?.shiftOpen || kktStatus.shiftNumber !== shift.shiftNumber) {
      return
    }
    const key = `${shift.shiftNumber}:${kktStatus.lastDocNumber ?? 0}`
    if (kktDocsSyncRef.current === key) return
    void syncKktShiftDocuments(shift, kktStatus)
      .then(() => {
        // Ошибку не повторяем в цикле: новый lastDocNumber или ручная
        // кнопка «Повторить сверку» снимут этот ключ.
        kktDocsSyncRef.current = key
      })
      .catch((error) => {
        console.warn('[PosPage] KKT documents sync failed:', error)
      })
  }, [shift, kktStatus, syncKktShiftDocuments])

  const retryKktSync = async () => {
    if (!shift || !kktStatus) return
    kktDocsSyncRef.current = ''
    const ok = await syncKktShiftDocuments(shift, kktStatus)
    showToast(
      ok
        ? { kind: 'info', text: 'Документы ККТ сверены' }
        : { kind: 'error', text: 'Сверка ККТ неполная — повторите позже' }
    )
  }

  /** X-отчёт нефискальный: не закрывает смену и не сбрасывает итоги. */
  const printXReport = async () => {
    const result = await core.fiscal.printXReport()
    if (result.success) {
      await core.fiscal.refreshKktStatus()
      showToast({ kind: 'info', text: 'X-отчёт напечатан' })
    } else {
      showToast({
        kind: 'error',
        text: result.error ?? 'Не удалось напечатать X-отчёт'
      })
    }
  }

  /** Открыть смену: запись в БД + команда OPEN_SHIFT в ККТ. */
  const confirmOpenShift = async () => {
    if (!openShiftForm) return
    if (kktShiftMismatch) {
      showToast({ kind: 'error', text: 'Сначала согласуйте смену POSPoint и ККТ' })
      return
    }
    const initialCash = Number(openShiftForm.initialCash.replace(',', '.')) || 0
    try {
      // Если смена зоны уже открыта (в т.ч. другим устройством) — openShift
      // вернёт её и мы ПРИСОЕДИНИМСЯ, а не заведём вторую смену.
      const existing = await core.shift.getActiveShift()
      const opened = await core.shift.openShift({
        cashierId: cashierName ?? 'unknown',
        cashierName: cashierName ?? 'Кассир',
        initialCash,
        fiscalizationEnabled: openShiftForm.fiscal
      })
      setShift(opened)
      // Выбор фискализации: сохранённый для этой смены или настройка смены.
      void applyFiscalPref(opened)
      setOpenShiftForm(null)
      if (existing) {
        // Присоединение к уже открытой смене зоны — ККТ не трогаем.
        showToast({ kind: 'info', text: `Присоединились к смене №${opened.shiftNumber}` })
      } else {
        // Команда в ККТ. Native — дожидаемся ответа и обновляем статус,
        // чтобы не показывать ложный mismatch сразу после открытия.
        const kktResult = await core.fiscal.openShiftOnKkt(opened.shiftNumber)
        if (!kktResult.success) {
          showToast({
            kind: 'error',
            text: `Смена открыта, но ККТ не подтвердила открытие смены: ${kktResult.error ?? 'ошибка ККТ'}`
          })
        } else {
          // Нумерация ККТ может отличаться от локальной истории POS. После
          // успешного openShift выравниваем локальный номер по ККТ.
          const kktShiftNumber = core.fiscal.getKktStatus()?.shiftNumber
          if (kktShiftNumber && kktShiftNumber !== opened.shiftNumber && opened.id != null) {
            await db.shifts.update(opened.id, { shiftNumber: kktShiftNumber })
            opened.shiftNumber = kktShiftNumber
            setShift({ ...opened })
          }
          showToast({ kind: 'info', text: `Смена №${opened.shiftNumber} открыта` })
        }
      }
    } catch (err: unknown) {
      showToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) })
    }
  }

  /** Закрыть смену: KKT Z → локальный Z. При ошибке ККТ смена остаётся открытой. */
  const confirmCloseShift = async () => {
    try {
      await core.fiscal.refreshKktStatus()
      const kkt = core.fiscal.getKktStatus()
      const kktConnected = kkt?.connected === true
      const kktShiftOpen = kkt?.shiftOpen === true
      const closeKkt = kktConnected && kktShiftOpen
      const summary = await core.shift.closeShift({
        requireKktZ: closeKkt,
        // В браузере документы смены ККТ не читаются: сверку не требуем.
        kktDocumentsReadable: isNativeKkt(),
        ...(closeKkt ? { kktClose: () => core.fiscal.closeShiftOnKkt() } : {})
      })
      setShift(null)
      setZReport(summary)
    } catch (err: unknown) {
      // Диагностика: сообщение видно в консоли даже если тост перекрыт.
      console.error('[closeShift] failed:', err)
      showToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) })
    }
  }

  /** Печать Z-отчёта: команда на ККТ (если подключён) + системная печать. */
  const printZReport = () => {
    if (!zReport) return
    // Z на ККТ уже снят в confirmCloseShift до закрытия локальной смены.
    // Повторный CLOSE_SHIFT здесь создал бы второй Z или ошибку.
    const money = (v: number) => formatCurrency(v, currency)
    // Полный Z по зоне (облако) либо частичный (свой вклад, касса офлайн).
    const breakdown = zReport.full
      ? `
      <div class="line"><span>Рабочее место:</span><span>${escapeHtml(shortId(zReport.full.zone_id))}</span></div>
      <div class="line"><span>Чеков (зона):</span><span>${zReport.full.sales_count}</span></div>
      ${(zReport.full.returns_count ?? 0) > 0
        ? `<div class="line"><span>Возвраты:</span><span>${zReport.full.returns_count} / ${money(zReport.full.returns_total ?? 0)}</span></div>`
        : ''}
      <div class="line"><span>Фискальная выручка:</span><span>${money(zReport.full.fiscal_total)}</span></div>
      <div class="line"><span>Отложено:</span><span>${money(zReport.full.deferred_total)}</span></div>
      <div class="line"><span>Нефискальные:</span><span>${money(zReport.full.non_fiscal_total)}</span></div>
      ${(zReport.full.held_count ?? 0) > 0
        ? `<div class="line"><span>Ждут решения (маркировка):</span><span>${zReport.full.held_count} / ${money(zReport.full.held_total ?? 0)}</span></div>`
        : ''}
      ${(zReport.full.valid_ready_count ?? 0) > 0
        ? `<div class="line"><span>Готовы к печати:</span><span>${zReport.full.valid_ready_count}</span></div>`
        : ''}
      ${(zReport.full.print_failures_count ?? 0) > 0
        ? `<div class="line"><span>Ошибки печати:</span><span>${zReport.full.print_failures_count}</span></div>`
        : ''}
      ${(zReport.full.cancelled_count ?? 0) > 0
        ? `<div class="line"><span>Hold отменены:</span><span>${zReport.full.cancelled_count} / ${money(zReport.full.cancelled_total ?? 0)}</span></div>`
        : ''}
      ${(zReport.full.corrections_income_count ?? 0) > 0
        ? `<div class="line"><span>Коррекции прихода:</span><span>${zReport.full.corrections_income_count} / ${money(zReport.full.corrections_income_total ?? 0)}</span></div>`
        : ''}
      ${(zReport.full.corrections_returns_count ?? 0) > 0
        ? `<div class="line"><span>Коррекции-возвраты:</span><span>${zReport.full.corrections_returns_count} / ${money(zReport.full.corrections_returns_total ?? 0)}</span></div>`
        : ''}
      <div class="sep"></div>
      <div class="line bold" style="font-size: 13px;"><span>Итого:</span><span>${money(zReport.full.total_in_drawer)}</span></div>
      ${(zReport.full.corrections_count ?? 0) > 0 && zReport.full.total_with_corrections != null
        ? `<div class="line bold"><span>Итого с коррекциями:</span><span>${money(zReport.full.total_with_corrections)}</span></div>`
        : ''}
      <div class="sep"></div>
      ${zReport.full.by_device
        .map(
          (d) =>
            `<div class="line"><span>${escapeHtml(deviceLabel(d.device_id).label)}</span><span>${money(Number(d.total))} (${d.count})</span></div>`
        )
        .join('')}`
      : `
      <div class="line"><span>Чеков (эта касса):</span><span>${zReport.salesCount}</span></div>
      ${(zReport.returnsCount ?? 0) > 0
        ? `<div class="line"><span>Возвраты:</span><span>${zReport.returnsCount} / ${money(zReport.returnsTotal ?? 0)}</span></div>`
        : ''}
      <div class="line"><span>Наличные:</span><span>${money(zReport.cashTotal)}</span></div>
      <div class="line"><span>Карта:</span><span>${money(zReport.cardTotal)}</span></div>
      <div class="line"><span>СБП / QR:</span><span>${money(zReport.sbpTotal)}</span></div>
      <div class="sep"></div>
      <div class="line bold" style="font-size: 13px;"><span>ИТОГО (эта касса):</span><span>${money(zReport.totalRevenue)}</span></div>
      <div class="line"><span>Неполный Z (офлайн)</span><span></span></div>`

    const html = `
      <div class="center bold">=== Z-ОТЧЁТ ===</div>
      <div class="center">СМЕНА №${zReport.shift.shiftNumber}</div>
      <div class="line"><span>Кассир:</span><span>${escapeHtml(zReport.shift.cashierName)}</span></div>
      <div class="line"><span>Открыта:</span><span>${new Date(zReport.shift.openedAt).toLocaleTimeString('ru-RU')}</span></div>
      <div class="line"><span>Закрыта:</span><span>${zReport.shift.closedAt ? new Date(zReport.shift.closedAt).toLocaleTimeString('ru-RU') : '—'}</span></div>
      <div class="sep"></div>
      ${breakdown}
    `
    printTicketViaIframe(html)
  }

  /**
   * Товарный чек «Без применения ККТ» (65.10). Только по кнопке на экране
   * успеха: автоматической печати нет.
   *
   * Тег 2108 [М+]/[М-] формирует ATOL-драйвер/ОФД в фискальном чеке
   * (`imcParams`). В нефискальном HTML-чеке признак не печатаем: это не
   * фискальный документ. См. docs/ARCH-CZ-WITHDRAWAL.md.
   */
  const printNonFiscalReceipt = async () => {
    const screen = successScreen
    if (!screen) return
    const receipt = await db.receipts.where('receiptId').equals(screen.receiptId).first()
    if (!receipt) {
      showToast({ kind: 'error', text: 'Чек не найден в локальной базе' })
      return
    }
    const [companyEntry, innEntry, addressEntry] = await Promise.all([
      db.meta.get(META_COMPANY_NAME),
      db.meta.get(META_COMPANY_INN),
      db.meta.get(META_STORE_ADDRESS)
    ])
    const company =
      typeof companyEntry?.value === 'string' && companyEntry.value.trim()
        ? companyEntry.value.trim()
        : 'Организация'
    const inn =
      typeof innEntry?.value === 'string' && innEntry.value.trim() ? innEntry.value.trim() : '—'
    const address =
      typeof addressEntry?.value === 'string' && addressEntry.value.trim()
        ? addressEntry.value.trim()
        : '—'
    if (!isOrganizationReady({ company, inn, address })) {
      showToast({ kind: 'error', text: 'Заполните ИНН и адрес организации/магазина перед печатью уведомления' })
      return
    }
    const isReturn = (receipt.settlementType ?? 'income') === 'income_return'
    const html = `
      <div class="center bold">${escapeHtml(company)}</div>
      <div class="center bold">УВЕДОМЛЕНИЕ О ПРОДАЖЕ</div>
      <div class="center bold">Без применения ККТ</div>
      ${organizationHtml({ company, inn, address })}
      <div class="line"><span>Признак расчета</span><span>${isReturn ? 'Возврат прихода' : 'Приход'}</span></div>
      <div class="sep"></div>
      <div class="line"><span>Дата</span><span>${escapeHtml(new Date(receipt.createdAt).toLocaleString('ru-RU'))}</span></div>
      <div class="line"><span>Чек</span><span>${escapeHtml(receipt.offlineReceiptNumber ?? shortId(receipt.receiptId))}</span></div>
      <div class="line"><span>Кассир</span><span>${escapeHtml(receipt.cashierName ?? '—')}</span></div>
      <div class="sep"></div>
      ${receiptItemsHtml(receipt.items)}
      <div class="sep"></div>
      <div class="line bold"><span>${isReturn ? 'ВОЗВРАТ' : 'ИТОГО'}</span><span>${Math.abs(receipt.total).toFixed(2)}</span></div>
      <div class="line"><span>Оплата</span><span>${paymentMethodLabel(receipt.paymentMethod)}</span></div>
      <div class="sep"></div>
      <div class="center">Должность: Кассир</div>
      <div class="center">Подпись: ________ / ${escapeHtml(receipt.cashierName ?? '—')}</div>
      <div class="sep"></div>
      <div class="center">Документ не является фискальным чеком</div>
    `
    printTicketViaIframe(html)
  }

  /**
   * Печать копии фискального чека. Копия не является фискальным документом
   * и не заменяет чек ККТ, поэтому шапка и подвал явно это указывают.
   */
  const printReceiptCopy = async () => {
    const screen = successScreen
    if (!screen) return
    const receipt = await db.receipts.where('receiptId').equals(screen.receiptId).first()
    if (!receipt) {
      showToast({ kind: 'error', text: 'Чек не найден в локальной базе' })
      return
    }
    const [companyEntry, innEntry, addressEntry] = await Promise.all([
      db.meta.get(META_COMPANY_NAME),
      db.meta.get(META_COMPANY_INN),
      db.meta.get(META_STORE_ADDRESS)
    ])
    const company =
      typeof companyEntry?.value === 'string' && companyEntry.value.trim()
        ? companyEntry.value.trim()
        : 'Организация'
    const inn =
      typeof innEntry?.value === 'string' && innEntry.value.trim() ? innEntry.value.trim() : '—'
    const address =
      typeof addressEntry?.value === 'string' && addressEntry.value.trim()
        ? addressEntry.value.trim()
        : '—'
    const isReturn = (receipt.settlementType ?? 'income') === 'income_return'
    const fiscal = receipt.fiscalData
    printTicketViaIframe(`
      <div class="center bold">КОПИЯ ЧЕКА</div>
      <div class="center bold">${escapeHtml(company)}</div>
      ${organizationHtml({ company, inn, address })}
      <div class="line"><span>Признак расчета</span><span>${isReturn ? 'Возврат прихода' : 'Приход'}</span></div>
      <div class="sep"></div>
      <div class="line"><span>Дата</span><span>${escapeHtml(new Date(receipt.createdAt).toLocaleString('ru-RU'))}</span></div>
      <div class="line"><span>Кассир</span><span>${escapeHtml(receipt.cashierName ?? '—')}</span></div>
      ${
        fiscal
          ? `<div class="line"><span>ФД</span><span>${fiscal.fiscalDocumentNumber}</span></div>
             <div class="line"><span>ФПД</span><span>${escapeHtml(fiscal.fiscalDocumentSign)}</span></div>
             <div class="line"><span>Смена</span><span>${fiscal.shiftNumber}</span></div>
             ${fiscal.fiscalDriveNumber ? `<div class="line"><span>ФН</span><span>${escapeHtml(fiscal.fiscalDriveNumber)}</span></div>` : ''}`
          : ''
      }
      <div class="sep"></div>
      ${receiptItemsHtml(receipt.items)}
      <div class="sep"></div>
      <div class="line bold"><span>${isReturn ? 'ВОЗВРАТ' : 'ИТОГО'}</span><span>${Math.abs(receipt.total).toFixed(2)}</span></div>
      <div class="line"><span>Оплата</span><span>${paymentMethodLabel(receipt.paymentMethod)}</span></div>
      <div class="sep"></div>
      <div class="center">Копия не является фискальным чеком</div>
    `)
  }

  /** Список позиций чека: прокручивается отдельно от итога. */
  const renderCartList = () => (
    <>
      {cart.length === 0 ? (
        <p className={hint}>Отсканируйте товар или выберите из плитки.</p>
      ) : (
        <ul className="m-0 list-none p-0">
          {cart.map((line, i) => (
            <li
              key={line.lineId}
              className="flex items-center gap-2 border-b border-line/60 py-1"
            >
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-ink">
                  {line.name}
                  {line.datamatrix ? (
                    (() => {
                      const verified = markStatus[line.datamatrix.trim()]?.status
                      const view =
                        verified === 'valid'
                          ? { label: 'Подтверждён', cls: 'bg-green-100 text-okDark' }
                          : verified === 'invalid'
                            ? { label: 'Не найден', cls: 'bg-red-100 text-dangerDark' }
                            : verified === 'unknown'
                              ? { label: 'Не проверен', cls: 'bg-black/5 text-muted' }
                              : { label: 'Проверяется…', cls: 'bg-black/5 text-muted' }
                      return (
                        <span
                          data-testid="line-mark-status"
                          data-mark-status={verified ?? 'checking'}
                          className={`ml-1.5 inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-micro font-bold ${view.cls}`}
                        >
                          <Marking size={16} /> {view.label}
                        </span>
                      )
                    })()
                  ) : (
                    line.requiresMarking && <Marking size={16} className="ml-1.5 text-okDark" />
                  )}
                </div>
                <div className="text-small font-semibold tabular-nums text-ink">
                  {formatCurrency(line.price * line.qty, currency)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {/*
                  Маркированная строка — одна единица = один код марки, поэтому
                  «−/+» скрыты (иначе один код «закроет» несколько единиц).
                */}
                {line.datamatrix ? (
                  <span className="min-w-8 text-center text-small">1</span>
                ) : (
                  <>
                    <button
                      className={cx(btnBase, btn.secondary, 'h-8 w-8 p-0 text-small')}
                      onClick={() => changeQty(i, line.unit === 'кг' ? -0.1 : -1)}
                      aria-label="Меньше"
                    >
                      −
                    </button>
                    <span className="min-w-8 text-center text-small">
                      {line.unit === 'кг' ? formatKg(line.qty) : line.qty}
                    </span>
                    <button
                      className={cx(btnBase, btn.secondary, 'h-8 w-8 p-0 text-small')}
                      onClick={() => changeQty(i, line.unit === 'кг' ? 0.1 : 1)}
                      // Возврат: больше проданного не вернуть — кнопка гаснет.
                      disabled={line.maxQty != null && line.qty >= line.maxQty}
                      title={
                        line.maxQty != null && line.qty >= line.maxQty
                          ? 'Достигнуто количество из исходного чека'
                          : undefined
                      }
                      aria-label="Больше"
                    >
                      +
                    </button>
                  </>
                )}
                <IconButton
                  tone="danger"
                  size="md"
                  label="Удалить"
                  onClick={() => removeLine(i)}
                >
                  <Close size={18} />
                </IconButton>
              </div>
            </li>
          ))}
        </ul>
      )}

      {cart.length === 0 && lastChange ? (
        <div className="mt-3 text-right" data-testid="last-change">
          <div className="text-small tabular-nums text-muted">Сдача с прошлого чека</div>
          <div className="text-[32px] font-bold tabular-nums text-okDark">
            {formatCurrency(lastChange.change, currency)}
          </div>
          <div className="text-tiny text-muted">
            Получено {formatCurrency(lastChange.cashGiven, currency)} · Чек{' '}
            {formatCurrency(lastChange.total, currency)}
          </div>
        </div>
      ) : null}
    </>
  )

  /** Итог и действия чека — закреплены внизу, не прокручиваются. */
  const renderCartFooter = () => (
    <>
      {!(cart.length === 0 && lastChange) && (
        <div className="text-right">
          <div className="text-small tabular-nums text-muted">
            {returnMode ? 'К возврату' : 'Итого'}
          </div>
          <div
            className={cx(
              'text-[32px] font-bold tabular-nums',
              returnMode ? 'text-danger' : 'text-ink'
            )}
          >
            {returnMode ? '−' : ''}
            {formatCurrency(total, currency)}
          </div>
        </div>
      )}

      {/* Возврат фискализируется всегда — галочка «нефискальный чек» не нужна. */}
      {!returnMode && renderFiscalToggle()}

      <button
        className={cx(
          btnBase,
          returnMode ? btn.danger : btn.success,
          'mt-2 h-11 w-full px-4 text-base2 font-bold'
        )}
        onClick={() => setPayModal({ stage: 'choose' })}
        disabled={cart.length === 0 || paying || !shift || kktShiftMismatch != null}
        title={
          kktShiftMismatch
            ? 'Сначала согласуйте смену POSPoint и ККТ'
            : !shift
              ? 'Откройте смену перед продажей'
              : undefined
        }
      >
        {paying ? 'ОФОРМЛЕНИЕ…' : returnMode ? 'ОФОРМИТЬ ВОЗВРАТ' : 'ОПЛАТИТЬ'}
      </button>

      {/* Опасное действие — текстовой ссылкой под CTA, чтобы не путать с оплатой. */}
      <button
        type="button"
        className="mt-1 w-full cursor-pointer text-small text-danger underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
        onClick={cancelCart}
        disabled={cart.length === 0 || paying}
        title="Очистить чек"
      >
        Отменить чек
      </button>
    </>
  )

  /** Полное содержимое чека — для мобильной шторки. */
  const renderCart = () => (
    <>
      {renderCartList()}
      {renderCartFooter()}
    </>
  )

  /**
   * Поле поиска товара. На десктопе живёт в строке оборудования панели смены
   * фиксированной ширины, без смены — отдельной строкой.
   */
  const searchForm = (
    <form onSubmit={submitManual} className="flex min-w-0 gap-1.5">
      <input
        type="text"
        placeholder="Поиск товара"
        value={manualCode}
        onChange={(e) => setManualCode(e.target.value)}
        className={cx(input, 'h-8 min-w-0 flex-1 text-small')}
      />
      {isMobile && (
        <button
          type="button"
          onClick={() => setCameraOpen(true)}
          className={cx(btnBase, btn.secondary, 'h-8 w-8 p-0')}
          title="Сканировать камерой"
        >
          <Camera size={18} />
        </button>
      )}
    </form>
  )

  // Бейдж ККТ в шапке смены: состояние + очередь ОФД + срок ФН.
  const kktBadgeLabel = kktStatus ? kktStateText(kktStatus.state) : 'нет данных'
  const kktBadgeOk = kktStatus?.state === 'ok'
  const kktOfdUnsent = kktStatus?.ofd?.unsentDocs ?? 0
  const kktFnDaysLeft = kktStatus?.fn?.daysLeft

  // Сводка проблем: одна свёрнутая строка вместо отдельных плашек.
  // «Смена закрыта» сюда не входит — это блокирующее состояние с CTA.
  const problems = useMemo(
    () =>
      buildPosProblems({
        heldCount: heldTasks.length,
        replacementCount: replacementTasks.length,
        kktMismatch: kktShiftMismatch != null,
        externalKktShift: Boolean(externalKktShift),
        kktState: kktStatus?.state,
        ofdUnsent: kktOfdUnsent,
        fnDaysLeft: kktFnDaysLeft ?? null,
        pendingFiscal: pendingFiscalCount,
        markingDisabled: Boolean(core.marking && !czEnabled),
        // Весы считаем проблемой только если они настроены, но отвалились.
        scalesDisconnected: Boolean(scaleStatus.protocol) && !scaleStatus.connected
      }),
    [
      heldTasks.length,
      replacementTasks.length,
      kktShiftMismatch,
      externalKktShift,
      kktStatus?.state,
      kktOfdUnsent,
      kktFnDaysLeft,
      pendingFiscalCount,
      core.marking,
      czEnabled,
      scaleStatus.protocol,
      scaleStatus.connected
    ]
  )
  const problemsSeverity = highestSeverity(problems)
  const criticalKey = criticalSignature(problems)

  // «Понятно» у info-уведомлений: отметка живёт до конца смены.
  const noticeKey = shift?.id != null ? `pos.notice.ack.${shift.id}` : ''
  useEffect(() => {
    if (!noticeKey) {
      setAckNotices([])
      return
    }
    try {
      const raw = window.localStorage.getItem(noticeKey)
      const parsed: unknown = raw ? JSON.parse(raw) : []
      setAckNotices(
        Array.isArray(parsed)
          ? (parsed.filter((v) => typeof v === 'string') as PosProblem['id'][])
          : []
      )
    } catch {
      setAckNotices([])
    }
  }, [noticeKey])

  const acknowledgeNotice = useCallback(
    (id: PosProblem['id']) => {
      if (!noticeKey) return
      setAckNotices((prev) => {
        if (prev.includes(id)) return prev
        const next = [...prev, id]
        try {
          window.localStorage.setItem(noticeKey, JSON.stringify(next))
        } catch {
          // Приватный режим/запрет storage: скрытие живёт только до перезагрузки.
        }
        return next
      })
    },
    [noticeKey]
  )


  // Подсветка — один раз на появление набора критичных проблем.
  useEffect(() => {
    if (!criticalKey) {
      lastCriticalSignatureRef.current = ''
      return
    }
    if (criticalKey === lastCriticalSignatureRef.current) return
    lastCriticalSignatureRef.current = criticalKey
    setProblemsHighlighted(true)
    const timer = setTimeout(() => setProblemsHighlighted(false), 1800)
    return () => clearTimeout(timer)
  }, [criticalKey])

  /** Действие с правами администратора: сразу или после PIN директора. */
  const runAdminAction = useCallback(
    (label: string, run: () => void) => {
      if (canOpenAdmin(cashier as ActiveCashier | null | undefined)) {
        run()
        return
      }
      setAdminConfirm({ label, run })
    },
    [cashier]
  )

  // Смена категории/поиска: сброс лимита плиток и сворачивание «Ещё».
  useEffect(() => {
    setGridLimit(GRID_PAGE)
    setCategoriesExpanded(false)
  }, [manualCode, selectedCategory, selectedSubcategory])

  // Категории: 7 самых крупных + «Ещё (N)». Выбранная категория не должна
  // исчезать при сворачивании — добавляем её к превью.
  // Частотные категории — в начало, остальные сохраняют порядок импорта.
  const sortedCategories = orderCategoriesByFrequency(categories, frequency.categories).list
  const previewCategories = sortedCategories.slice(0, CATEGORY_PREVIEW)
  const hiddenSelected =
    selectedCategory !== 'all' && !previewCategories.some((c) => c.name === selectedCategory)
  const visibleCategories = categoriesExpanded
    ? sortedCategories
    : hiddenSelected
      ? [...previewCategories, sortedCategories.find((c) => c.name === selectedCategory)!]
      : previewCategories
  const hiddenCategoriesCount = sortedCategories.length - previewCategories.length
  // Каталог рисуется порциями по GRID_PAGE плиток.
  const shownProducts = gridProducts.slice(0, gridLimit)
  // Главный экран: показываем категории, а не все товары сразу.
  // Поиск и выбранная категория работают как раньше.
  const isHome = !manualCode.trim() && selectedCategory === 'all' && !returnMode

  /**
   * Чекбокс «Фискализировать чек в ККТ». Управляет ТОЛЬКО наличными
   * (карта/СБП фискализируются всегда) и запоминается на текущую смену.
   */
  const renderFiscalToggle = () => {
    if (returnMode) {
      return (
        <p className="my-2.5 rounded-md border border-line bg-surface px-3 py-2 text-small text-muted">
          {returnNonFiscal
            ? 'Возврат по продаже без ККТ: только наличные.'
            : 'Возврат фискальный — по режиму исходного чека.'}
        </p>
      )
    }
    if (hasMarkingSkip) {
      return (
        <p className="my-2.5 rounded-md border border-warnLine bg-red-50 px-3 py-2 text-small font-semibold text-dangerDark">
          В чеке есть товар без кода маркировки — чек будет без ККТ, оплата только наличными.
        </p>
      )
    }
    if (!canSkipFiscalization) {
      return (
        <label className="my-2.5 flex items-start gap-2 text-sm opacity-70">
          <input type="checkbox" checked disabled className="mt-0.5 h-4 w-4" />
          <span className="font-semibold text-ink">
            Чек будет фискализирован
            <span className="block text-tiny font-normal text-muted">
              Снять фискализацию может старший смены или управляющий
            </span>
          </span>
        </label>
      )
    }
    return (
    <label className="my-1.5 flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        className="h-4 w-4 accent-ok"
        checked={fiscalizeThisSale}
        onChange={(e) => {
          const value = e.target.checked
          setFiscalizeThisSale(value)
          if (shift) {
            void db.meta.put({
              key: fiscalPrefKey(shift.zoneId ?? 'legacy', shift.shiftNumber),
              value
            })
          }
        }}
      />
      <span className="font-semibold text-ink">Фискализировать чек в ККТ</span>
    </label>
    )
  }

  return (
    <section>
      {toast && (
        <div
          className={cx(
            'fixed bottom-6 left-1/2 z-[1200] flex -translate-x-1/2 items-center justify-between gap-3 rounded-card border px-4 py-2.5 text-sm font-semibold shadow-modal',
            toast.kind === 'error'
              ? 'border-warnLine bg-red-50 text-dangerDark'
              : 'border-blue-200 bg-blue-50 text-blue-700'
          )}
          role="status"
        >
          <span>{toast.text}</span>
          {toast.action && (
            <button
              className="shrink-0 cursor-pointer rounded-md border border-current bg-transparent px-3 py-1.5 font-semibold text-inherit"
              onClick={toast.action.onClick}
            >
              {toast.action.label}
            </button>
          )}
        </div>
      )}

      {/* Раскладка кассы: слева 30% — смена, ККТ+весы, уведомления и чек;
          справа 70% — каталог на всю высоту. */}
      <div
        className={
          compactLayout
            ? 'block min-w-0'
            : 'grid grid-cols-[minmax(320px,30%)_minmax(0,1fr)] items-stretch gap-3'
        }
      >
        {/* Левая колонка: фиксированная высота, чек забирает остаток. */}
        <div
          className={cx(
            'flex min-w-0 flex-col gap-3',
            !compactLayout && 'h-[calc(100dvh-8.5rem)] min-h-[520px]'
          )}
        >

      {/* Единый блок «Проблемы»: свёрнут всегда, цвет — по тяжести. */}
      <PosProblemsPanel
        problems={problems}
        severity={problemsSeverity}
        open={problemsOpen}
        onToggle={() => setProblemsOpen((v) => !v)}
        onAcknowledge={acknowledgeNotice}
        acknowledged={ackNotices}
        highlighted={problemsHighlighted}
        compact={compactLayout}
        className="order-2 shrink-0"
        overlay
        actions={{
          hold: {
            main: heldTasks[0]
              ? {
                  label: 'Открыть',
                  onClick: () => openHeldModal(heldTasks[0].receipt)
                }
              : undefined,
            more: heldTasks[0]
              ? [
                  {
                    label: 'Проверить ещё раз',
                    onClick: () => void retryMarkingCheck(heldTasks[0].receipt),
                    disabled: heldBusy
                  }
                ]
              : []
          },
          replacement: {
            main: replacementTasks[0]
              ? {
                  label: 'Печать фискального',
                  onClick: () => openReplacementModal(replacementTasks[0].receipt),
                  disabled: replacementBusy
                }
              : undefined,
            more: replacementTasks[0]
              ? [
                  {
                    label: 'Проверить ещё раз',
                    onClick: () => void retryMarkingCheck(replacementTasks[0].receipt),
                    disabled: replacementBusy
                  }
                ]
              : []
          },
          kkt_mismatch: {
            main:
              kktShiftMismatch === 'kkt_closed_pos_open' && shift
                ? {
                    label: 'Закрыть смену POS',
                    onClick: () =>
                      runAdminAction('Согласование смены POSPoint и ККТ', () =>
                        void confirmCloseShift()
                      )
                  }
                : undefined
          },
          kkt_external: {
            main: { label: 'X-отчёт', onClick: () => void printXReport() }
          }
        }}
        details={{
          hold:
            heldTasks.length > 0 ? (
              <div className="space-y-1">
                {heldTasks.map(({ receipt }) => (
                  <div
                    key={receipt.receiptId}
                    className="rounded-md border border-line bg-white px-2 py-1.5 text-tiny"
                  >
                    {formatCurrency(receipt.total, currency)} · {receipt.items.length} поз. ·{' '}
                    {receipt.markingPrintState === 'ready'
                      ? 'код подтверждён'
                      : 'проверяется / код недействителен'}
                    {' · '}
                    деньги в ящике уже есть
                  </div>
                ))}
              </div>
            ) : undefined,
          replacement:
            replacementTasks.length > 0 ? (
              <div className="space-y-1">
                {replacementTasks.map(({ receipt }) => {
                  const ready = receipt.replacementState === 'awaiting_print'
                  const failed = receipt.replacementState === 'failed'
                  return (
                    <div
                      key={receipt.receiptId}
                      className="flex flex-wrap items-center justify-between gap-1.5 rounded-md border border-line bg-white px-2 py-1.5 text-tiny"
                    >
                      <span>
                        {formatCurrency(receipt.total, currency)} ·{' '}
                        {receipt.items.length} поз. ·{' '}
                        {ready
                          ? 'код подтверждён'
                          : failed
                            ? 'ошибка/код недействителен'
                            : 'проверяется в ЧЗ'}
                      </span>
                      <button
                        className={cx(btnBase, btn.secondary, btnSize.sm)}
                        disabled={replacementBusy}
                        onClick={() => openReplacementModal(receipt)}
                      >
                        Отмена
                      </button>
                    </div>
                  )
                })}
              </div>
            ) : undefined,
          kkt_mismatch: kktShiftMismatch ? (
            <div className="text-tiny">
              {kktShiftMismatchText(kktShiftMismatch)}
              {kktStatus?.shiftNumber ? ` · ККТ №${kktStatus.shiftNumber}` : ''}. Фискальные
              операции заблокированы до согласования.
            </div>
          ) : undefined,
          kkt_external: externalKktShift ? (
            <div className="text-tiny">
              Смена №{kktStatus?.shiftNumber}. Z-отчёт и закрытие выполняет ПО, открывшее
              смену — POSPoint работает в режиме просмотра.
            </div>
          ) : undefined
        }}
      />


        {/* Текущий чек: список прокручивается, итог и оплата закреплены. */}
        {!compactLayout && (
          <div className="order-1 flex min-h-0 flex-1 flex-col rounded-card border border-line p-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="m-0 text-big font-bold text-ink">Текущий чек</h3>
              <span className="text-tiny text-muted">
                {cart.length > 0 ? `${cart.length} поз.` : 'пусто'}
              </span>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">{renderCartList()}</div>
            <div className="shrink-0 border-t border-line pt-3">{renderCartFooter()}</div>
          </div>
        )}
        </div>

        {/* Правая колонка: каталог на всю высоту, выровнен со сменой. */}
        <div
          className={cx(
            'flex min-w-0 flex-col',
            !compactLayout && 'h-[calc(100dvh-8.5rem)] min-h-[520px]'
          )}
        >
        {/* Зона каталога + ручной ввод/сканер (в компактном лэйауте — вверху, одной колонкой) */}
        <div className="flex min-h-0 flex-1 flex-col rounded-card border border-line p-4">
          {/* ARCH-TASK-65: панель возврата — поиск исходного чека этой кассы. */}
          {returnMode && (
            <div className="mb-3 rounded-card border border-warnLine bg-red-50 p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <strong className="text-dangerDark">Режим возврата</strong>
                {returnSource ? (
                  <span className="flex items-center gap-2 text-small text-muted">
                    Исходный чек: {shortId(returnSource.receiptId)} ·{' '}
                    {formatCurrency(returnSource.total, currency)}
                    {returnSource.status === 'non_fiscal' && cashier?.role === 'admin' && (
                      <button
                        type="button"
                        onClick={() => void openCorrectionModal(returnSource)}
                        className={cx(btnBase, btn.secondary, btnSize.sm)}
                        title="Коррекция возврата прихода — только для администратора"
                      >
                        Чек коррекции
                      </button>
                    )}
                  </span>
                ) : (
                  cart.length > 0 && (
                    <span className="text-small text-amber-800">
                      Возврат без исходного чека — лимит по проданному не проверяется
                    </span>
                  )
                )}
              </div>
              <input
                type="text"
                placeholder="Номер чека, дата (ДД.ММ.ГГГГ) или сумма…"
                value={returnSearch}
                onChange={(e) => setReturnSearch(e.target.value)}
                className={input}
              />

              {/* 65.10: «вернуть на склад» или «возврат + списание» */}
              {canWriteoff && (
                <div className="mt-2 rounded-md border border-line bg-surface p-2">
                  <label className="flex items-center gap-1.5 text-small font-semibold text-ink">
                    <input
                      type="checkbox"
                      checked={returnWriteoff}
                      onChange={(e) => setReturnWriteoff(e.target.checked)}
                      className="h-4 w-4 cursor-pointer accent-ok"
                    />
                    Списать возвращённый товар
                  </label>
                  {returnWriteoff && (
                    <div className="mt-1.5 flex flex-col gap-1.5">
                      <select
                        value={writeoffReason}
                        onChange={(e) =>
                          setWriteoffReason(e.target.value as WriteoffReasonCode)
                        }
                        className={input}
                      >
                        <option value="damage">Порча</option>
                        <option value="expired">Истёк срок</option>
                        <option value="breakage">Бой</option>
                        <option value="supplier_return">Возврат поставщику</option>
                        <option value="other">Другое</option>
                      </select>
                      <input
                        type="text"
                        value={writeoffComment}
                        onChange={(e) => setWriteoffComment(e.target.value)}
                        className={input}
                        placeholder="Комментарий к списанию"
                      />
                      <span className="text-tiny text-muted">
                        Товар вернётся на склад и сразу спишется; итог по остатку — 0.
                      </span>
                    </div>
                  )}
                </div>
              )}
              {!returnSource && (
                <ul className="m-0 mt-2 max-h-64 list-none space-y-1.5 overflow-y-auto p-0">
                  {returnCandidates.length === 0 && (
                    <li className={hint}>
                      Чеки не найдены. Возврат можно оформить и по штрихкоду — отсканируйте товар.
                    </li>
                  )}
                  {returnCandidates.map((candidate) => {
                    const r = candidate.receipt
                    return (
                      <li key={r.receiptId}>
                        <button
                          type="button"
                          className={cx(
                            'flex w-full items-center justify-between gap-2 rounded-md border border-lineStrong bg-surface px-3 py-2 text-left text-small',
                            candidate.fullyReturned
                              ? 'cursor-not-allowed opacity-60'
                              : 'hover:bg-soft'
                          )}
                          onClick={() => loadReturnReceipt(candidate)}
                          disabled={candidate.fullyReturned}
                          title={
                            candidate.fullyReturned
                              ? 'По этому чеку уже возвращено всё проданное'
                              : candidate.nonFiscalSource
                                ? 'Продажа без фискального чека — возврат без ККТ (только наличные)'
                                : 'Подставить позиции чека в возврат'
                          }
                        >
                          <span className="min-w-0">
                            <span className="block font-semibold text-ink">
                              {new Date(r.createdAt).toLocaleString('ru-RU', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}{' '}
                              · {r.items.length} поз.
                              {candidate.nonFiscalSource && (
                                <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-micro font-semibold text-amber-800">
                                  без фискального чека — возврат без ККТ
                                </span>
                              )}
                              {candidate.fullyReturned && (
                                <span className="ml-1.5 rounded bg-red-100 px-1.5 py-0.5 text-micro font-semibold text-dangerDark">
                                  возвращён полностью
                                </span>
                              )}
                              {!candidate.fullyReturned && candidate.partiallyReturned && (
                                <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-micro font-semibold text-amber-800">
                                  частично возвращён
                                </span>
                              )}
                            </span>
                            <span className="block text-tiny text-muted">
                              {shortId(r.receiptId)} · {r.paymentMethod ?? 'cash'}
                              {r.fiscalData ? ` · ФД ${r.fiscalData.fiscalDocumentNumber}` : ''}
                            </span>
                          </span>
                          <span className="shrink-0 font-bold tabular-nums text-ink">
                            {formatCurrency(r.total, currency)}
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          )}

          {/* Смена, ККТ, весы и поиск — внутри окна каталога. */}
          {shift ? (
            <div className="mb-3 shrink-0">
              <PosShiftPanel
                shift={shift}
                kktStatus={kktStatus}
                kktBadgeLabel={kktBadgeLabel}
                kktBadgeOk={kktBadgeOk}
                scaleStatus={scaleStatus}
                canManageShifts={canManageShifts}
                isOpenerDevice={
                  !shift.openedByDevice || shift.openedByDevice === myDeviceId
                }
                returnMode={returnMode}
                paying={paying}
                currency={currency}
                onMore={() => setMoreActionsOpen(true)}
                onExitReturn={() => exitReturnMode()}
                onCloseShift={() => void confirmCloseShift()}
                onRetryKktSync={() => void retryKktSync()}
                search={searchForm}
              />
            </div>
          ) : (
            <>
              {shiftLoaded && !kktShiftMismatch && (
                <div className="mb-3 flex shrink-0 flex-col items-center gap-3 rounded-panel border border-warnLine bg-red-50 px-5 py-4 text-dangerDark">
                  <strong>Кассовая смена закрыта</strong>
                  <button
                    className={cx(btnBase, btn.success, btnSize.lg)}
                    onClick={() => setOpenShiftForm({ initialCash: '', fiscal: true })}
                  >
                    Открыть смену
                  </button>
                </div>
              )}
              <div className="mb-3">{searchForm}</div>
            </>
          )}

          {/* Горизонтальный скролл-бар категорий (скрыт в режиме поиска). */}
          {/* «Часто покупают»: 7-дневная история. Скрыт по умолчанию,
              включается галочкой «Быстрые товары» в «Политике кассы». */}
          {!manualCode.trim() && frequentEnabled && frequentProducts.length > 0 && (
            <div className="mb-2" data-testid="frequent-products">
              <div className="mb-1 text-tiny font-semibold text-muted">Часто покупают</div>
              <div className="flex flex-wrap gap-1.5">
                {frequentProducts.map((p) => (
                  <button
                    key={p.id ?? p.barcode}
                    type="button"
                    className={cx(
                      'cursor-pointer truncate rounded-md border border-line bg-soft px-2.5 font-semibold text-ink hover:bg-white',
                      touch ? 'min-h-11 max-w-[220px] text-small' : 'min-h-9 max-w-[180px] text-tiny'
                    )}
                    onClick={() => void addProductWithMarking(p)}
                    title={`${p.name} · ${formatCurrency(p.price, currency)}`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!isHome && !manualCode.trim() && categories.length > 0 && (
            <div
              className={cx(
                'relative z-10 mb-2 flex overflow-x-auto pb-2.5',
                touch ? 'gap-2.5' : 'gap-2'
              )}
            >
              <button
                className={cx(
                  'inline-flex items-center whitespace-nowrap rounded-pill border font-semibold',
                  touch ? 'min-h-11 px-4 py-2.5 text-sm' : 'px-3.5 py-1.5 text-small',
                  selectedCategory === 'all'
                    ? 'border-ink bg-ink text-white'
                    : 'border-lineStrong bg-white text-ink'
                )}
                onClick={() => setSelectedCategory('all')}
              >
                Все ({categories.reduce((s, c) => s + c.count, 0)})
              </button>
              {visibleCategories.map((c) => (
                <button
                  key={c.name}
                  className={cx(
                    'inline-flex items-center whitespace-nowrap rounded-pill border font-semibold',
                    touch
                      ? 'min-h-11 gap-2 px-3 py-2 text-sm'
                      : 'gap-1.5 px-3.5 py-1.5 text-small',
                    selectedCategory === c.name
                      ? 'border-ink bg-ink text-white'
                      : 'border-lineStrong bg-white text-ink'
                  )}
                  onClick={() => setSelectedCategory(c.name)}
                >
                  <CategoryImage
                    name={c.name}
                    hasImage={c.hasImage}
                    size={touch ? 28 : 22}
                    className="shrink-0"
                  />
                  {c.name} ({c.count})
                </button>
              ))}
              {hiddenCategoriesCount > 0 && (
                <button
                  className={cx(
                    'whitespace-nowrap rounded-pill border border-lineStrong bg-white font-semibold text-ink',
                    touch ? 'min-h-11 px-4 py-2.5 text-sm' : 'px-3.5 py-1.5 text-small'
                  )}
                  onClick={() => setCategoriesExpanded((v) => !v)}
                  title={
                    categoriesExpanded ? 'Свернуть категории' : 'Показать остальные категории'
                  }
                >
                  {categoriesExpanded ? 'Свернуть' : `Ещё (${hiddenCategoriesCount})`}
                </button>
              )}
            </div>
          )}

          {/* Листья выбранного верхнего уровня (если есть). */}
          {!manualCode.trim() && selectedCategory !== 'all' && subcategories.length > 0 && (
            <div className={cx('mb-2 flex overflow-x-auto pb-2', touch ? 'gap-2.5' : 'gap-2')}>
              <button
                className={cx(
                  'whitespace-nowrap rounded-pill border font-semibold',
                  touch ? 'min-h-10 px-3.5 py-2 text-sm' : 'px-3 py-1 text-small',
                  selectedSubcategory === 'all'
                    ? 'border-ink bg-ink text-white'
                    : 'border-lineStrong bg-white text-ink'
                )}
                onClick={() => setSelectedSubcategory('all')}
              >
                Все
              </button>
              {subcategories.map((s) => (
                <button
                  key={s.name}
                  className={cx(
                    'whitespace-nowrap rounded-pill border font-semibold',
                    touch ? 'min-h-10 px-3.5 py-2 text-sm' : 'px-3 py-1 text-small',
                    selectedSubcategory === s.name
                      ? 'border-ink bg-ink text-white'
                      : 'border-lineStrong bg-white text-ink'
                  )}
                  onClick={() => setSelectedSubcategory(s.name)}
                >
                  {s.name} ({s.count})
                </button>
              ))}
            </div>
          )}

          <div className={cx('mt-1 flex items-center justify-between gap-2', returnMode && 'hidden')}>
            <h3 className="m-0 text-base2 font-bold text-ink">
              {manualCode.trim()
                ? `Результаты поиска · ${gridProducts.length}`
                : isHome
                  ? `Категории · ${categories.length}`
                  : selectedSubcategory === 'all'
                    ? `${selectedCategory} · ${gridProducts.length}`
                    : `${selectedCategory} / ${selectedSubcategory} · ${gridProducts.length}`}
            </h3>
            {!manualCode.trim() && selectedCategory !== 'all' && (
              <button
                type="button"
                className="cursor-pointer whitespace-nowrap text-small font-semibold text-muted hover:text-ink"
                onClick={() => setSelectedCategory('all')}
                title="Вернуться к категориям"
              >
                ← Все категории
              </button>
            )}
            {manualCode.trim() && (
              <button
                type="button"
                className="cursor-pointer whitespace-nowrap text-small font-semibold text-muted hover:text-ink"
                onClick={() => setManualCode('')}
                title="Сбросить поиск"
              >
                <Close size={16} /> Сбросить
              </button>
            )}
          </div>
          {/* В режиме возврата каталог скрыт: добавление позиций — из чека или
              сканированием штрихкода (без риска случайно «продать»). */}
          <div className={cx('relative z-0 mt-2', returnMode && 'hidden')}>
          {isHome ? (
            categories.length === 0 ? (
              <p className={hint}>
                В активной версии каталога нет категорий. Импортируйте прайс в кабинете.
              </p>
            ) : (
              <CategoryGrid
                items={sortedCategories.map((c) => ({
                  name: c.name,
                  count: c.count,
                  hasImage: c.hasImage
                }))}
                onSelect={(name) => setSelectedCategory(name)}
                mobile={isMobile}
                touch={touch}
              />
            )
          ) : gridProducts.length === 0 ? (
            <p className={hint}>
              {manualCode.trim()
                ? 'Ничего не найдено. Проверьте запрос или отсканируйте штрихкод.'
                : selectedCategory === 'all'
                  ? 'В активной версии каталога нет товаров. Импортируйте прайс в кабинете или добавьте товар при сканировании.'
                  : 'В этой категории нет товаров.'}
            </p>
          ) : (
            <div
              className={
                isMobile
                  ? 'grid grid-cols-2 gap-2.5'
                  : touch
                    ? 'grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(150px,1fr))]'
                    : 'grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(120px,1fr))]'
              }
            >
              {shownProducts.map((p, index) => {
                const isWeight = p.isWeight || p.unit === 'кг'
                return (
                  <button
                    key={p.id}
                    data-ordinal={p.categoryOrdinal ?? ''}
                    className={cx(
                      'flex flex-col justify-between gap-1 rounded-card border border-lineStrong bg-surface text-left hover:bg-soft',
                      touch ? 'min-h-[150px] p-3' : 'min-h-[132px] p-2.5',
                      isMobile && 'min-h-[140px] p-2.5'
                    )}
                    onClick={() => {
                      // Единый путь добавления: весовой → модалка веса,
                      // маркируемый → ожидание DataMatrix/пул тары (F1).
                      void addProductWithMarking(p)
                      if (!p.requiresMarking && !isWeight) {
                        showToast({ kind: 'info', text: `Добавлено: ${p.name}` })
                      }
                    }}
                    title={p.name}
                  >
                    <ProductTileImage
                      name={p.name}
                      categoryName={p.categoryParent || p.category}
                      ordinal={p.categoryOrdinal ?? index + 1}
                      imageUrl={p.imageUrl}
                      fullWidth
                      className="mb-1.5"
                    />
                    <span
                      className={cx(
                        'line-clamp-2 font-semibold leading-snug',
                        touch ? 'text-base2' : 'text-sm'
                      )}
                    >
                      {p.name}
                    </span>
                    {isWeight ? (
                      <span
                        className={cx(
                          'mt-auto font-bold text-okDark',
                          touch ? 'text-big' : 'text-base2'
                        )}
                      >
                        <Scales size={16} /> {formatCurrency(p.price, currency)}/кг
                      </span>
                    ) : (
                      <span
                        className={cx(
                          'mt-auto font-bold text-info',
                          touch ? 'text-big' : 'text-base2'
                        )}
                      >
                        {formatCurrency(p.price, currency)}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}

          {gridProducts.length > gridLimit && (
            <div className="mt-2.5 flex justify-center">
              <button
                className={cx(btnBase, btn.secondary, btnSize.md)}
                onClick={() => setGridLimit((v) => v + GRID_PAGE)}
              >
                Показать ещё {GRID_PAGE}
              </button>
            </div>
          )}
          </div>
        </div>
        </div>
      </div>

      {/* Плавающая нижняя панель корзины (Bottom Action Bar) — компактный лэйаут.
          Оплата — одно действие из панели: «К чеку» открывает шторку для правки
          строк, отдельная кнопка сразу ведёт к выбору способа оплаты. */}
      {compactLayout && cart.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-bar flex min-h-16 items-stretch gap-2 bg-ink px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] text-white">
          <button
            type="button"
            className="flex min-h-12 min-w-0 flex-1 items-center text-left"
            onClick={() => setCartSheetOpen(true)}
            title="Открыть чек: изменить количество и удалить строки"
          >
            <span className="min-w-0">
              <span className="text-tiny opacity-85">
                {returnMode ? 'К возврату' : 'К чеку'} · {qtyTotal}
                {returnMode ? '' : ' шт'}
              </span>
              <br />
              <strong className="text-big tabular-nums">
                {returnMode ? '−' : ''}
                {formatCurrency(total, currency)}
              </strong>
            </span>
            <span className="ml-2 inline-flex items-center gap-1 whitespace-nowrap rounded-[10px] px-3 py-2.5 text-small font-bold opacity-90 ring-1 ring-white/30">
              <Cart size={16} /> Чек
            </span>
          </button>
          <button
            type="button"
            className={cx(
              btnBase,
              btnSize.md,
              'min-h-12 shrink-0 whitespace-nowrap px-4 text-base2 font-bold',
              returnMode ? 'bg-danger text-white' : 'bg-ok text-white',
              'disabled:opacity-50'
            )}
            onClick={() => setPayModal({ stage: 'choose' })}
            disabled={!shift || paying || kktShiftMismatch != null}
            title={
              kktShiftMismatch
                ? 'Сначала согласуйте смену POSPoint и ККТ'
                : !shift
                  ? 'Откройте смену перед продажей'
                  : undefined
            }
          >
            {paying ? '…' : returnMode ? 'ВОЗВРАТ' : 'ОПЛАТИТЬ'}
          </button>
        </div>
      )}
      {compactLayout && cart.length === 0 && (
        <div className="fixed inset-x-0 bottom-0 z-bar flex min-h-12 items-center justify-center border-t border-line bg-soft pb-[env(safe-area-inset-bottom)] text-small text-muted">
          Выберите товар, чтобы начать чек
        </div>
      )}

      {/* Шторка чека (Bottom Sheet) — компактный лэйаут. */}
      {compactLayout && cartSheetOpen && (
        <div
          className="fixed inset-0 z-sheet flex items-end bg-ink/50"
          role="dialog"
          aria-modal="true"
          aria-label="Текущий чек"
        >
          <div className="flex max-h-[85vh] w-full flex-col rounded-t-sheet bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="m-0 text-big font-bold text-ink">Текущий чек</h3>
              <button
                className={cx(btnBase, btn.secondary, 'h-11 w-11 p-0 text-base2')}
                onClick={() => setCartSheetOpen(false)}
                aria-label="Закрыть"
              >
                <Close size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">{renderCart()}</div>
          </div>
        </div>
      )}

      {/* Модалка добавления товара «на лету» */}
      {createModal && (
        <div className={cx(overlay, 'z-modal')} role="dialog" aria-modal="true" aria-label="Добавить товар на лету">
          <div className={modalLg}>
            <h3 className="mt-0 text-big font-bold">Добавить товар на лету</h3>
            <form onSubmit={submitCreate}>
              <label className={fieldLabel}>
                Штрихкод
                <input
                  type="text"
                  value={createModal.barcode}
                  onChange={(e) =>
                    setCreateModal((m) => (m ? { ...m, barcode: e.target.value } : m))
                  }
                  className={fieldInput}
                  placeholder="(необязательно)"
                />
              </label>
              <label className={fieldLabel}>
                Наименование *
                <input
                  type="text"
                  autoFocus
                  value={createModal.name}
                  onChange={(e) =>
                    setCreateModal((m) => (m ? { ...m, name: e.target.value } : m))
                  }
                  className={fieldInput}
                  placeholder="Например, Пакет-майка"
                />
              </label>
              <label className={fieldLabel}>
                {createModal.isWeight ? 'Цена за кг, ₽ *' : 'Цена, ₽ *'}
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={createModal.price}
                  onChange={(e) =>
                    setCreateModal((m) => (m ? { ...m, price: e.target.value } : m))
                  }
                  className={fieldInput}
                  placeholder="0.00"
                />
              </label>
              <div className="mt-3 flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-small">
                  <input
                    type="checkbox"
                    checked={createModal.isWeight}
                    onChange={(e) =>
                      setCreateModal((m) => (m ? { ...m, isWeight: e.target.checked } : m))
                    }
                    className="h-4 w-4 accent-ok"
                  />
                  Весовой товар (цена за кг)
                </label>
                {czEnabled && (
                  <label className="flex items-center gap-2 text-small">
                    <input
                      type="checkbox"
                      checked={createModal.requiresMarking}
                      onChange={(e) =>
                        setCreateModal((m) =>
                          m ? { ...m, requiresMarking: e.target.checked } : m
                        )
                      }
                      className="h-4 w-4 accent-ok"
                    />
                    Обязательная маркировка
                  </label>
                )}
              </div>
              <div className="mt-4 flex gap-2">
                <button type="submit" className={cx(btnBase, btn.success, btnSize.md, 'w-full')} disabled={saving}>
                  {saving ? 'Сохранение…' : 'Сохранить и добавить в чек'}
                </button>
                <button type="button" onClick={() => setCreateModal(null)} disabled={saving} className={cx(btnBase, btn.secondary, btnSize.md)}>
                  Отмена
                </button>
              </div>
            </form>
            <p className={hint}>
              Товар сохранится в активную версию каталога с пометкой «заведён на
              кассе» — директор увидит его в кабинете для ревизии.
            </p>
          </div>
        </div>
      )}

      {/*
        Блокирующий диалог scan-time маркировки (десктоп): касса ждёт DataMatrix
        для добавленных позиций. Коды приходят аппаратным 2D-сканером через шину
        (маршрутизация «очередь → attachMarkToLine»), список тает и окно
        закрывается само. Кнопки «закрыть/пропустить» нет — только лазейки:
        «✕ Убрать из чека» и «Без марки». Окно марок плагина (fallback) важнее,
        поэтому при открытом markModal диалог не показываем.
      */}
      {!isMobile && pendingMarks.length > 0 && !markModal && (
        <div
          className={cx(overlay, 'z-modal')}
          role="dialog"
          aria-modal="true"
          aria-label="Требуется маркировка"
        >
          <div className={modalSm}>
            <h3 className="mt-0 flex flex-wrap items-center gap-2 text-big font-bold"><Marking size={20} /> Требуется маркировка «Честный Знак»</h3>
            <p className={hint}>
              Считайте код DataMatrix сканером. Осталось позиций: {pendingMarks.length}
            </p>
            <ul className="m-0 list-none p-0">
              {pendingMarks.map((lineId) => {
                const line = cart.find((l) => l.lineId === lineId)
                return (
                  <li
                    key={lineId}
                    className="flex items-center gap-2 border-b border-line/60 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-ink">{line?.name ?? lineId}</div>
                      <span className="text-tiny text-muted">Ожидается код DataMatrix…</span>
                    </div>
                    {canSkipMarking && (
                      <button
                        className={cx(btnBase, btn.secondary, btnSize.sm, 'min-h-10')}
                        title="Продать без кода марки (только наличные, по праву)"
                        onClick={() => sellWithoutMark(lineId)}
                      >
                        Без марки
                      </button>
                    )}
                    <button
                      className={cx(btnBase, btn.dangerGhost, 'h-11 w-11 p-0 text-base2')}
                      title="Убрать из чека"
                      onClick={() => dropPendingLine(lineId)}
                    >
                      <Close size={18} />
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Модалка ввода кодов маркировки (Честный Знак) — поверх оплаты (F3) */}
      {markModal && (
        <div className={cx(overlay, 'z-mark')} role="dialog" aria-modal="true" aria-label="Ввод кодов маркировки">
          <div className={modalSm}>
            <h3 className="mt-0 flex flex-wrap items-center gap-2 text-big font-bold"><Marking size={20} /> Маркировка «Честный Знак»</h3>
            <p className={hint}>
              Отсканируйте код маркировки (DataMatrix) для каждой позиции
              {online ? ' (проверяется структура кода)' : ' (оффлайн — код уйдёт в буфер чека)'}.
            </p>
            <form onSubmit={confirmMarks}>
              {markModal.request.items.map((it, idx) => {
                const code = markModal.codes[markKey(it)] ?? ''
                const inspection = code.trim() ? core.marking?.inspect(code) ?? null : null
                return (
                  <label key={markKey(it)} className={fieldLabel}>
                    {it.name}
                    <div className="flex gap-1.5">
                      <input
                        type="text"
                        autoFocus={idx === 0}
                        value={code}
                        onChange={(e) => updateMarkCode(markKey(it), e.target.value)}
                        className={cx(fieldInput, 'flex-1')}
                        placeholder="Код маркировки (DataMatrix)"
                      />
                      {isMobile && (
                        <button
                          type="button"
                          className={cx(btnBase, btn.secondary, 'h-11 w-11 p-0')}
                          onClick={() => openCameraForMark(markKey(it))}
                          title="Сканировать камерой"
                        >
                          <Camera size={20} />
                        </button>
                      )}
                    </div>
                    {inspection && !inspection.acceptable && (
                      <span className="mt-1 block text-tiny font-semibold text-danger">
                        {inspection.reason ?? 'код недействителен'}
                        {inspection.hint ? ` · ${inspection.hint}` : ''}
                      </span>
                    )}
                    {inspection?.acceptable && inspection.note && (
                      <span className="mt-1 block text-tiny text-muted">{inspection.note}</span>
                    )}
                    {(() => {
                      const verified = markStatus[code.trim()]?.status
                      if (!verified) return null
                      const label =
                        verified === 'valid'
                          ? 'Код подтверждён в «Честном Знаке»'
                          : verified === 'invalid'
                            ? 'Код не найден в «Честном Знаке»'
                            : 'Проверить не удалось'
                      const cls =
                        verified === 'valid'
                          ? 'text-okDark'
                          : verified === 'invalid'
                            ? 'text-danger'
                            : 'text-muted'
                      return (
                        <span className={`mt-1 block text-tiny font-semibold ${cls}`}>{label}</span>
                      )
                    })()}
                  </label>
                )
              })}
              <p className={hint}>
                Можно считать аппаратным 2D-сканером — код впишется в первую
                пустую позицию, при заполнении всех марок продажа подтвердится.
              </p>
              <div className="mt-4 flex gap-2">
                <button type="submit" className={cx(btnBase, btn.success, btnSize.md, 'w-full')}>
                  Подтвердить марки
                </button>
                <button type="button" onClick={cancelMarks} className={cx(btnBase, btn.secondary, btnSize.md)}>
                  Отмена (прервать продажу)
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 65.10: проблемы маркировки — убрать позицию или пропустить без кода */}
      {markIssuesModal && (
        <div className={cx(overlay, 'z-mark')} role="dialog" aria-modal="true" aria-label="Проблема с маркировкой">
          <div className={modalSm}>
            <h3 className="mt-0 flex items-center gap-2 text-big font-bold">
              <Marking size={20} /> Проблема с маркировкой
            </h3>
            <ul className="m-0 mb-3 list-none space-y-1.5 p-0">
              {markIssuesModal.issues.map((issue, idx) => (
                <li key={`${issue.lineId ?? issue.name}-${idx}`} className="rounded-md border border-warnLine bg-red-50 px-3 py-2">
                  <div className="font-semibold text-ink">{issue.name}</div>
                  <div className="text-tiny text-dangerDark">{issue.reason}</div>
                </li>
              ))}
            </ul>

            {canSkipMarking ? (
              <>
                <label className={fieldLabel}>
                  Причина
                  <select
                    value={markIssuesModal.reasonCode}
                    onChange={(e) =>
                      setMarkIssuesModal((m) =>
                        m
                          ? {
                              ...m,
                              reasonCode: e.target.value as MarkIssuesModalState['reasonCode']
                            }
                          : m
                      )
                    }
                    className={fieldInput}
                  >
                    <option value="unreadable">Код не читается</option>
                    <option value="not_found">Код не найден в ЧЗ</option>
                    <option value="supplier">Ошибка поставщика</option>
                    <option value="other">Другое</option>
                  </select>
                </label>
                <label className={fieldLabel}>
                  Комментарий (необязательно)
                  <input
                    type="text"
                    value={markIssuesModal.comment}
                    onChange={(e) =>
                      setMarkIssuesModal((m) => (m ? { ...m, comment: e.target.value } : m))
                    }
                    className={fieldInput}
                    placeholder="Например, код повреждён"
                  />
                </label>
                <p className={hint}>
                  Пропуск без кода: чек без ККТ, только наличные. Код останется в обороте «Честного Знака» —
                  ответственность несёт организация.
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={skipMarkingIssueLines}
                    className={cx(btnBase, btn.danger, btnSize.md, 'w-full')}
                  >
                    Пропустить без кода (наличные)
                  </button>
                  <button
                    type="button"
                    onClick={removeMarkingIssueLines}
                    className={cx(btnBase, btn.secondary, btnSize.md, 'w-full')}
                  >
                    Убрать проблемные позиции
                  </button>
                  <button
                    type="button"
                    onClick={() => setMarkIssuesModal(null)}
                    className={cx(btnBase, btn.secondary, btnSize.md, 'w-full')}
                  >
                    Отмена
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className={hint}>
                  Нет права продавать маркированный товар без кода. Уберите позиции или
                  обратитесь к старшему смены.
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={removeMarkingIssueLines}
                    className={cx(btnBase, btn.secondary, btnSize.md, 'w-full')}
                  >
                    Убрать проблемные позиции
                  </button>
                  <button
                    type="button"
                    onClick={() => setMarkIssuesModal(null)}
                    className={cx(btnBase, btn.secondary, btnSize.md, 'w-full')}
                  >
                    Отмена
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Модалка оплаты по СБП (QR + ожидание транзакции) */}
      {sbpModal && (
        <div className={cx(overlay, 'z-modal')} role="dialog" aria-modal="true" aria-label="Оплата по СБП">
          <div className={cx(modalSm, 'text-center')}>
            <h3 className="mt-0 text-big font-bold">Оплата через СБП</h3>

            {sbpModal.phase === 'waiting' ? (
              <>
                <p className="my-1">
                  Покупатель, отсканируйте QR-код для оплаты через СБП.
                </p>
                <p className="my-1 text-huge font-bold">
                  Сумма: {formatCurrency(sbpModal.request.amount, currency)}
                </p>
                <div className={qrBox} aria-label="QR-код для оплаты через СБП">
                  {sbpModal.qrSvg ? (
                    <div
                      className={qrSvg}
                      dangerouslySetInnerHTML={{ __html: sbpModal.qrSvg }}
                    />
                  ) : (
                    <code className={qrPayload}>{sbpModal.qr.payload}</code>
                  )}
                </div>
                <p className={hint}>
                  Ожидаем подтверждение транзакции от банка (WebSocket NewPay)…
                </p>
                <p className="flex items-center justify-center gap-1.5 font-semibold text-blue-600"><Clock size={18} /> Ожидание оплаты…</p>
                <button type="button" onClick={cancelSbp} className={cx(btnBase, btn.secondary, btnSize.md)}>
                  Отменить оплату
                </button>
              </>
            ) : (
              <p className="text-xl font-bold text-okDark">
                <Check size={18} className="text-okDark" /> Транзакция одобрена
              </p>
            )}
          </div>
        </div>
      )}

      {/* Модалка выбора способа оплаты */}
      {payModal && (
        <div className={cx(overlay, 'z-modal')} role="dialog" aria-modal="true" aria-label="Оплата чека">
          <div className={modalSm}>
            <h3 className="mt-0 text-big font-bold">
              {returnMode ? 'Возврат на сумму ' : 'Оплата чека на сумму '}
              {formatCurrency(total, currency)}
            </h3>

            {payModal.stage === 'choose' && (
              <div className="flex flex-col gap-2.5">
                <button
                  className={cx(btnBase, btn.secondary, btnSize.lg, 'w-full justify-start')}
                  onClick={() => {
                    // Возврат наличными — без экрана «получено/сдача»: деньги
                    // выдаём из кассы, сумма фиксирована.
                    if (returnMode) {
                      setPayModal(null)
                      void pay('cash')
                    } else {
                      setPayModal({ stage: 'cash' })
                    }
                  }}
                >
                  <Cash size={20} /> {returnMode ? 'Вернуть наличными' : 'Наличные'}
                </button>
                {!cashOnly && core.payments.supports(PAYMENT_METHODS.sbp) && (
                  <button
                    className={cx(btnBase, btn.secondary, btnSize.lg, 'w-full justify-start')}
                    onClick={() => {
                      setPayModal(null)
                      void pay('sbp')
                    }}
                  >
                    <Qr size={20} /> {returnMode ? 'Возврат по СБП' : 'СБП / QR'}
                  </button>
                )}
                {/* Банковская карта — только на стационарном POS. */}
                {!cashOnly && !isMobile && (
                  <button className={cx(btnBase, btn.secondary, btnSize.lg, 'w-full justify-start')} onClick={chooseCard}>
                    <Card size={20} /> {returnMode ? 'Возврат на карту' : 'Банковская карта'}
                  </button>
                )}
                {cashOnly ? (
                  <p className="m-0 inline-flex items-center gap-1.5 text-tiny font-semibold text-dangerDark">
                    <Check size={16} />{' '}
                    {returnNonFiscal
                      ? 'Возврат по продаже без ККТ — только наличные'
                      : 'Товар без кода — только наличные, чек без ККТ'}
                  </p>
                ) : (
                  <p className="m-0 inline-flex items-center gap-1.5 text-tiny text-muted">
                    <Check size={16} /> Карта и СБП фискализируются всегда
                  </p>
                )}
                <button onClick={() => setPayModal(null)} className={cx(btnBase, btn.secondary, btnSize.md)}>Отмена</button>
              </div>
            )}

            {payModal.stage === 'cash' && (
              <div>
                {/* Строка введённой суммы */}
                <div className={amountDisplay}>{formatCurrency(Number(cashReceived.replace(',', '.')) || 0, currency)}</div>
                {/* Наличные — единственный способ, где галочка управляема. */}
                {renderFiscalToggle()}

                {(() => {
                  const received = Number(cashReceived.replace(',', '.')) || 0
                  const change = received - total
                  return (
                    <div
                      className={cx(
                        'my-2 text-center text-[26px] font-extrabold tabular-nums',
                        change < 0 ? 'text-danger' : 'text-okDark'
                      )}
                    >
                      {change < 0
                        ? `Не хватает: ${formatCurrency(-change, currency)}`
                        : `Сдача: ${formatCurrency(change, currency)}`}
                    </div>
                  )
                })()}

                {/* Быстрые банкноты */}
                <div className="my-2 flex flex-wrap gap-2">
                  <button className={cx(btnBase, btn.secondary, btnSize.sm)} onClick={() => setCashReceived(String(total))}>Ровно</button>
                  <button className={cx(btnBase, btn.secondary, btnSize.sm)} onClick={() => addDenomination(500)}>+500</button>
                  <button className={cx(btnBase, btn.secondary, btnSize.sm)} onClick={() => addDenomination(1000)}>+1000</button>
                  <button className={cx(btnBase, btn.secondary, btnSize.sm)} onClick={() => addDenomination(5000)}>+5000</button>
                </div>

                {/* Сенсорный numpad 4x3: «Стереть» — в сетке, не отдельной полосой */}
                <div className="grid grid-cols-4 gap-2">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                    <button key={d} className="h-12 rounded-card border border-lineStrong bg-soft text-big font-bold" onClick={() => numpadPress(d)}>
                      {d}
                    </button>
                  ))}
                  <button className="h-12 rounded-card border border-lineStrong bg-soft text-big font-bold" onClick={() => numpadPress('0')}>
                    0
                  </button>
                  <button className="h-12 rounded-card border border-lineStrong bg-soft text-big font-bold" onClick={() => numpadPress('00')}>
                    00
                  </button>
                  <button
                    className="h-12 rounded-card border border-lineStrong bg-soft text-big font-bold"
                    onClick={numpadBackspace}
                    title="Стереть"
                    aria-label="Стереть"
                  >
                    ⌫
                  </button>
                </div>
                <button
                  className="mb-2 mt-2 w-full rounded-card border border-warnLine bg-red-50 py-2 text-small font-semibold text-danger"
                  onClick={numpadClear}
                >
                  Очистить
                </button>

                <div className="flex gap-2">
                  <button
                    className={cx(btnBase, btn.success, btnSize.md, 'w-full')}
                    disabled={(Number(cashReceived.replace(',', '.')) || 0) < total || paying}
                    onClick={() => void pay('cash')}
                  >
                    Подтвердить и закрыть чек
                  </button>
                  <button onClick={() => setPayModal({ stage: 'choose' })} className={cx(btnBase, btn.secondary, btnSize.md)}>Назад</button>
                </div>
                <p className={hint}>Можно вводить с клавиатуры ПК · Enter — закрыть чек · Esc — назад</p>
              </div>
            )}

            {payModal.stage === 'card' && (
              <div className="text-center">
                <p className="my-2 text-huge font-bold tabular-nums">
                  К оплате: {formatCurrency(total, currency)}
                </p>
                <p className={hint}>Проведите оплату на банковском терминале.</p>
                <div className="mt-3 flex gap-2">
                  <button className={cx(btnBase, btn.success, btnSize.md, 'w-full')} disabled={paying} onClick={() => void pay('card')}>
                    <Check size={18} /> Оплата подтверждена
                  </button>
                  <button onClick={() => setPayModal({ stage: 'choose' })} className={cx(btnBase, btn.secondary, btnSize.md)}>Назад</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Модалка открытия смены */}
      {openShiftForm && (
        <div className={cx(overlay, 'z-modal')} role="dialog" aria-modal="true" aria-label="Открыть смену">
          <div className={modalSm}>
            <h3 className="mt-0 text-big font-bold">Открытие кассовой смены</h3>
            <label className={fieldLabel}>
              Размен в кассе (наличные)
              <input
                type="number"
                min={0}
                step="0.01"
                value={openShiftForm.initialCash}
                onChange={(e) =>
                  setOpenShiftForm((f) => (f ? { ...f, initialCash: e.target.value } : f))
                }
                className={fieldInput}
                placeholder="0.00"
              />
            </label>
            <label className="my-2 mb-4 flex items-center gap-2">
              <input
                type="checkbox"
                checked={openShiftForm.fiscal}
                onChange={(e) =>
                  setOpenShiftForm((f) => (f ? { ...f, fiscal: e.target.checked } : f))
                }
               className="h-4 w-4 cursor-pointer accent-ok"/>
              <span>Фискализация по умолчанию включена</span>
            </label>
            <div className="flex gap-2">
              <button className={cx(btnBase, btn.success, btnSize.md, 'w-full')} onClick={() => void confirmOpenShift()}>
                Открыть смену
              </button>
              <button onClick={() => setOpenShiftForm(null)} className={cx(btnBase, btn.secondary, btnSize.md)}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Чек коррекции возврата прихода по нефискальной продаже (65.6). */}
      {correctionModal && (
        <div
          className={cx(overlay, 'z-modal')}
          role="dialog"
          aria-modal="true"
          aria-label="Чек коррекции"
        >
          <div className={modalLg}>
            <h3 className="mt-0 text-big font-bold">Чек коррекции: возврат прихода</h3>
            <p className={hint}>
              Чек {shortId(correctionModal.receipt.receiptId)} ·{' '}
              {formatCurrency(correctionModal.receipt.total, currency)} — продажа без
              фискального чека. По 54-ФЗ возврат оформляется чеком коррекции.
            </p>
            {correctionModal.error && (
              <div className="mb-2 rounded-card border border-warnLine bg-red-50 px-3 py-2 text-small text-dangerDark">
                {correctionModal.error}
              </div>
            )}

            {/* Состав: какие позиции и сколько возвращаем (65.6.2). */}
            <div className="mb-2 max-h-52 overflow-y-auto rounded-card border border-line">
              <table className="w-full text-small">
                <thead className="sticky top-0 bg-surface text-tiny text-muted">
                  <tr>
                    <th className="w-8 px-2 py-1" />
                    <th className="px-2 py-1 text-left">Позиция</th>
                    <th className="px-2 py-1 text-right">Продано</th>
                    <th className="px-2 py-1 text-right">Доступно</th>
                    <th className="px-2 py-1 text-right">К возврату</th>
                    <th className="px-2 py-1 text-right">Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {correctionModal.items.map((line) => {
                    const qty = Number(line.qty.replace(',', '.'))
                    const lineSum = Number.isFinite(qty) && qty > 0 ? line.price * qty : 0
                    return (
                      <tr key={line.lineId} className="border-t border-line/60">
                        <td className="px-2 py-1">
                          <input
                            type="checkbox"
                            className="h-4 w-4 cursor-pointer accent-ok"
                            checked={line.selected}
                            disabled={line.availableQty <= 0}
                            onChange={(e) =>
                              changeCorrectionLine(line.lineId, { selected: e.target.checked })
                            }
                            aria-label={`Выбрать ${line.name}`}
                          />
                        </td>
                        <td className="max-w-[180px] truncate px-2 py-1" title={line.name}>
                          {line.name}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums text-muted">
                          {line.unit === 'кг' ? formatKg(line.soldQty) : line.soldQty}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums text-muted">
                          {line.unit === 'кг' ? formatKg(line.availableQty) : line.availableQty}
                        </td>
                        <td className="px-2 py-1 text-right">
                          <input
                            className="w-16 rounded-md border border-lineStrong px-1.5 py-1 text-right tabular-nums"
                            value={line.qty}
                            inputMode="decimal"
                            disabled={!line.selected || line.availableQty <= 0}
                            onChange={(e) => changeCorrectionLine(line.lineId, { qty: e.target.value })}
                            aria-label={`Количество ${line.name}`}
                          />
                        </td>
                        <td className="whitespace-nowrap px-2 py-1 text-right font-semibold tabular-nums">
                          {formatCurrency(lineSum, currency)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-small">
              <span className="text-muted">
                По составу:{' '}
                <strong className="tabular-nums text-ink">
                  {formatCurrency(correctionComposition, currency)}
                </strong>
              </span>
              <span className="text-muted">
                Доступно по деньгам:{' '}
                <strong className="tabular-nums text-ink">
                  {formatCurrency(correctionModal.remaining, currency)}
                </strong>
              </span>
            </div>
            {(() => {
              const totalNumber = Number(correctionModal.total.replace(',', '.'))
              const diff = Number.isFinite(totalNumber) ? totalNumber - correctionComposition : 0
              if (correctionComposition <= 0) {
                return (
                  <p className="mb-2 text-tiny text-amber-700">
                    Состав не выбран — будет оформлена коррекция расчёта без номенклатуры.
                  </p>
                )
              }
              if (Math.abs(diff) > 0.01) {
                return (
                  <p className="mb-2 text-tiny text-amber-700">
                    Сумма отличается от состава на {formatCurrency(diff, currency)} — проверьте.
                  </p>
                )
              }
              return null
            })()}

            <div className="flex flex-col gap-2.5">
              <label className={fieldLabel}>
                Сумма возврата (можно скорректировать вручную)
                <input
                  className={fieldInput}
                  value={correctionModal.total}
                  inputMode="decimal"
                  onChange={(e) =>
                    setCorrectionModal((m) =>
                      m ? { ...m, total: e.target.value, error: undefined } : m
                    )
                  }
                />
              </label>
              <label className={fieldLabel}>
                Основание
                <input
                  className={fieldInput}
                  value={correctionModal.basis}
                  onChange={(e) =>
                    setCorrectionModal((m) => (m ? { ...m, basis: e.target.value } : m))
                  }
                />
              </label>
              <label className={fieldLabel}>
                Дата документа
                <input
                  className={fieldInput}
                  type="date"
                  value={correctionModal.baseDate}
                  onChange={(e) =>
                    setCorrectionModal((m) => (m ? { ...m, baseDate: e.target.value } : m))
                  }
                />
              </label>
              <label className={fieldLabel}>
                Номер документа
                <input
                  className={fieldInput}
                  value={correctionModal.baseNumber}
                  onChange={(e) =>
                    setCorrectionModal((m) => (m ? { ...m, baseNumber: e.target.value } : m))
                  }
                />
              </label>
              <button
                className={cx(btnBase, btn.danger, btnSize.lg, 'w-full')}
                onClick={() => void submitCorrection()}
                disabled={paying}
              >
                {paying ? 'ОФОРМЛЕНИЕ…' : 'ПРОБИТЬ ЧЕК КОРРЕКЦИИ'}
              </button>
              <button
                className={cx(btnBase, btn.secondary, btnSize.md)}
                onClick={() => setCorrectionModal(null)}
                disabled={paying}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Подтверждение оформленной коррекции (65.6.2). */}
      {correctionSuccess && (
        <div
          className={cx(overlay, 'z-modal')}
          role="dialog"
          aria-modal="true"
          aria-label="Чек коррекции оформлен"
          onClick={() => setCorrectionSuccess(null)}
        >
          <div className={cx(modalSm, 'text-center')} onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-dangerDark">
              <Check size={24} />
            </div>
            <h3 className="my-2 text-big font-bold">Чек коррекции оформлен</h3>
            <div className="my-1 text-xl font-bold tabular-nums text-dangerDark">
              −{formatCurrency(correctionSuccess.total, currency)} возвращено
            </div>
            {correctionSuccess.itemsCount > 0 && (
              <div className={hint}>Позиций в составе: {correctionSuccess.itemsCount}</div>
            )}
            {correctionSuccess.status === 'fiscalized' ? (
              <div className="mt-1.5 flex items-center justify-center gap-1.5 text-tiny font-semibold text-okDark">
                <Check size={16} /> Фискализирован
                {correctionSuccess.fiscalData
                  ? `: ФД №${correctionSuccess.fiscalData.fiscalDocumentNumber} · ФПД ${correctionSuccess.fiscalData.fiscalDocumentSign}`
                  : ''}
              </div>
            ) : (
              <div className="mt-2 flex items-start gap-1.5 rounded-card border border-amber-300 bg-amber-50 px-3 py-2 text-tiny font-semibold text-amber-800">
                <Clock size={16} className="mt-0.5 shrink-0" />
                <span>
                  <strong>Не пробит на ККТ: {kktUnavailableReason(correctionSuccess.kktState)}.</strong>{' '}
                  Пробьём автоматически, когда ККТ станет доступна.
                  <span className="mt-1 block font-normal text-amber-700">
                    Документ сохранён и учтён в смене.
                  </span>
                </span>
              </div>
            )}
            <button
              className={cx(btnBase, btn.secondary, btnSize.md, 'mt-3 w-full')}
              onClick={() => setCorrectionSuccess(null)}
            >
              Готово
            </button>
          </div>
        </div>
      )}

      {/* Модалка Z-отчёта (после закрытия смены) */}
      {zReport && (
        <div className={cx(overlay, 'z-modal')} role="dialog" aria-modal="true" aria-label="Z-отчёт">
          <div className={modalLg}>
            {/* Печатаемый блок (чековая лента). */}
            <div id="z-report-print" className="whitespace-pre-wrap font-mono text-small text-ink">
              <div className="text-center font-bold">
                === Z-ОТЧЁТ СМЕНА №{zReport.shift.shiftNumber} ===
              </div>
              <div className="my-0.5">Кассир: {zReport.shift.cashierName}</div>
              <div className="my-0.5">
                Открыта: {new Date(zReport.shift.openedAt).toLocaleString('ru-RU')}
              </div>
              <div className="my-0.5">
                Закрыта:{' '}
                {zReport.shift.closedAt
                  ? new Date(zReport.shift.closedAt).toLocaleString('ru-RU')
                  : '—'}
              </div>
              <div className="my-1 text-muted">--------------------------------</div>
              {zReport.full ? (
                <>
                  <div className="my-0.5">Рабочее место: {shortId(zReport.full.zone_id)}</div>
                  <div className="my-0.5">
                    Чеков за смену (зона): {zReport.full.sales_count}
                  </div>
                  {(zReport.full.returns_count ?? 0) > 0 && (
                    <div className="my-0.5">
                      Возвраты: {zReport.full.returns_count} на{' '}
                      {formatCurrency(zReport.full.returns_total ?? 0, currency)}
                    </div>
                  )}
                  <div className="my-0.5">
                    Фискальная выручка: {formatCurrency(zReport.full.fiscal_total, currency)}
                  </div>
                  <div className="my-0.5">
                    Отложенные (ждут ККТ):{' '}
                    {formatCurrency(zReport.full.deferred_total, currency)}
                  </div>
                  <div className="my-0.5">
                    Нефискальные (намеренно):{' '}
                    {formatCurrency(zReport.full.non_fiscal_total, currency)}
                  </div>
                  {(zReport.full.held_count ?? 0) > 0 && (
                    <div className="my-0.5 text-dangerDark">
                      Ждут решения по маркировке: {zReport.full.held_count} на{' '}
                      {formatCurrency(zReport.full.held_total ?? 0, currency)}
                    </div>
                  )}
                  {(zReport.full.valid_ready_count ?? 0) > 0 && (
                    <div className="my-0.5">
                      Готовы к печати: {zReport.full.valid_ready_count}
                    </div>
                  )}
                  {(zReport.full.print_failures_count ?? 0) > 0 && (
                    <div className="my-0.5 text-dangerDark">
                      Ошибки печати: {zReport.full.print_failures_count}
                    </div>
                  )}
                  {(zReport.full.cancelled_count ?? 0) > 0 && (
                    <div className="my-0.5">
                      Hold отменены: {zReport.full.cancelled_count} на{' '}
                      {formatCurrency(zReport.full.cancelled_total ?? 0, currency)}
                    </div>
                  )}
                  {(zReport.full.corrections_count ?? 0) > 0 && (
                    <div className="my-0.5">
                      Коррекции прихода: {zReport.full.corrections_income_count ?? 0} на{' '}
                      {formatCurrency(zReport.full.corrections_income_total ?? 0, currency)}
                    </div>
                  )}
                  {(zReport.full.corrections_returns_count ?? 0) > 0 && (
                    <div className="my-0.5 text-dangerDark">
                      Коррекции-возвраты: {zReport.full.corrections_returns_count}{' '}
                      на {formatCurrency(zReport.full.corrections_returns_total ?? 0, currency)}
                    </div>
                  )}
                  <div className="my-1 text-muted">--------------------------------</div>
                  <div className="my-0.5 text-base2 font-bold">
                    ИТОГО ПО ЯЩИКУ: {formatCurrency(zReport.full.total_in_drawer, currency)}
                  </div>
                  {(zReport.full.corrections_count ?? 0) > 0 &&
                    zReport.full.total_with_corrections != null && (
                      <div className="my-0.5 font-semibold">
                        Итого с коррекциями:{' '}
                        {formatCurrency(zReport.full.total_with_corrections, currency)}
                      </div>
                    )}
                  <div className="my-1 text-muted">--------------------------------</div>
                  <div className="my-0.5">По устройствам:</div>
                  {zReport.full.by_device.map((d) => (
                    <div key={d.device_id} className="my-0.5">
                      {deviceLabel(d.device_id).label}: {formatCurrency(Number(d.total), currency)} ({d.count})
                    </div>
                  ))}
                  {zReport.full.by_cashier.length > 0 && (
                    <>
                      <div className="my-0.5">По кассирам:</div>
                      {zReport.full.by_cashier.map((c, i) => (
                        <div key={`${c.cashier_name ?? 'none'}-${i}`} className="my-0.5">
                          {c.cashier_name || 'Без кассира'}:{' '}
                          {formatCurrency(Number(c.total), currency)} ({c.count})
                        </div>
                      ))}
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="my-0.5 flex items-start gap-1.5 text-warnFg">
                    <Alert size={16} className="mt-0.5 shrink-0" /> Неполный Z (касса офлайн) — итог по смене появится в облаке.
                  </div>
                  <div className="my-0.5">Чеков за смену (эта касса): {zReport.salesCount}</div>
                  {(zReport.returnsCount ?? 0) > 0 && (
                    <div className="my-0.5">
                      Возвраты: {zReport.returnsCount} на{' '}
                      {formatCurrency(zReport.returnsTotal ?? 0, currency)}
                    </div>
                  )}
                  <div className="my-0.5">
                    Наличные: {formatCurrency(zReport.cashTotal, currency)}
                  </div>
                  <div className="my-0.5">Карта: {formatCurrency(zReport.cardTotal, currency)}</div>
                  <div className="my-0.5">
                    СБП/безнал: {formatCurrency(zReport.sbpTotal, currency)}
                  </div>
                  <div className="my-1 text-muted">--------------------------------</div>
                  <div className="my-0.5 text-base2 font-bold">
                    ИТОГО (эта касса): {formatCurrency(zReport.totalRevenue, currency)}
                  </div>
                </>
              )}
            </div>

            <div className="z-report-actions mt-4 flex gap-2">
              <button className={cx(btnBase, btn.success, btnSize.md, 'w-full')} onClick={printZReport}>
                <Printer size={18} /> Распечатать Z-отчёт
              </button>
              <button className={cx(btnBase, btn.secondary, btnSize.md)} onClick={() => setZReport(null)}>
                Готово
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Сканер камерой смартфона */}
      <CameraScannerModal
        isOpen={cameraOpen}
        onClose={() => {
          setCameraOpen(false)
          setCameraMarkTarget(null)
        }}
        onScan={async (text) => {
          // 1. Открытие тары: код DataMatrix головы → в форму вскрытия + lookup.
          if (containerModal?.tab === 'new') {
            void applyContainerDatamatrix(text)
            setCameraOpen(false)
            return { found: true, name: 'Код тары принят', barcode: text }
          }
          // 2. Модалка «на лету» — скан в поле «Штрихкод» (F6).
          if (createModalOpenRef.current) {
            createModalBarcodeRef.current(text)
            setCameraOpen(false)
            return { found: true, name: 'Штрихкод принят', barcode: text }
          }
          // 3. Модалка маркировки (Честный Знак): код в поле позиции (F5).
          if ((cameraMarkTarget || markModalRef.current) && (core.marking?.looksLike(text) ?? false)) {
            routeCodeToMarkModal(text, cameraMarkTarget ?? undefined)
            setCameraMarkTarget(null)
            setCameraOpen(false)
            return { found: true, name: 'Код марки принят', barcode: text }
          }
          // 4. Scan-time marking: очередь ожидания марок (F4/F5) — адресуем по lineId.
          if (scanMarkQueueRef.current.length > 0 && (core.marking?.looksLike(text) ?? false)) {
            const lineId = takeMarkTarget()
            if (lineId) {
              attachMarkToLine(lineId, text)
              setCameraOpen(false)
              beepMark()
              return { found: true, name: 'Код марки привязан', barcode: text }
            }
            // Цель не нашлась (всё уже промаркировано) — код марки не отправляем
            // в поиск товара, просто игнорируем.
            setCameraOpen(false)
            return { found: false, barcode: text, canCreate: false }
          }
          return handleBarcode(text)
        }}
        // B2: подсказка под задачу съёмки — маркировка ЧЗ требует DataMatrix.
        prompt={
          pendingMarks.length > 0 || cameraMarkTarget
            ? 'Отсканируйте код маркировки «Честный Знак»'
            : undefined
        }
        subtitle={
          cameraMarkTarget ??
          (pendingMarks[0] ? cart.find((l) => l.lineId === pendingMarks[0])?.name : undefined)
        }
        onChangeItemQty={onChangeItemQty}
        czEnabled={czEnabled}
        onCreateProduct={async (data) => {
          const product = await catalog.createOnPos({
            ...data,
            createdByName: cashier?.name ?? cashierName
          })
          addProductToCart(product)
          reloadCategories()
          reloadSubcategories(selectedCategory)
          reloadGrid(manualCode, selectedCategory, selectedSubcategory)
        }}
      />

      {/* Редкие действия: не занимают строку смены рядом с оплатой. */}
      {moreActionsOpen && (
        <div
          className={cx(overlay, 'z-modal')}
          role="dialog"
          aria-modal="true"
          aria-label="Ещё действия"
        >
          <div className={modalSm}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="m-0 text-big font-bold text-ink">Ещё действия</h3>
              <button
                className={cx(btnBase, btn.ghost, btnSize.sm)}
                onClick={() => setMoreActionsOpen(false)}
                aria-label="Закрыть"
              >
                <Close size={18} />
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {canCreateOnPos && (
                <button
                  className={cx(btnBase, btn.secondary, btnSize.lg, 'justify-start')}
                  onClick={() => {
                    setMoreActionsOpen(false)
                    openContainerModal()
                  }}
                >
                  <Box size={18} /> Тары ({activeContainerCount})
                </button>
              )}
              {canRefund && !returnMode && (
                <button
                  className={cx(btnBase, btn.softDanger, btnSize.lg, 'justify-start')}
                  disabled={paying}
                  onClick={() => {
                    setMoreActionsOpen(false)
                    enterReturnMode()
                  }}
                >
                  Возврат по чеку
                </button>
              )}
              {returnMode && (
                <button
                  className={cx(btnBase, btn.secondary, btnSize.lg, 'justify-start')}
                  disabled={paying}
                  onClick={() => {
                    setMoreActionsOpen(false)
                    exitReturnMode()
                  }}
                >
                  Выйти из возврата
                </button>
              )}
              {returnSource && (
                <button
                  className={cx(btnBase, btn.secondary, btnSize.lg, 'justify-start')}
                  disabled={paying}
                  onClick={() => {
                    setMoreActionsOpen(false)
                    openCorrectionModal(returnSource)
                  }}
                >
                  Коррекция выбранного чека
                </button>
              )}
              <button
                className={cx(btnBase, btn.secondary, btnSize.lg, 'justify-start')}
                onClick={() => {
                  setMoreActionsOpen(false)
                  void printXReport()
                }}
              >
                X-отчёт
              </button>
            </div>
            <p className="mt-3 text-tiny text-muted">
              Z-отчёт снимается при закрытии смены. X-отчёт — промежуточный, без закрытия.
            </p>
          </div>
        </div>
      )}

      {/* Подтверждение действия кассира PIN-кодом администратора. */}
      <PinPadModal
        isOpen={adminConfirm != null}
        onCancel={() => setAdminConfirm(null)}
        onSuccess={(confirmed) => {
          const pending = adminConfirm
          setAdminConfirm(null)
          if (!pending) return
          if (canOpenAdmin(confirmed)) {
            pending.run()
          } else {
            showToast({ kind: 'error', text: 'Нужен PIN администратора' })
          }
        }}
      />

      {/* Модалка тар: «вскрыть новую» + «открытые тары» (пул массы). */}
      {containerModal && (
        <div className={cx(overlay, 'z-modal')} role="dialog" aria-modal="true" aria-label="Тары">
          <div className={modalLg}>
            <h3 className="mt-0 flex flex-wrap items-center gap-2 text-big font-bold text-ink"><Box size={20} /> Тары (маркированные весовые)</h3>

            <div className="mb-3 flex flex-wrap gap-2">
              <button
                className={cx(
                  btnBase,
                  btnSize.sm,
                  containerModal.tab === 'new'
                    ? 'border-ink bg-ink text-white'
                    : 'border-lineStrong bg-white text-ink'
                )}
                onClick={() => setContainerModal((m) => (m ? { ...m, tab: 'new' } : m))}
              >
                + Вскрыть новую тару
              </button>
              <button
                className={cx(
                  btnBase,
                  btnSize.sm,
                  containerModal.tab === 'list'
                    ? 'border-ink bg-ink text-white'
                    : 'border-lineStrong bg-white text-ink'
                )}
                onClick={() => {
                  setContainerModal((m) => (m ? { ...m, tab: 'list' } : m))
                  void reloadOpenedContainers()
                }}
              >
                Открытые тары ({activeContainerCount})
              </button>
            </div>

            {containerModal.tab === 'new' && (
              <>
                <p className={hint}>
                  Отсканируйте код DataMatrix с заводской упаковки сыра или колбасы.
                  Товар определится автоматически. Укажите вес — нарезка будет
                  продаваться без запроса маркировки.
                </p>

            {/* 1. DataMatrix головы — первым, автофокус для аппаратного 2D-сканера. */}
            <label className={fieldLabel}>
              Код DataMatrix головы
              <div className="flex gap-1.5">
                <input
                  type="text"
                  autoFocus
                  value={containerModal.datamatrix}
                  placeholder="Отсканируйте код марки"
                  onChange={(e) => void applyContainerDatamatrix(e.target.value)}
                  className={cx(fieldInput, 'flex-1')}
                />
                {/* Камера — только на мобиле; на стационаре 2D-сканер (F6). */}
                {isMobile && (
                  <button
                    type="button"
                    className={cx(btnBase, btn.secondary, 'h-11 w-11 p-0')}
                    onClick={() => setCameraOpen(true)}
                    title="Сканировать камерой"
                  >
                    <Camera size={20} />
                  </button>
                )}
              </div>
            </label>

            {/* 2. Распознанный товар — зелёная плашка; иначе ручной поиск. */}
            {containerModal.product ? (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-card border border-emerald-200 bg-emerald-50 px-3 py-2 text-small text-emerald-900">
                <span className="inline-flex items-center gap-1.5">
                  <Check size={16} className="shrink-0 text-okDark" /> Распознан: <strong>{containerModal.product.name}</strong> (
                  {formatCurrency(containerModal.product.price, currency)}/кг)
                </span>
                <button
                  type="button"
                  className={cx(btnBase, btn.secondary, btnSize.sm)}
                  onClick={() =>
                    setContainerModal((m) => (m ? { ...m, product: null, search: '' } : m))
                  }
                >
                  Изменить
                </button>
              </div>
            ) : (
              <>
                <label className={fieldLabel}>
                  Товар (ручной поиск)
                  <input
                    type="text"
                    value={containerModal.search}
                    placeholder="Поиск по наименованию…"
                    onChange={(e) => {
                      const q = e.target.value
                      setContainerModal((m) => (m ? { ...m, search: q, product: null } : m))
                      void catalog.searchProducts(q).then((list) => {
                        setContainerCandidates(list.filter((p) => p.unit === 'кг' || p.isWeight))
                      })
                    }}
                    className={fieldInput}
                  />
                </label>
                {containerCandidates.length > 0 && (
                  <div className="mb-3 max-h-52 overflow-y-auto rounded-card border border-line bg-white">
                    {containerCandidates.slice(0, 8).map((p) => (
                      <button
                        key={p.id}
                        className="block w-full border-b border-line/60 bg-white px-3 py-2 text-left text-small text-ink hover:bg-soft"
                        onClick={() =>
                          setContainerModal((m) => (m ? { ...m, product: p, search: p.name } : m))
                        }
                      >
                        {p.name} · {formatCurrency(p.price, currency)}/кг
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* 3. Вес головки: readOnly + только наш экранный numpad. */}
            <label className={fieldLabel}>
              Вес головки, кг
              <input
                type="text"
                readOnly
                inputMode="decimal"
                data-container-field="weight"
                value={containerModal.weight}
                placeholder="0.000"
                onFocus={() => setContainerNumpadOpen(true)}
                className={fieldInput}
              />
            </label>

            {containerNumpadOpen && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                    <button key={d} className="h-12 rounded-card border border-lineStrong bg-soft text-big font-bold" onClick={() => containerWeightPress(d)}>
                      {d}
                    </button>
                  ))}
                  <button
                    className="h-12 rounded-card border border-lineStrong bg-soft text-big font-bold text-danger"
                    onClick={containerWeightClear}
                  >
                    C
                  </button>
                  <button className="h-12 rounded-card border border-lineStrong bg-soft text-big font-bold" onClick={() => containerWeightPress('0')}>
                    0
                  </button>
                  <button className="h-12 rounded-card border border-lineStrong bg-soft text-big font-bold" onClick={() => containerWeightPress('.')}>
                    .
                  </button>
                </div>
                <button
                  className="mb-2 w-full rounded-card border border-lineStrong bg-soft py-2 text-small font-semibold"
                  onClick={containerWeightBackspace}
                >
                  ⌫ Стереть
                </button>
              </>
            )}

            <div className="mt-3 flex gap-2">
              <button className={cx(btnBase, btn.success, btnSize.md, 'flex-1')} onClick={() => void confirmContainerOpen()}>
                Подтвердить вскрытие
              </button>
              <button className={cx(btnBase, btn.secondary, btnSize.md)} onClick={() => setContainerModal(null)}>
                Отмена
              </button>
            </div>
              </>
            )}

            {containerModal.tab === 'list' && (
              <>
                {(() => {
                  const active = openedContainers.filter((c) => c.status === 'active')
                  if (active.length === 0) {
                    return (
                      <p className={hint}>
                        Открытых тар нет. Вскройте головку на вкладке «+ Вскрыть новую тару».
                      </p>
                    )
                  }
                  return (
                    <div className="flex flex-col gap-2">
                      {active.map((c) => {
                        const ratio =
                          c.initialWeight > 0 ? c.remainingWeight / c.initialWeight : 0
                        const low = ratio < 0.15
                        const stale = Date.now() - c.openedAt > SHELF_LIFE_MS
                        return (
                          <div
                            key={c.id}
                            className={cx(
                              'rounded-card border bg-white p-3',
                              stale
                                ? 'border-red-300'
                                : low
                                  ? 'border-amber-300'
                                  : 'border-line'
                            )}
                          >
                            <div className="flex justify-between gap-2">
                              <strong>{c.name}</strong>
                              <span className="whitespace-nowrap text-tiny text-muted">
                                {formatRelative(c.openedAt)}
                              </span>
                            </div>
                            <div className="my-1 text-small text-inkSoft">
                              {formatKg(c.remainingWeight)} / {formatKg(c.initialWeight)} кг ·{' '}
                              вскрыта {new Date(c.openedAt).toLocaleString('ru-RU')}
                            </div>
                            <div className="h-2 overflow-hidden rounded-pill bg-soft">
                              <div
                                className={cx('h-full', low ? 'bg-amber-500' : 'bg-ok')}
                                style={{ width: `${Math.max(0, Math.min(100, ratio * 100))}%` }}
                              />
                            </div>
                            {stale ? (
                              <div className="mt-2 flex items-center gap-1.5 rounded-card border border-red-200 bg-red-50 px-3 py-2 text-small text-dangerDark">
                                <Alert size={16} /> Просрочена — изъять (старше 72 ч)
                              </div>
                            ) : low ? (
                              <div className="mt-2 rounded-card border border-amber-300 bg-amber-50 px-3 py-2 text-small text-warnFg">
                                Скоро закончится
                              </div>
                            ) : null}

                            {containerEdit && containerEdit.id === c.id ? (
                              <div className="mt-2 flex items-center gap-1.5">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={containerEdit.value}
                                  onChange={(e) =>
                                    setContainerEdit((s) =>
                                      s ? { ...s, value: e.target.value } : s
                                    )
                                  }
                                  className={cx(fieldInput, 'w-28')}
                                />
                                <span className="text-tiny text-muted">кг</span>
                                <button
                                  type="button"
                                  className={cx(btnBase, btn.secondary, btnSize.sm)}
                                  onClick={() => void saveContainerEdit()}
                                >
                                  Сохранить
                                </button>
                                <button
                                  type="button"
                                  className={cx(btnBase, btn.ghost, btnSize.sm)}
                                  onClick={() => setContainerEdit(null)}
                                >
                                  Отмена
                                </button>
                              </div>
                            ) : (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                <button
                                  type="button"
                                  className={cx(btnBase, btn.secondary, btnSize.sm)}
                                  onClick={() =>
                                    setContainerEdit({
                                      id: c.id as number,
                                      value: String(c.remainingWeight)
                                    })
                                  }
                                >
                                  <Pencil size={16} /> Дописать вес
                                </button>
                                <button
                                  type="button"
                                  className={cx(btnBase, btn.softDanger, btnSize.sm)}
                                  onClick={() => void closeContainer(c.id as number)}
                                >
                                  <Trash size={16} /> Закрыть тару
                                </button>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}
                <div className="mt-3">
                  <button onClick={() => setContainerModal(null)} className={cx(btnBase, btn.secondary, btnSize.md)}>Готово</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 65.14: модалка hold-чека — печать после valid или отмена. */}
      {heldModal && (
        <div className={cx(overlay, 'z-modal')} role="dialog" aria-modal="true">
          <div className={modalSm}>
            <h3 className="mt-0 flex items-center gap-2 text-big font-bold">
              <Marking size={20} /> Чек на проверке маркировки
            </h3>
            <p className={hint}>
              Деньги уже в ящике. Если код подтверждён — пробейте фискальный чек.
              Если нет — отмените с возвратом наличных и товара на склад.
            </p>
            <div className="my-3 rounded-card border border-line bg-surface px-3 py-2">
              <div className="flex justify-between text-sm">
                <span>Сумма чека</span>
                <strong className="tabular-nums">
                  {formatCurrency(heldModal.total, currency)}
                </strong>
              </div>
              <div className="mt-1 text-tiny text-muted">
                Код: {heldModal.markingPrintState === 'ready' ? 'подтверждён' : 'не подтверждён'}
              </div>
            </div>
            <label className={fieldLabel} htmlFor="held-amount">
              Подтвердите сумму
            </label>
            <input
              id="held-amount"
              className={fieldInput}
              inputMode="decimal"
              value={heldAmount}
              onChange={(e) => setHeldAmount(e.target.value)}
            />
            <label className={cx(fieldLabel, 'mt-3')} htmlFor="held-reason">
              Причина отмены (если возвращаем)
            </label>
            <input
              id="held-reason"
              className={fieldInput}
              value={heldReason}
              onChange={(e) => setHeldReason(e.target.value)}
              placeholder="Например: код недействителен"
            />
            <div className="mt-4 flex flex-col gap-2">
              <button
                className={cx(btnBase, btn.success, btnSize.lg)}
                disabled={
                  heldBusy ||
                  !(
                    heldModal.markingPrintState === 'ready' ||
                    (heldModal.markingPrintState === 'failed' &&
                      heldModal.czStatus === 'valid')
                  )
                }
                onClick={() => void confirmHeldPrint()}
              >
                <Printer size={18} /> Пробить фискальный чек
              </button>
              <button
                className={cx(btnBase, btn.secondary, btnSize.md)}
                disabled={heldBusy}
                onClick={() => void confirmHeldCancel()}
              >
                Отменить и вернуть наличные
              </button>
              <button
                className={cx(btnBase, btn.secondary, btnSize.md)}
                disabled={heldBusy}
                onClick={() => setHeldModal(null)}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* B3/65.14: модалка замены non_fiscal на фискальный чек. */}
      {replacementModal && (
        <div className={cx(overlay, 'z-modal')} role="dialog" aria-modal="true">
          <div className={modalSm}>
            <h3 className="mt-0 flex items-center gap-2 text-big font-bold">
              <Marking size={20} /> Замена чека без ККТ
            </h3>
            <p className={hint}>
              Код подтверждён в «Честном Знаке». Верните покупателю наличные и примите их снова,
              затем подтвердите сумму — чек будет пробит фискально, а код выведен
              из оборота.
            </p>
            <div className="my-3 rounded-card border border-line bg-surface px-3 py-2">
              <div className="flex justify-between text-sm">
                <span>Сумма чека</span>
                <strong className="tabular-nums">
                  {formatCurrency(replacementModal.total, currency)}
                </strong>
              </div>
              <div className="mt-1 text-tiny text-muted">
                Способ: наличные · позиций: {replacementModal.items.length}
              </div>
              <ul className="mt-2 space-y-0.5 text-tiny">
                {replacementModal.items.map((item, idx) => (
                  <li key={`${item.lineId ?? idx}`} className="flex justify-between gap-2">
                    <span className="min-w-0 truncate">{item.name}</span>
                    <span className="shrink-0 tabular-nums">
                      {formatQty(item.qty)} × {formatCurrency(item.price, currency)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <label className={fieldLabel} htmlFor="replacement-amount">
              Подтвердите сумму оплаты
            </label>
            <input
              id="replacement-amount"
              className={fieldInput}
              inputMode="decimal"
              value={replacementAmount}
              onChange={(e) => setReplacementAmount(e.target.value)}
            />
            <label className={cx(fieldLabel, 'mt-3')} htmlFor="replacement-reason">
              Причина отмены (если не печатаем)
            </label>
            <input
              id="replacement-reason"
              className={fieldInput}
              value={replacementReason}
              onChange={(e) => setReplacementReason(e.target.value)}
              placeholder="Например: покупатель ушёл"
            />
            <div className="mt-4 flex flex-col gap-2">
              <button
                className={cx(btnBase, btn.success, btnSize.lg)}
                disabled={
                  replacementBusy ||
                  (replacementModal.replacementState !== 'awaiting_print' &&
                    replacementModal.replacementState !== 'failed')
                }
                onClick={() => void confirmReplacement()}
              >
                <Printer size={18} /> Печать фискального чека
              </button>
              <button
                className={cx(btnBase, btn.secondary, btnSize.md)}
                disabled={replacementBusy}
                onClick={() => void cancelReplacement()}
              >
                Отмена
              </button>
              <button
                className={cx(btnBase, btn.secondary, btnSize.md)}
                disabled={replacementBusy}
                onClick={() => setReplacementModal(null)}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Экран успеха / сдачи после оплаты */}
      {successScreen && (
        <div
          className={cx(overlay, 'z-modal')}
          role="dialog"
          aria-modal="true"
          aria-label={successScreen.isReturn ? 'Возврат оформлен' : 'Чек оплачен'}
          onClick={() => setSuccessScreen(null)}
        >
          <div className={cx(modalSm, 'text-center')} onClick={(e) => e.stopPropagation()}>
            <div className={cx('mx-auto mb-1 flex h-12 w-12 items-center justify-center rounded-full', successScreen.isReturn ? 'bg-red-100 text-dangerDark' : 'bg-green-100 text-okDark')}><Check size={24} /></div>
            <h3 className="my-2 text-big font-bold">
              {successScreen.isReturn
                ? successScreen.fiscalData
                  ? `Возврат №${successScreen.fiscalData.fiscalDocumentNumber} оформлен`
                  : 'Возврат оформлен'
                : successScreen.status === 'fiscalized' && successScreen.fiscalData
                  ? `Фискальный чек №${successScreen.fiscalData.fiscalDocumentNumber} оплачен`
                  : successScreen.status === 'offline_pending'
                    ? 'Отложенный чек'
                    : 'Уведомление о продаже'}
            </h3>
            {successScreen.isReturn ? (
              <div className="my-2 text-xl font-bold tabular-nums text-dangerDark">
                −{formatCurrency(successScreen.total, currency)} возвращено
              </div>
            ) : successScreen.method === 'cash' && successScreen.change != null ? (
              <>
                <div className="text-[40px] font-extrabold tabular-nums text-okDark">
                  СДАЧА: {formatCurrency(successScreen.change, currency)}
                </div>
                <div className={hint}>
                  Получено: {formatCurrency(successScreen.cashGiven ?? 0, currency)} · Итог чека:{' '}
                  {formatCurrency(successScreen.total, currency)}
                </div>
              </>
            ) : (
              <div className="my-2 text-xl font-bold tabular-nums text-okDark">
                {formatCurrency(successScreen.total, currency)} оплачено
              </div>
            )}
            {successScreen.offlineReceiptNumber && (<div className='mt-1.5 text-tiny text-muted'>{successScreen.status === 'offline_pending' ? 'Отложенный чек' : 'Уведомление о продаже'}: {successScreen.offlineReceiptNumber}</div>)}
            {successScreen.status === 'fiscalized' ? (
              <div className="mt-1.5 flex items-center justify-center gap-1.5 text-tiny font-semibold text-okDark">
                <Check size={16} /> Фискальный чек
                {successScreen.fiscalData
                  ? `: ФД №${successScreen.fiscalData.fiscalDocumentNumber} · ФПД ${successScreen.fiscalData.fiscalDocumentSign}`
                  : ''}
              </div>
            ) : successScreen.status === 'offline_pending' ? (
              <div className="mt-2 flex items-start gap-1.5 rounded-card border border-amber-300 bg-amber-50 px-3 py-2 text-tiny font-semibold text-amber-800">
                <Clock size={16} className="mt-0.5 shrink-0" />
                <span>
                  <strong>Не пробит на ККТ: {kktUnavailableReason(kktStatus?.state)}.</strong>{' '}
                  Пробьём автоматически, когда ККТ станет доступна.
                  <span className="mt-1 block font-normal text-amber-700">
                    Отложенный чек сохранён и учтён в смене.
                  </span>
                </span>
              </div>
            ) : successScreen.status === 'marking_review' ? (
              <div className="mt-2 flex items-start gap-1.5 rounded-card border border-amber-300 bg-amber-50 px-3 py-2 text-tiny font-semibold text-amber-800">
                <Alert size={16} className="mt-0.5 shrink-0" />
                <span>
                  <strong>Код маркировки не проверен.</strong> Чек ждёт проверки/решения:
                  смена не закроется, пока чек не будет распечатан или отменён.
                </span>
              </div>
            ) : successScreen.status === 'non_fiscal' ? (
              <div className="mt-2 flex items-start gap-1.5 rounded-card border border-amber-300 bg-amber-50 px-3 py-2 text-tiny font-semibold text-amber-800">
                <Alert size={16} className="mt-0.5 shrink-0" /> Уведомление о продаже: чек не пробит на кассе.
                Код проверяется в «Честном Знаке»: если он подтвердится, касса предложит заменить
                чек фискальным (возврат наличных + повторная продажа).
              </div>
            ) : successScreen.fiscalData ? (
              <div className="mt-1.5 text-tiny text-muted">
                ФД №{successScreen.fiscalData.fiscalDocumentNumber} · ФПД{' '}
                {successScreen.fiscalData.fiscalDocumentSign}
              </div>
            ) : null}
            {(successScreen.status === 'non_fiscal' ||
              successScreen.status === 'marking_review' ||
              successScreen.status === 'offline_pending') && (
              <button
                className={cx(btnBase, btn.secondary, btnSize.md, 'mt-3 w-full')}
                onClick={() => void printNonFiscalReceipt()}
              >
                {successScreen.status === 'marking_review'
                  ? 'Печать уведомления о продаже (фискальный — после проверки)'
                  : 'Печать уведомления о продаже'}
              </button>
            )}
            {successScreen.status === 'fiscalized' && (
              <button
                className={cx(btnBase, btn.secondary, btnSize.md, 'mt-3 w-full')}
                onClick={() => void printReceiptCopy()}
              >
                <Printer size={18} /> Печать копии
              </button>
            )}
            <button
              className={cx(btnBase, btn.success, btnSize.md, 'mt-3 w-full')}
              onClick={() => setSuccessScreen(null)}
            >
              Следующий покупатель (Enter)
            </button>
          </div>
        </div>
      )}

      {/* Модалка ввода веса (товары с unit === 'кг') */}
      {weightModal && (
        <div className={cx(overlay, 'z-modal')} role="dialog" aria-modal="true" aria-label="Ввод веса">
          <div className={modalSm}>
            <h3 className="mt-0 text-big font-bold">Ввод веса: {weightModal.product.name}</h3>
            <p className={hint}>Цена: {formatCurrency(weightModal.product.price, currency)} / кг</p>

            {liveScaleMode ? (
              (() => {
                const kg = scaleReading?.kg ?? 0
                const stable = Boolean(scaleReading?.stable) && kg > 0
                const last = lastAutoKgRef.current
                const stillLoaded =
                  last != null && kg > 0 && !(kg < 0.005 || Math.abs(kg - last) > 0.02)
                const sum = kg * weightModal.product.price
                return (
                  <>
                    <div className="rounded-panel bg-surface px-4 py-3 text-center text-[44px] font-bold tabular-nums text-ink">
                      {formatKg(kg)} кг
                    </div>
                    <div
                      className={cx(
                        'mb-2 mt-1 text-center text-base2 font-bold',
                        stillLoaded || !stable ? 'text-amber-700' : 'text-okDark'
                      )}
                      role="status"
                    >
                      {stillLoaded
                        ? '⏸ Груз не снят'
                        : stable
                          ? 'Стабильно — добавляю в чек…'
                          : '… Взвешивание'}
                    </div>
                    <div
                      className="mb-2 text-center text-big font-semibold"
                    >
                      {formatCurrency(sum, currency)}
                    </div>
                    <p className={cx(hint, 'text-center')}>
                      {stillLoaded
                        ? 'Снимите товар с весов, чтобы взвесить следующую позицию.'
                        : stable
                          ? 'Вес принят автоматически.'
                          : 'Положите товар на весы и дождитесь стабильного показания.'}
                    </p>
                    <div className="mb-2.5 flex items-center justify-center gap-1.5 text-tiny text-muted">
                      <Scales size={16} /> Весы: {scaleStatus.port ?? '—'}
                      {scaleStatus.protocol ? ` · ${scaleStatus.protocol}` : ''}
                    </div>
                    <div className="flex gap-2">
                      <button
                        className={cx(btnBase, btn.success, btnSize.md, 'w-full')}
                        onClick={() => {
                          setWeightInput('')
                          setWeightManual(true)
                        }}
                      >
                        Ввести вручную
                      </button>
                      <button
                        onClick={() => {
                          setWeightModal(null)
                          setWeightInput('')
                          setWeightManual(false)
                        }}
                       className={cx(btnBase, btn.secondary, btnSize.md)}>
                        Отмена
                      </button>
                    </div>
                  </>
                )
              })()
            ) : (
              <>
            <div className={amountDisplay}>
              {formatKg(Number(weightInput.replace(',', '.')) || 0)} кг
            </div>

            {(() => {
              const kg = Number(weightInput.replace(',', '.')) || 0
              const sum = kg * weightModal.product.price
              return (
                <div className="my-2 text-center text-xl font-bold tabular-nums">
                  {formatKg(kg)} кг × {formatCurrency(weightModal.product.price, currency)} ={' '}
                  <span className="text-okDark">{formatCurrency(sum, currency)}</span>
                </div>
              )
            })()}

            <div className="my-2 flex flex-wrap gap-2">
              <button className={cx(btnBase, btn.secondary, btnSize.sm)} onClick={() => weightAdd(0.1)}>+0.1 кг</button>
              <button className={cx(btnBase, btn.secondary, btnSize.sm)} onClick={() => weightAdd(0.5)}>+0.5 кг</button>
              <button className={cx(btnBase, btn.secondary, btnSize.sm)} onClick={() => weightAdd(1)}>+1.0 кг</button>
              <button className={cx(btnBase, btn.secondary, btnSize.sm)} onClick={() => weightAdd(2)}>+2.0 кг</button>
              <button className={cx(btnBase, btn.secondary, btnSize.sm)} onClick={() => setWeightInput('')}>Очистить</button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <button key={d} className="h-12 rounded-card border border-lineStrong bg-soft text-big font-bold" onClick={() => weightPress(d)}>
                  {d}
                </button>
              ))}
              <button className="h-12 rounded-card border border-lineStrong bg-soft text-big font-bold" onClick={() => weightPress('.')}>
                .
              </button>
              <button className="h-12 rounded-card border border-lineStrong bg-soft text-big font-bold" onClick={() => weightPress('0')}>
                0
              </button>
              <button className="h-12 rounded-card border border-lineStrong bg-soft text-big font-bold" onClick={weightBackspace}>
                ⌫
              </button>
            </div>

            <div className="mt-2 flex gap-2">
              <button
                className={cx(btnBase, btn.success, btnSize.md, 'w-full')}
                disabled={(Number(weightInput.replace(',', '.')) || 0) <= 0}
                onClick={() => confirmWeight()}
              >
                Добавить в чек (Enter)
              </button>
              <button
                onClick={() => {
                  setWeightModal(null)
                  setWeightInput('')
                  setWeightManual(false)
                }}
               className={cx(btnBase, btn.secondary, btnSize.md)}>
                Отмена
              </button>
            </div>

            {/* Весы подключены, но выбран ручной ввод — можно вернуться к весам. */}
            {scaleStatus.connected && (
              <button
                type="button"
                className={cx(btnBase, btn.secondary, btnSize.sm, 'mt-2 w-full')}
                onClick={() => setWeightManual(false)}
              >
                ← Использовать весы ({scaleStatus.port ?? scaleStatus.protocol ?? 'подключены'})
              </button>
            )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

/** Контейнер чипов статус-строки кассы (смена · ККТ · весы · очередь). */
/** Один чип статус-строки. */
/** Полоса устройств (ККТ/ОФД/ФН/весы/очередь) — видна всегда, вне зависимости от смены. */
/** Чип полосы устройств. */
/** Кнопка «Отменить чек» (danger, но не конкурирует с «ОПЛАТИТЬ»). */
/**
 * Окно ввода марок — ПОВЕРХ окна оплаты (F3): payModal остаётся смонтированной
 * (stage='cash'), когда плагин ЧЗ просит марки, поэтому одинаковый zIndex
 * приводил к тому, что марки оказывались «под» оплатой.
 */
